import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { apiOrigin } from '../utils/surface';
import { cameraSystemForProperty } from '../utils/cameraSystem';
import './PropertyDetail.css';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
delete (L.Icon.Default.prototype as any)._getIconUrl;
(L.Icon.Default as any).mergeOptions({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});
// §28.518.16 — Property location map. Google Maps embed rejected with
// X-Frame-Options: SAMEORIGIN; using OpenStreetMap tiles via react-leaflet
// which is already in the codebase (GeofencePicker).
function PropertyLeafletMap({ address, lat, lng }: { address: string; lat?: number; lng?: number }) {
  const [coords, setCoords] = useState<[number, number] | null>(
    lat && lng ? [Number(lat), Number(lng)] : null
  );
  useEffect(() => {
    if (coords) return;
    if (!address) return;
    (async () => {
      try {
        const r = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(address)}`,
          { headers: { 'Accept': 'application/json' } }
        );
        const j = await r.json();
        if (j?.[0]?.lat && j?.[0]?.lon) {
          setCoords([Number(j[0].lat), Number(j[0].lon)]);
        }
      } catch {}
    })();
  }, [address, coords]);
  if (!coords) {
    return (
      <div className="mt-4 w-full h-[180px] rounded-lg overflow-hidden shadow-sm border border-gray-200 bg-gray-50 flex items-center justify-center text-sm text-gray-500">
        {address ? `Looking up "${address}"…` : 'No address on file'}
      </div>
    );
  }
  return (
    <div className="mt-4 w-full h-[180px] rounded-lg overflow-hidden shadow-sm border border-gray-200">
      <MapContainer
        center={coords}
        zoom={17}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom={false}
      >
        <TileLayer
          attribution='Imagery &copy; Esri, Maxar, Earthstar Geographics'
          url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
          maxZoom={19}
        />
        <Marker position={coords}>
          <Popup>{address}</Popup>
        </Marker>
      </MapContainer>
    </div>
  );
}
import { CallHistory } from './CallHistory';
import { DeviceHistory } from './DeviceHistory';
import { useParams, useNavigate } from 'react-router-dom';
import { EntityUsageCard } from '../components/EntityUsageCard';
import { HlsVideo } from '../components/cameras/HlsVideo';
import { AdminReassignCard } from '../components/AdminReassignCard';
import { Card, CardHeader } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { DeviceActionButton, DeviceStatusIcon, deviceTypeIcon, intrlockTypeLabel } from '../components/access/DeviceVisuals';
import { deviceAccessId, eligibleUnlockDevices } from '../utils/deviceCapabilities';
import { Button } from '../components/ui/Button';
import { Table } from '../components/ui/Table';
import { Input } from '../components/ui/Input';
import { PhoneInput } from '../components/ui/PhoneInput';
import { PinCell } from '../components/PinCell';
import { Modal } from '../components/ui/Modal';
import { UnitForm } from '../components/forms/UnitForm';
import { DeviceForm } from '../components/forms/DeviceForm';
import { AddDeviceModal } from '../components/modals/AddDeviceModal';
import { TenantForm } from '../components/forms/TenantForm';
import { BulkImportWizard } from '../components/bulkImport/BulkImportWizard';
import type { ImportMode } from '../components/bulkImport/types';
import { PropertyForm } from '../components/forms/PropertyForm';
import { Unit, Device, CallRecord } from '../types';
import { formatDateTime, formatDuration } from '../utils/formatters';
import { getPropertyById } from '../api/properties';
import { getDevices, updateDevice, deleteDevice } from '../api/devices';
import { controlPDKDoor, getPDKLiveDevices } from '../api/pdkApi';
import { UsersList } from '../components/UsersList';
import { lockSeamDevice, unlockSeamDevice, getSeamDevice } from '../api/seamApi';
import { getUnits, createUnit, updateUnit, deleteUnit } from '../api/units';
import { refreshPropertyCounters } from '../utils/propertyCounters';
import { getTenants, createTenant, updateTenant, deleteTenant } from '../api/tenants';
import { getCalls } from '../api/calls';
import { getCustomers } from '../api/customers';
import { getIntegratorById } from '../api/integrators';
import { CreateSubscriptionModal } from '../components/billing/CreateSubscriptionModal';
import { IntercomDoorConfig } from '../components/IntercomDoorConfig';
import { VisitorPassEntryPhotos } from '../components/VisitorPassEntryPhotos';
import { generateActivationCode, getIntercomConfigs, IntercomConfig } from '../api/intercomConfig';
import { CreateInvoiceModal } from '../components/billing/CreateInvoiceModal';
import { CustomPricingModal } from '../components/billing/CustomPricingModal';
import { apiList, apiGet, apiUpdate, apiCreate, apiPost, apiRequestGet } from '../api/apiClient';
import { buildVisitorPassPayload } from '../utils/visitorPassPayload';
import { RatesTab } from '../components/billing/RatesTab';
import { FeatureToggleList } from '../components/billing/FeatureToggleList';
import { isSuperAdmin, isAdmin } from '../utils/roles';
import { useAuth } from '../hooks/useAuth';
import { refreshAllCounters } from '../utils/refreshCounters';
import { GeofencePicker } from '../components/GeofencePicker';
import { listVendors, Vendor } from '../api/vendors';
import { VendorDetail } from './VendorDetail';
import { VendorCreateModal } from './Vendors';
import { EmailTenantModal } from '../components/EmailTenantModal';
import { BulkActions, useSelection } from '../components/ui/BulkActions';
import { ActionMenu } from '../components/ui/ActionMenu';
import { isAdminHost } from '../utils/host';
import { pollAfterAction } from '../utils/postActionPoll';

type Tab = 'overview' | 'units' | 'tenants' | 'intercoms' | 'devices' | 'users' | 'guests' | 'deliveries' | 'vendors' | 'contacts' | 'billing_rates' | 'settings' | 'calls' | 'device_history';

const recordKeys = (row: any) => [row?.id, row?.uuid, row?.person_id, row?.tenant_id, row?.person_uuid, row?.tenant_uuid]
  .filter((value) => value !== undefined && value !== null && value !== '')
  .map(String);

/** Canonical property-person loader. Merges legacy people fields with the
 * multi-property tenant_properties relationship and overlays the assignment's
 * unit_id. All property surfaces must use this instead of querying one source. */
const loadPropertyPeople = async (propertyId: number | string) => {
  const [legacyRaw, junctionRaw] = await Promise.all([
    getTenants({ property_id: propertyId, per_page: 500 }).catch(() => []),
    apiList('tenant_properties', { property_id: String(propertyId) }).catch(() => []),
  ]);
  const legacy = (Array.isArray(legacyRaw) ? legacyRaw : [])
    .filter((person: any) => String(person.property_id) === String(propertyId));
  const junctionRows = (Array.isArray(junctionRaw) ? junctionRaw : [])
    .filter((link: any) => String(link.property_id) === String(propertyId) && link.status !== 'revoked');

  const peopleByKey = new Map<string, any>();
  for (const person of legacy) for (const key of recordKeys(person)) peopleByKey.set(key, person);

  const unresolved = junctionRows.some((link: any) =>
    !recordKeys(link).some((key) => peopleByKey.has(key))
  );
  if (unresolved) {
    const allPeopleRaw = await apiList('people', { per_page: 1000 }).catch(() => []);
    for (const person of (Array.isArray(allPeopleRaw) ? allPeopleRaw : [])) {
      for (const key of recordKeys(person)) peopleByKey.set(key, person);
    }
  }

  const mergedByPerson = new Map<string, any>();
  for (const person of legacy) {
    const identity = recordKeys(person)[0];
    if (identity) mergedByPerson.set(identity, person);
  }
  for (const link of junctionRows) {
    const person = recordKeys(link).map((key) => peopleByKey.get(key)).find(Boolean);
    if (!person) continue;
    const identity = recordKeys(person)[0];
    if (!identity) continue;
    mergedByPerson.set(identity, {
      ...person,
      property_id: propertyId,
      unit_id: link.unit_id || person.unit_id,
      _from_junction: true,
    });
  }
  return { people: Array.from(mergedByPerson.values()), junctionRows };
};

/* ─── Enhanced Tenant Modal with Select Existing or Create New ─── */
const TenantFormModal: React.FC<{
  open: boolean;
  onClose: () => void;
  onSubmit: (data: any) => Promise<void>;
  onSelectExisting?: (tenant: any) => Promise<void>;
  initial?: any;
  propertyId?: number | string;
  currentUnitTenantIds?: (number | string)[];
}> = ({ open, onClose, onSubmit, onSelectExisting, initial, propertyId, currentUnitTenantIds = [] }) => {
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', phone: '' });
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState<'select' | 'create'>('select');
  const [allPropertyTenants, setAllPropertyTenants] = useState<any[]>([]);
  const [loadingTenants, setLoadingTenants] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [propertyFilter, setPropertyFilter] = useState<string>('all');
  const [propertiesById, setPropertiesById] = useState<Record<string, any>>({});
  const [unitsById, setUnitsById] = useState<Record<string, any>>({});
  const [linksByPerson, setLinksByPerson] = useState<Record<string, Array<{ property_id: string; unit_id: string }>>>({});

  // Per-tenant door-access table. Each row shows a door + a "Mobile
  // access" toggle reflecting the tenant's effective visibility:
  //   mobile_access = (baseline ∪ grants) \ hides
  // Toggling updates grants/hides; on save both lists are persisted.
  type DoorRow = {
    id: string;
    name: string;
    source: string;
    baseline: boolean;
    granted: boolean;
    hidden: boolean;
    mobile_access: boolean;
  };
  const [doorRows, setDoorRows] = useState<DoorRow[]>([]);
  const [doorRowsOrig, setDoorRowsOrig] = useState<DoorRow[]>([]);
  const [doorsLoading, setDoorsLoading] = useState(false);

  // Load all people across every property the current user can see, plus their
  // unit/property assignments, when the modal opens in add mode. Showing only
  // the current property hides duplicates that already exist elsewhere — the
  // whole point of this picker is to AVOID creating those duplicates.
  useEffect(() => {
    if (open && !initial && mode === 'select') {
      setLoadingTenants(true);
      Promise.all([
        getTenants({ per_page: 1000 }).catch(() => []),
        apiList('properties').catch(() => []),
        apiList('units').catch(() => []),
        apiList('tenant_properties').catch(() => []),
      ])
        .then(([tenantsRaw, propsRaw, unitsRaw, tpRaw]) => {
          const allTenants = Array.isArray(tenantsRaw) ? tenantsRaw : [];
          const props = Array.isArray(propsRaw) ? propsRaw : [];
          const units = Array.isArray(unitsRaw) ? unitsRaw : [];
          const tps = Array.isArray(tpRaw) ? tpRaw : [];

          const pMap: Record<string, any> = {};
          for (const p of props) pMap[String(p.id)] = p;
          const uMap: Record<string, any> = {};
          for (const u of units) uMap[String(u.id)] = u;

          // Build per-person assignment list. Include both the legacy
          // people.{property_id,unit_id} and the tenant_properties junction.
          const links: Record<string, Array<{ property_id: string; unit_id: string }>> = {};
          const seen = new Set<string>();
          for (const tp of tps) {
            if (tp.status && tp.status !== 'active') continue;
            const pid = String(tp.person_id);
            const key = `${pid}|${tp.property_id}|${tp.unit_id ?? ''}`;
            if (seen.has(key)) continue;
            seen.add(key);
            (links[pid] ??= []).push({ property_id: String(tp.property_id), unit_id: String(tp.unit_id ?? '') });
          }
          for (const t of allTenants) {
            if (!t.property_id) continue;
            const pid = String(t.id);
            const key = `${pid}|${t.property_id}|${t.unit_id ?? ''}`;
            if (seen.has(key)) continue;
            seen.add(key);
            (links[pid] ??= []).push({ property_id: String(t.property_id), unit_id: String(t.unit_id ?? '') });
          }

          const available = allTenants.filter((t: any) => !currentUnitTenantIds.includes(t.id));
          setAllPropertyTenants(available);
          setPropertiesById(pMap);
          setUnitsById(uMap);
          setLinksByPerson(links);
        })
        .catch((e) => {
          console.error('Failed to load people:', e);
          setAllPropertyTenants([]);
        })
        .finally(() => setLoadingTenants(false));
    }
  }, [open, initial, mode, currentUnitTenantIds]);

  useEffect(() => {
    if (open) {
      if (initial) {
        // Edit mode
        setMode('create');
        setForm({
          first_name: initial.first_name || '',
          last_name: initial.last_name || '',
          email: initial.email || '',
          phone: initial.phone || '',
        });
      } else {
        // Add mode - default to select existing
        setMode('select');
        setForm({ first_name: '', last_name: '', email: '', phone: '' });
        setSearchQuery('');
      }
    }
  }, [open, initial]);

  // Edit mode only: load per-door effective state for this tenant.
  useEffect(() => {
    if (!open || !initial || !propertyId) {
      setDoorRows([]);
      setDoorRowsOrig([]);
      return;
    }
    let cancelled = false;
    setDoorsLoading(true);
    apiGet('people', `${initial.id}/property-doors?property_id=${propertyId}`)
      .then((res: any) => {
        if (cancelled) return;
        const rows: DoorRow[] = Array.isArray(res?.doors) ? res.doors.map((d: any) => ({
          id: String(d.id),
          name: String(d.name ?? 'Door'),
          source: String(d.source ?? ''),
          baseline: !!d.baseline,
          granted: !!d.granted,
          hidden: !!d.hidden,
          mobile_access: !!d.mobile_access,
        })) : [];
        setDoorRows(rows);
        setDoorRowsOrig(rows);
      })
      .catch(() => { /* leave empty */ })
      .finally(() => { if (!cancelled) setDoorsLoading(false); });
    return () => { cancelled = true; };
  }, [open, initial, propertyId]);

  // Toggle a door's mobile_access. Flipping ON: drop from hides; if door
  // is NOT baseline-visible we also add it to grants so it actually shows.
  // Flipping OFF: drop from grants; if door IS baseline-visible we add to
  // hides so the tenant stops seeing it.
  const toggleDoorMobileAccess = (id: string) => {
    setDoorRows((prev) => prev.map((r) => {
      if (r.id !== id) return r;
      const turningOn = !r.mobile_access;
      if (turningOn) {
        return {
          ...r,
          mobile_access: true,
          hidden: false,
          granted: r.baseline ? false : true,
        };
      } else {
        return {
          ...r,
          mobile_access: false,
          granted: false,
          hidden: r.baseline ? true : false,
        };
      }
    }));
  };

  const doorRowsChanged = (() => {
    if (doorRows.length !== doorRowsOrig.length) return true;
    const origById = new Map(doorRowsOrig.map((r) => [r.id, r]));
    return doorRows.some((r) => {
      const o = origById.get(r.id);
      return !o || o.granted !== r.granted || o.hidden !== r.hidden;
    });
  })();

  const handle = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSubmit(form);
      // Edit mode + door-override changes: persist via the new admin endpoint.
      if (initial && propertyId && doorRowsChanged) {
        try {
          const grants = doorRows.filter((r) => r.granted).map((r) => r.id);
          const hides  = doorRows.filter((r) => r.hidden).map((r) => r.id);
          await apiUpdate('people', `${initial.id}/property-doors`, {
            property_id: propertyId,
            door_grants: grants,
            door_hides: hides,
          });
        } catch (err) {
          console.error('[TenantFormModal] door override save failed:', err);
          alert('Profile saved but door access could not be updated. Try again.');
        }
      }
      onClose();
    } catch (err) {
      console.error('Form submit error:', err);
      alert('Failed to save person. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleSelectTenant = async (tenant: any) => {
    if (!onSelectExisting) return;
    setSaving(true);
    try {
      await onSelectExisting(tenant);
      onClose();
    } catch (err) {
      console.error('Failed to select tenant:', err);
      alert('Failed to assign person to unit. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Filter tenants by search query AND property filter
  const filteredTenants = allPropertyTenants.filter((t) => {
    if (propertyFilter !== 'all') {
      const links = linksByPerson[String(t.id)] || [];
      const inProp = links.some((l) => l.property_id === propertyFilter)
        || String(t.property_id ?? '') === propertyFilter;
      if (!inProp) return false;
    }
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      (t.first_name || '').toLowerCase().includes(query) ||
      (t.last_name || '').toLowerCase().includes(query) ||
      (t.email || '').toLowerCase().includes(query) ||
      (t.phone || '').toLowerCase().includes(query)
    );
  });

  // Group tenants by their primary property for the picker.
  const groupedTenants = (() => {
    const groups: Record<string, { propertyName: string; tenants: any[] }> = {};
    for (const t of filteredTenants) {
      const links = linksByPerson[String(t.id)] || [];
      const primaryPid = String(t.property_id ?? links[0]?.property_id ?? 'none');
      const propName = propertiesById[primaryPid]?.name || `Property ${primaryPid.slice(0, 8)}`;
      groups[primaryPid] ??= { propertyName: propName, tenants: [] };
      groups[primaryPid].tenants.push(t);
    }
    return Object.entries(groups).sort((a, b) => a[1].propertyName.localeCompare(b[1].propertyName));
  })();

  const formatAssignments = (personId: string): string => {
    const links = linksByPerson[String(personId)] || [];
    if (links.length === 0) return '';
    return links
      .map((l) => {
        const u = unitsById[l.unit_id];
        const p = propertiesById[l.property_id];
        const unitLabel = u?.unit_number ? `Unit ${u.unit_number}` : 'No unit';
        const propLabel = p?.name || `Property ${l.property_id.slice(0, 8)}`;
        return `${unitLabel} at ${propLabel}`;
      })
      .join(', ');
  };

  return (
    <Modal open={open} onClose={onClose} title={initial ? 'Edit Person' : 'Add Person to Unit'} size="lg">
      {!initial && (
        <div className="flex gap-2 mb-4 border-b border-gray-200 pb-1">
          <button
            onClick={() => setMode('select')}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              mode === 'select'
                ? 'text-brand-600 border-b-2 border-brand-600 -mb-[1px]'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Select Existing Person
          </button>
          <button
            onClick={() => setMode('create')}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              mode === 'create'
                ? 'text-brand-600 border-b-2 border-brand-600 -mb-[1px]'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Create New Person
          </button>
        </div>
      )}

      {mode === 'select' && !initial ? (
        <div>
          <p className="text-sm text-gray-600 mb-3">
            Select an existing person to assign to this unit. People can be assigned to multiple units across properties — picking someone here links the existing record instead of creating a duplicate.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Input
              label="Search"
              value={searchQuery}
              onChange={(e: any) => setSearchQuery(e.target.value)}
              placeholder="Search by name, email, or phone..."
            />
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Property</label>
              <select
                value={propertyFilter}
                onChange={(e) => setPropertyFilter(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              >
                <option value="all">All properties</option>
                {Object.values(propertiesById)
                  .sort((a: any, b: any) => (a.name || '').localeCompare(b.name || ''))
                  .map((p: any) => (
                    <option key={p.id} value={String(p.id)}>{p.name || p.id}</option>
                  ))}
              </select>
            </div>
          </div>
          <div className="mt-3 max-h-96 overflow-y-auto border border-gray-200 rounded-lg">
            {loadingTenants ? (
              <div className="p-8 text-center text-gray-400 text-sm">Loading people...</div>
            ) : filteredTenants.length === 0 ? (
              <div className="p-8 text-center text-gray-400 text-sm">
                {allPropertyTenants.length === 0 ? (
                  <>
                    No existing people found.
                    <button
                      onClick={() => setMode('create')}
                      className="block mx-auto mt-2 text-brand-600 hover:text-brand-700 font-medium"
                    >
                      Create a new person instead
                    </button>
                  </>
                ) : (
                  'No people match your search.'
                )}
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {groupedTenants.map(([pid, group]) => (
                  <div key={pid}>
                    <div className="bg-gray-50 px-4 py-2 sticky top-0 z-10 border-b border-gray-200">
                      <p className="text-xs font-semibold uppercase tracking-wider text-gray-600">
                        {group.propertyName} <span className="text-gray-400 font-normal">({group.tenants.length})</span>
                      </p>
                    </div>
                    {group.tenants.map((t: any) => {
                      const assignments = formatAssignments(t.id);
                      return (
                        <button
                          key={t.id}
                          onClick={() => handleSelectTenant(t)}
                          disabled={saving}
                          className="w-full text-left p-4 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-wait flex items-center justify-between group"
                        >
                          <div>
                            <p className="font-medium text-gray-900">
                              {t.first_name} {t.last_name}
                            </p>
                            <p className="text-sm text-gray-500">
                              {t.email && <span>{t.email}</span>}
                              {t.email && t.phone && <span> · </span>}
                              {t.phone && <span>{t.phone}</span>}
                            </p>
                            {assignments && (
                              <p className="text-xs text-gray-400 mt-1">Currently in {assignments}</p>
                            )}
                          </div>
                          <svg
                            className="w-5 h-5 text-gray-400 group-hover:text-brand-600"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                        </button>
                      );
                    })}
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="secondary" type="button" onClick={onClose}>
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <form onSubmit={handle} className="space-y-4">
          <Input
            label="First Name"
            value={form.first_name}
            onChange={(e: any) => setForm((f) => ({ ...f, first_name: e.target.value }))}
            required
          />
          <Input
            label="Last Name"
            value={form.last_name}
            onChange={(e: any) => setForm((f) => ({ ...f, last_name: e.target.value }))}
            required
          />
          <Input
            label="Email"
            type="email"
            value={form.email}
            onChange={(e: any) => setForm((f) => ({ ...f, email: e.target.value }))}
          />
          <PhoneInput
            label="Phone"
            value={form.phone}
            onChange={(v) => setForm((f) => ({ ...f, phone: v }))}
          />

          {/* Per-tenant door access table. Each row shows a property
              door + a "Mobile access" toggle. Toggle reflects the
              tenant's effective visibility; flipping it grants or
              revokes access for THIS person only. */}
          {initial && propertyId && (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Door Access
              </label>
              {doorsLoading ? (
                <p className="text-[11px] text-gray-500 italic px-3 py-2 border border-gray-200 rounded-lg">Loading doors…</p>
              ) : doorRows.length === 0 ? (
                <p className="text-[11px] text-gray-500 italic px-3 py-2 border border-gray-200 rounded-lg">No doors configured on this property.</p>
              ) : (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="flex items-center gap-3 px-3 py-1.5 text-[10px] uppercase tracking-wide text-gray-500 bg-gray-50 border-b border-gray-100">
                    <span className="flex-1">Door</span>
                    <span className="w-24 text-right">Mobile access</span>
                  </div>
                  <div className="max-h-56 overflow-y-auto">
                    {doorRows.map((r) => {
                      const name = (r.name && String(r.name).trim()) || 'Door';
                      const subtle = !r.mobile_access ? 'text-gray-500' : 'text-gray-800';
                      const overrideTag = r.granted
                        ? <span className="text-[10px] text-accent-600 font-medium">granted</span>
                        : r.hidden
                          ? <span className="text-[10px] text-red-600 font-medium">hidden</span>
                          : null;
                      return (
                        <div key={r.id} className="flex items-center gap-3 px-3 py-2 text-sm border-b border-gray-50 last:border-b-0">
                          <div className="flex-1 min-w-0">
                            <div className={`truncate ${subtle}`}>{name}</div>
                            {overrideTag && <div className="mt-0.5">{overrideTag}</div>}
                          </div>
                          {/* Mobile-access toggle */}
                          <button
                            type="button"
                            onClick={() => toggleDoorMobileAccess(r.id)}
                            className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-accent-500 focus:ring-offset-1 ${r.mobile_access ? 'bg-accent-500' : 'bg-gray-300'}`}
                            role="switch"
                            aria-checked={r.mobile_access}
                            aria-label={`Mobile access for ${name}`}
                          >
                            <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${r.mobile_access ? 'translate-x-5' : 'translate-x-0.5'}`} />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              <p className="text-[11px] text-gray-400 mt-1">
                Turn off a door to hide it from this tenant in the mobile app (overrides the intercom default). Turn on a door the PM has hidden to give this specific tenant access.
              </p>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" type="button" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? 'Saving...' : initial ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
};

/* ─── Per-device tenant access editor ───
   Lists every tenant on the property with a Mobile Access toggle for
   ONE specific device. Reads each tenant's effective state from
   GET /people/:id/property-doors, then writes back per-tenant grants/
   hides on Save. Mirrors the toggle semantics used in TenantDetail's
   "Doors with Access" table — flipping ON removes from hides (and adds
   to grants if not in baseline); flipping OFF removes from grants (and
   adds to hides if currently in baseline). Same source of truth keeps
   every surface consistent. */
const DeviceTenantAccessModal: React.FC<{
  device: any | null;
  propertyId: string;
  tenants: any[];
  onClose: () => void;
}> = ({ device, propertyId, tenants, onClose }) => {
  type Row = {
    tenantId: string;
    tenantName: string;
    baseline: boolean;
    granted: boolean;
    hidden: boolean;
    mobileAccess: boolean;
    grantsAll: Set<string>; // full per-property override set, mutated on toggle
    hidesAll: Set<string>;
  };
  const [rows, setRows] = useState<Row[]>([]);
  const [rowsOrig, setRowsOrig] = useState<Row[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const open = !!device;
  const doorKey = String(device?.id ?? '');

  useEffect(() => {
    if (!open || !propertyId || !doorKey) {
      setRows([]); setRowsOrig([]); return;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      const result: Row[] = [];
      // tenants prop may contain non-tenants too; filter to active people.
      const tenantList = (tenants || []).filter((t: any) => t && t.id);
      for (const t of tenantList) {
        try {
          const res: any = await apiGet('people', `${t.id}/property-doors?property_id=${propertyId}`);
          const doorList: any[] = Array.isArray(res?.doors) ? res.doors : [];
          const me = doorList.find((d) => String(d.id) === doorKey);
          if (!me) continue; // door not on this property as far as this tenant's view goes
          result.push({
            tenantId: String(t.id),
            tenantName: `${t.first_name || ''} ${t.last_name || ''}`.trim() || t.email || 'Tenant',
            baseline: !!me.baseline,
            granted: !!me.granted,
            hidden: !!me.hidden,
            mobileAccess: !!me.mobile_access,
            grantsAll: new Set<string>((res.door_grants ?? []).map(String)),
            hidesAll:  new Set<string>((res.door_hides ?? []).map(String)),
          });
        } catch (e) {
          console.warn('[DeviceTenantAccess] load failed for', t.id, e);
        }
      }
      if (!cancelled) {
        setRows(result);
        setRowsOrig(result.map((r) => ({
          ...r,
          grantsAll: new Set(r.grantsAll),
          hidesAll: new Set(r.hidesAll),
        })));
      }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [open, propertyId, doorKey, tenants]);

  const toggleRow = (tenantId: string) => {
    setRows((prev) => prev.map((r) => {
      if (r.tenantId !== tenantId) return r;
      const turningOn = !r.mobileAccess;
      const nextGrants = new Set(r.grantsAll);
      const nextHides  = new Set(r.hidesAll);
      if (turningOn) {
        nextHides.delete(doorKey);
        if (!r.baseline) nextGrants.add(doorKey);
      } else {
        nextGrants.delete(doorKey);
        if (r.baseline) nextHides.add(doorKey);
      }
      return {
        ...r,
        mobileAccess: turningOn,
        granted: nextGrants.has(doorKey),
        hidden: nextHides.has(doorKey),
        grantsAll: nextGrants,
        hidesAll: nextHides,
      };
    }));
  };

  const dirtyCount = rows.filter((r, i) => {
    const o = rowsOrig[i];
    return o && (r.granted !== o.granted || r.hidden !== o.hidden);
  }).length;

  const onSave = async () => {
    if (dirtyCount === 0) return;
    setSaving(true);
    const failures: string[] = [];
    for (let i = 0; i < rows.length; i++) {
      const r = rows[i];
      const o = rowsOrig[i];
      if (!o || (r.granted === o.granted && r.hidden === o.hidden)) continue;
      try {
        await apiUpdate('people', `${r.tenantId}/property-doors`, {
          property_id: propertyId,
          door_grants: Array.from(r.grantsAll),
          door_hides:  Array.from(r.hidesAll),
        });
      } catch (err) {
        console.error('[DeviceTenantAccess] save failed for', r.tenantId, err);
        failures.push(r.tenantName);
      }
    }
    setSaving(false);
    if (failures.length > 0) {
      alert(`Failed to save: ${failures.join(', ')}`);
    } else {
      // Update snapshot so dirty count clears.
      setRowsOrig(rows.map((r) => ({
        ...r,
        grantsAll: new Set(r.grantsAll),
        hidesAll: new Set(r.hidesAll),
      })));
      onClose();
    }
  };

  return (
    <Modal open={open} onClose={onClose} title={`Tenant access — ${device?.name || ''}`} size="md">
      <div className="space-y-3">
        <p className="text-xs text-gray-500">
          Toggle Mobile Access per tenant for this device. Changes only apply to the tenant in that row.
        </p>
        {loading ? (
          <p className="text-sm text-gray-500 italic px-3 py-4">Loading tenants…</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-gray-500 italic px-3 py-4">No tenants on this property.</p>
        ) : (
          <div className="border border-gray-200 rounded-lg max-h-[55vh] overflow-y-auto">
            <div className="flex items-center gap-3 px-3 py-2 text-[10px] uppercase tracking-wide text-gray-500 bg-gray-50 border-b border-gray-100">
              <span className="flex-1">Tenant</span>
              <span className="w-24 text-right">Mobile Access</span>
            </div>
            {rows.map((r) => {
              const overrideTag = r.granted
                ? <span className="block text-[10px] text-brand-600 font-medium mt-0.5">granted</span>
                : r.hidden
                  ? <span className="block text-[10px] text-red-600 font-medium mt-0.5">hidden</span>
                  : null;
              return (
                <div key={r.tenantId} className="flex items-center gap-3 px-3 py-2 text-sm border-b border-gray-50 last:border-b-0">
                  <div className="flex-1 min-w-0">
                    <div className={r.mobileAccess ? 'text-gray-800' : 'text-gray-500'}>{r.tenantName}</div>
                    {overrideTag}
                  </div>
                  <button
                    type="button"
                    disabled={saving}
                    onClick={() => toggleRow(r.tenantId)}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-1 ${r.mobileAccess ? 'bg-green-500' : 'bg-gray-300'} ${saving ? 'opacity-50' : ''}`}
                    role="switch"
                    aria-checked={r.mobileAccess}
                    aria-label={`Mobile access for ${r.tenantName}`}
                  >
                    <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${r.mobileAccess ? 'translate-x-5' : 'translate-x-0.5'}`} />
                  </button>
                </div>
              );
            })}
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          {dirtyCount > 0 && (
            <span className="self-center text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1">
              {dirtyCount} unsaved
            </span>
          )}
          <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={onSave} disabled={dirtyCount === 0 || saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

/* ─── Unit Detail Slide-out Panel ─── */
const UnitDetailPanel: React.FC<{
  unit: Unit;
  propertyId: number | string;
  onClose: () => void;
  onUpdate: () => void;
  onEdit?: (unit: Unit) => void;
}> = ({ unit, propertyId, onClose, onUpdate, onEdit }) => {
  const [tenants, setTenants] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [tenantModal, setTenantModal] = useState<{ open: boolean; tenant?: any }>({ open: false });
  const [confirmDelete, setConfirmDelete] = useState<number | null>(null);

  const fetchTenants = useCallback(async () => {
    setLoading(true);
    try {
      const { people } = await loadPropertyPeople(propertyId);
      console.log(`🔍 Fetched ${people.length} tenants for property ${propertyId}`);
      const unitTenants = people.filter((t: any) => {
            // Require a real unit_id — null/undefined values from historical rows
            // would otherwise match a unit's missing `uuid` and put orphan
            // tenants (Test Sub, Sydney Sweeney) in every unit's panel.
            if (!t.unit_id) return false;
            return t.unit_id === unit.uuid || String(t.unit_id) === String(unit.id);
          });
      console.log(`📋 Found ${unitTenants.length} tenants for unit ${unit.id}`);
      setTenants(unitTenants);
    } catch (e) {
      console.error('❌ Failed to fetch tenants:', e);
      setTenants([]);
    } finally {
      setLoading(false);
    }
  }, [unit.id, unit.uuid, propertyId]);

  useEffect(() => {
    fetchTenants();
  }, [fetchTenants]);

  const handleAddTenant = async (data: any) => {
    try {
      const newTenant = await createTenant({ ...data, unit_id: unit.id, property_id: propertyId });
      // Activation code is generated manually by the user from the Mobile Access tab or People detail page
      await fetchTenants();
      onUpdate();
    } catch (error: any) {
      console.error('Failed to create tenant:', error);
      alert(`Failed to add person: ${error?.response?.data?.message || error?.message || 'Unknown error'}`);
      throw error;
    }
  };

  const handleSelectExistingTenant = async (existingTenant: any) => {
    try {
      // `link_person_id` tells POST /people to bypass fuzzy dedup
      // (which misses contact-less records) and link the EXACT person
      // chosen in the picker via a tenant_properties junction row.
      // Without this, the backend would heuristically search by phone /
      // email / (name + same unit_id) and silently create a duplicate
      // people row for the same human at the new property.
      const payload = {
        link_person_id: existingTenant.id,
        first_name: existingTenant.first_name,
        last_name: existingTenant.last_name,
        email: existingTenant.email || '',
        phone: existingTenant.phone || '',
        unit_id: unit.id,
        property_id: propertyId,
      };
      console.log('🔵 Linking existing tenant:', payload);
      const result = await createTenant(payload);
      console.log('✅ Linked:', result, 'linked_existing=', (result as any).linked_existing);
      await fetchTenants();
      onUpdate();
    } catch (error: any) {
      console.error('❌ Failed to assign existing tenant:', error);
      console.error('❌ Error response:', error?.response?.data);
      alert(`Failed to assign person: ${error?.response?.data?.message || error?.message || 'Unknown error'}`);
      throw error;
    }
  };

  const handleEditTenant = async (data: any) => {
    if (!tenantModal.tenant) return;
    await updateTenant(tenantModal.tenant.id, data);
    await fetchTenants();
    onUpdate();
  };

  const handleDeleteTenant = async (id: number) => {
    try {
      // First, try to deactivate app access
      try {
        console.log('[Auto-deactivation] Looking up activation for tenant:', id);
        const activations = await apiList('app_activations', { tenant_id: id });
        const activation = Array.isArray(activations) && activations.length > 0 ? activations[0] : null;
        
        if (activation && activation.id) {
          console.log('[Auto-deactivation] Found activation:', activation.id);
          const response = await fetch(`${apiOrigin()}/intercom/deactivate`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ activation_id: activation.id })
          });
          
          if (response.ok) {
            console.log('[Auto-deactivation] Successfully revoked app access');
            alert('✅ Tenant removed and mobile app access revoked.');
          } else {
            console.warn('[Auto-deactivation] Deactivation request failed:', await response.text());
            alert('⚠️ Tenant removed, but failed to revoke app access.');
          }
        } else {
          console.log('[Auto-deactivation] No activation found for tenant');
        }
      } catch (deactivationError) {
        console.error('[Auto-deactivation] Error during deactivation:', deactivationError);
        // Continue with tenant deletion even if deactivation fails
      }
      
      // Delete the tenant record
      await deleteTenant(id);
      setConfirmDelete(null);
      await fetchTenants();
      onUpdate();
    } catch (error) {
      console.error('Failed to delete tenant:', error);
      alert('Failed to remove tenant. Please try again.');
      throw error;
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/30 z-40" onClick={onClose} />
      {/* Panel */}
      <div className="fixed top-0 right-0 h-full w-full max-w-md bg-white shadow-2xl z-50 flex flex-col animate-slideIn">
        <style>{`@keyframes slideIn{from{transform:translateX(100%)}to{transform:translateX(0)}}.animate-slideIn{animation:slideIn .2s ease-out}`}</style>
        {/* Header */}
        <div className="p-5 border-b border-gray-200 flex items-start justify-between">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Unit #{unit.unit_number}</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              Floor: {unit.floor || '—'} · Status: <Badge status={unit.status}>{unit.status}</Badge>
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none p-1">
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* People */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-900">People ({tenants.length})</h3>
              <Button size="sm" onClick={() => setTenantModal({ open: true })}>
                Add Person
              </Button>
            </div>
            {loading ? (
              <p className="text-sm text-gray-400 text-center py-4">Loading people...</p>
            ) : tenants.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-4">No people in this unit</p>
            ) : (
              <div className="space-y-2">
                {tenants.map((t: any) => (
                  <div key={t.id} className="border border-gray-200 rounded-lg p-3">
                    <p className="font-medium text-gray-900">
                      {t.first_name} {t.last_name}
                    </p>
                    <p className="text-sm text-gray-500">
                      {t.email && <span>{t.email}</span>}
                      {t.email && t.phone && <span> · </span>}
                      {t.phone && <span>{t.phone}</span>}
                    </p>
                    <div className="flex gap-2 mt-2">
                      <button
                        onClick={() => setTenantModal({ open: true, tenant: t })}
                        className="text-xs text-brand-600 hover:text-brand-700 font-medium"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => setConfirmDelete(t.id)}
                        className="text-xs text-red-600 hover:text-red-700 font-medium"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Unit Settings */}
          <div className="border-t border-gray-100 pt-4">
            <h3 className="font-semibold text-gray-900 mb-3">Unit Settings</h3>
            <div className="flex gap-2">
              <Button variant="secondary" size="sm" onClick={() => onEdit?.(unit)}>
                Edit Unit
              </Button>
              <Button variant="secondary" size="sm" className="!text-red-600 !border-red-200 hover:!bg-red-50">
                Delete Unit
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Person Add/Edit Modal */}
      <TenantFormModal
        open={tenantModal.open}
        onClose={() => setTenantModal({ open: false })}
        onSubmit={tenantModal.tenant ? handleEditTenant : handleAddTenant}
        onSelectExisting={handleSelectExistingTenant}
        initial={tenantModal.tenant}
        propertyId={propertyId}
        currentUnitTenantIds={tenants.map((t) => t.id)}
      />

      {/* Delete Confirmation */}
      <Modal open={confirmDelete !== null} onClose={() => setConfirmDelete(null)} title="Remove Person">
        <p className="text-sm text-gray-600 mb-4">Are you sure you want to remove this person?</p>
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setConfirmDelete(null)}>
            Cancel
          </Button>
          <Button
            className="!bg-red-600 hover:!bg-red-700"
            onClick={() => confirmDelete && handleDeleteTenant(confirmDelete)}
          >
            Remove
          </Button>
        </div>
      </Modal>
    </>
  );
};

/* ─── Property Detail Page ─── */
export const PropertyDetail: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const role = user?.role || 'customer';
  const [tab, setTab] = useState<Tab>('overview');

  const [unitModal, setUnitModal] = useState(false);
  const [reassignModal, setReassignModal] = useState(false);
  const [addPersonModal, setAddPersonModal] = useState(false);
  const [bulkImportMode, setBulkImportMode] = useState<ImportMode | null>(null);
  const [editingUnit, setEditingUnit] = useState<Unit | null>(null);
  const [editPropertyOpen, setEditPropertyOpen] = useState(false);
  const [savingProperty, setSavingProperty] = useState(false);

  const handleEditProperty = async (data: any) => {
    if (!id) return;
    setSavingProperty(true);
    try {
      const updated = await apiUpdate('properties', id, data);
      setProperty((prev: any) => ({ ...prev, ...updated }));
      setEditPropertyOpen(false);
      refreshAllCounters();
    } catch (err) {
      console.error('Failed to update property:', err);
      alert('Failed to update property — see console for details.');
    } finally {
      setSavingProperty(false);
    }
  };
  const [deletingUnit, setDeletingUnit] = useState<Unit | null>(null);
  const [deviceModal, setDeviceModal] = useState(false);
  const [editingDevice, setEditingDevice] = useState<any | null>(null);
  // Email Tenant compose dialog — opens with that tenant's address prefilled
  // and lets the signed-in user pick their own send surface (default mail app,
  // Outlook web, or Gmail web). Message is composed in the user's own client.
  const [emailTenant, setEmailTenant] = useState<any | null>(null);
  const [tenantConfirmDelete, setTenantConfirmDelete] = useState<any | null>(null);
  const [tenantDeleting, setTenantDeleting] = useState(false);
  const { selected: selectedTenants, toggle: toggleTenant, toggleAll: toggleAllTenants, clear: clearTenantSelection } = useSelection();
  const [routingDevice, setRoutingDevice] = useState<any | null>(null);
  // Per-device tenant-access editor — opened from the Devices tab row.
  // Shows every tenant on the property with a Mobile Access toggle that
  // reflects their effective visibility for THIS one device.
  const [deviceAccessFor, setDeviceAccessFor] = useState<any | null>(null);
  // Active Turing camera stream overlay state.
  const [turingPlay, setTuringPlay] = useState<{ systemId: string; cameraId: number; streamUrl: string | null; error: string | null } | null>(null);
  const [routingForm, setRoutingForm] = useState<{ preferred_integration: string; bridge_serial: string; bridge_channel: string; pulse_ms: string }>({ preferred_integration: '', bridge_serial: '', bridge_channel: '', pulse_ms: '' });
  const [routingSaving, setRoutingSaving] = useState(false);
  const [property, setProperty] = useState<any>(null);
  const [devices, setDevices] = useState<any[]>([]);
  const unlockDevices = useMemo(() => eligibleUnlockDevices(devices), [devices]);

  // Property detail device-type filter — pills under the Devices header
  // narrow the table to a single category (lock, camera, intercom, ...)
  // so PMs with a long device list can drill down without searching.
  const [deviceTypeFilter, setDeviceTypeFilter] = useState<string>('all');
  // Pre-warmed live-stream URLs keyed by device id. Populated as soon as
  // the user clicks the Devices tab so when they hit Play, the HLS
  // manifest is already cached + the player skips the cold start where
  // it would otherwise replay the last keyframe.
  const [warmedStreams, setWarmedStreams] = useState<Record<string, string>>({});
  const [units, setUnits] = useState<Unit[]>([]);
  const [calls, setCalls] = useState<CallRecord[]>([]);
  const [callDateFilter, setCallDateFilter] = useState<'today' | 'week' | 'month' | 'all'>('all');
  const [callStatusFilter, setCallStatusFilter] = useState<'all' | 'answered' | 'missed' | 'no_answer'>('all');
  const [allTenants, setAllTenants] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [lockLoading, setLockLoading] = useState<Record<number, boolean>>({});
  const [deviceStatuses, setDeviceStatuses] = useState<
    Record<number, { locked?: boolean; doorOpen?: boolean; online?: boolean }>
  >({});
  const [pdkDeviceIdMap, setPdkDeviceIdMap] = useState<Record<number, string>>({}); // DB device id → PDK device id
  const [selectedUnit, setSelectedUnit] = useState<Unit | null>(null);
  const [customerData, setCustomerData] = useState<any>(null);
  const [dealerData, setDealerData] = useState<any>(null);
  const [createSubOpen, setCreateSubOpen] = useState(false);
  const [createInvOpen, setCreateInvOpen] = useState(false);
  const [customPricingOpen, setCustomPricingOpen] = useState(false);
  const [mobileAccessData, setMobileAccessData] = useState<any[]>([]);
  const [loadingMobile, setLoadingMobile] = useState(false);
  const [actionLoading, setActionLoading] = useState<Record<number | string, boolean>>({});
  const [guestPins, setGuestPins] = useState<any[]>([]);
  const [propertyPasses, setPropertyPasses] = useState<any[]>([]);
  const [propertyPassLoadError, setPropertyPassLoadError] = useState('');
  const [revealedPropertyPassPins, setRevealedPropertyPassPins] = useState<Record<string, string>>({});
  const [propertyContacts, setPropertyContacts] = useState<any[]>([]);
  const [webUsersCount, setWebUsersCount] = useState<number>(0);
  const [activationsByTenant, setActivationsByTenant] = useState<Record<string, any>>({});
  const [latestActByTenant, setLatestActByTenant] = useState<Record<string, any>>({});
  const [tenantTypeFilter, setTenantTypeFilter] = useState<'all' | 'tenant' | 'subtenant'>('all');
  // ── Settings tab: scope picker for which intercom these settings apply to ──
  const [intercoms, setIntercoms] = useState<IntercomConfig[]>([]);
  const [selectedIntercomId, setSelectedIntercomId] = useState<string | number | ''>('');
  const [applySettingsToAll, setApplySettingsToAll] = useState(false);
  // Sidebar-driven view: only one settings group is visible at a time
  // (Intercom Devices + Settings Scope are always rendered above whichever
  // group is selected). Default to Branding and Display.
  const [activeSettingsView, setActiveSettingsView] = useState<
    'branding-display' | 'vv' | 'access-sec' | 'mobile-app'
  >('branding-display');
  const [contactModal, setContactModal] = useState<{ open: boolean; contact?: any }>({ open: false });
  const [contactForm, setContactForm] = useState({ name: '', role: '', phone: '', email: '', show_in_directory: false, dtmf_shortcut: '' });
  const [loadingGuests, setLoadingGuests] = useState(false);
  // Create-guest + create-delivery modal state (PM-side, lives next to the
  // Guests / Deliveries tabs on PropertyDetail).
  const [showCreateGuestModal, setShowCreateGuestModal] = useState(false);
  const [showCreateDeliveryModal, setShowCreateDeliveryModal] = useState(false);
  const [createGuestForm, setCreateGuestForm] = useState({
    tenant_id: '', first_name: '', last_name: '', phone: '', email: '',
    starts_at: '', expires_at: '', access_mode: 'all' as 'all' | 'selected' | 'none', device_ids: [] as string[],
    consent_enabled: false, consent_title: 'Visitor consent', consent_text: '', consent_acknowledgement: 'I have read and agree to this consent form.',
  });
  const [createDeliveryForm, setCreateDeliveryForm] = useState({
    tenant_id: '', carrier: '', courier_phone: '', arrival_at: '', expires_at: '', notes: '', access_mode: 'all' as 'all' | 'selected' | 'none', device_ids: [] as string[],
  });
  const [createGuestBusy, setCreateGuestBusy] = useState(false);
  const [createDeliveryBusy, setCreateDeliveryBusy] = useState(false);
  const [createGuestResult, setCreateGuestResult] = useState<{ pin: string; name: string; passUrl?: string } | null>(null);
  const [createDeliveryResult, setCreateDeliveryResult] = useState<{ pin: string; arrival: string; passUrl?: string } | null>(null);
  const [createGuestErr, setCreateGuestErr] = useState<string | null>(null);
  const [createDeliveryErr, setCreateDeliveryErr] = useState<string | null>(null);

  // Edit-guest / edit-delivery modal state — handlers live below where
  // intercomApiPost + fetchGuestPins are declared.
  const [editingGuest, setEditingGuest] = useState<any | null>(null);
  const [editGuestForm, setEditGuestForm] = useState({
    first_name: '', last_name: '', phone: '', email: '',
    starts_at: '', expires_at: '', device_ids: [] as string[],
    carrier: '', notes: '',
  });
  const [editGuestBusy, setEditGuestBusy] = useState(false);
  const [editGuestErr, setEditGuestErr] = useState<string | null>(null);
  const isEditingDelivery = editingGuest && String(editingGuest.kind || '').toLowerCase() === 'delivery';
  const [editPermModal, setEditPermModal] = useState<{ open: boolean; person?: any }>({ open: false });

  // ── Face Access state ──────────────────────────────────────────────────────
  const FACE_API = apiOrigin();
  const [faceEnrolledCount, setFaceEnrolledCount] = useState<number | null>(null);
  const [faceCountLoading, setFaceCountLoading] = useState(false);
  const [faceResetConfirm, setFaceResetConfirm] = useState(false);
  const [faceResetLoading, setFaceResetLoading] = useState(false);

  const fetchFaceCount = useCallback(async () => {
    if (!id) return;
    setFaceCountLoading(true);
    try {
      const res = await fetch(`${FACE_API}/face/count?property_id=${id}`);
      if (res.ok) {
        const data = await res.json();
        setFaceEnrolledCount(data.count ?? data.enrolled ?? 0);
      }
    } catch (e) {
      console.error('Failed to fetch face count:', e);
    } finally {
      setFaceCountLoading(false);
    }
  }, [id]);

  const handleFaceReset = async () => {
    if (!id) return;
    setFaceResetLoading(true);
    try {
      const res = await fetch(`${FACE_API}/face/reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ property_id: id }),
      });
      if (res.ok) {
        setFaceResetConfirm(false);
        setFaceEnrolledCount(0);
        alert('All face enrollments reset. Tenants will need to re-enroll.');
      } else {
        alert('Failed to reset enrollments. Please try again.');
      }
    } catch (e) {
      console.error('Face reset error:', e);
      alert('Connection error. Please try again.');
    } finally {
      setFaceResetLoading(false);
    }
  };

  // ── Visitor Intercom tab state ──────────────────────────────────────────────
  const [editGreeting, setEditGreeting] = useState('');
  const [editTheme, setEditTheme] = useState('Modern Dark');
  const [editDirectoryDisplay, setEditDirectoryDisplay] = useState('');
  const [editShowLogo, setEditShowLogo] = useState(true);
  const [editSearchUnit, setEditSearchUnit] = useState(true);
  const [editSearchTenant, setEditSearchTenant] = useState(true);
  const [editCallTimeout, setEditCallTimeout] = useState('45');
  const [editRingDuration, setEditRingDuration] = useState('30');
  const [editAllowUnlock, setEditAllowUnlock] = useState(true);
  const [editRecordCalls, setEditRecordCalls] = useState(true);
  const [editRequirePhone, setEditRequirePhone] = useState(false);
  const [intercomSaving, setIntercomSaving] = useState(false);
  const [intercomSaveMsg, setIntercomSaveMsg] = useState('');

  // ── Comprehensive Intercom Manager Controls state ────────────────────────────
  // Collapsible sections
  // Settings subsections are now always expanded (each view shows ONE Card with
  // its subsections inline). State kept for back-compat with the face-count
  // effect; default everything to true so dependent effects fire.
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    directory: true, calls: true, access: true, guestAccess: true,
    subTenant: true, branding: true, privacy: true, voice: true,
  });
  // Save state per section
  const [sectionSaving, setSectionSaving] = useState<Record<string, boolean>>({});
  const [sectionSaveMsg, setSectionSaveMsg] = useState<Record<string, string>>({});

  // Section 1: Directory Display Settings
  const [iDirectoryDisplay, setIDirectoryDisplay] = useState('tenant_name');
  const [iDirectoryNameFormat, setIDirectoryNameFormat] = useState('First Last');
  // Display defaults aligned with the property baseline: every helpful
  // visitor-facing toggle is ON unless the PM explicitly turns it off.
  const [iShowPhoneOnDirectory, setIShowPhoneOnDirectory] = useState(true);
  const [iShowFloorGrouping, setIShowFloorGrouping] = useState(true);
  const [iUnitSortOrder, setIUnitSortOrder] = useState('Numerical');
  const [iHideVacantUnits, setIHideVacantUnits] = useState(true);
  const [iShowTenantCount, setIShowTenantCount] = useState(true);

  // Section 2: Call Settings
  const [iAllowVideoCalls, setIAllowVideoCalls] = useState(true);
  const [iAllowPhoneCalls, setIAllowPhoneCalls] = useState(true);
  const [iCallTimeoutSeconds, setICallTimeoutSeconds] = useState('45');
  const [iRingDurationSeconds, setIRingDurationSeconds] = useState('30');
  const [iSimultaneousRing, setISimultaneousRing] = useState(true);
  const [iAllowDoorUnlockInCall, setIAllowDoorUnlockInCall] = useState(true);
  const [iRecordVideoCalls, setIRecordVideoCalls] = useState(false);
  const [iRequireVisitorName, setIRequireVisitorName] = useState(false);
  const [iRequireVisitorPhone, setIRequireVisitorPhone] = useState(false);
  const [iNoAnswerMessage, setINoAnswerMessage] = useState('');
  const [iCallBlockedMessage, setICallBlockedMessage] = useState('');

  // Section 3: Access Controls
  // PIN and Face are PREMIUM features — default OFF, PM must explicitly opt
  // in (and acknowledge the billing impact). Existing properties are zeroed
  // by migration 027.
  const [iPinAccessEnabled, setIPinAccessEnabled] = useState(false);
  const [iFaceAccessEnabled, setIFaceAccessEnabled] = useState(false);
  const [visitorPassEntryPhotoMode, setVisitorPassEntryPhotoMode] = useState<'off' | 'optional' | 'required'>('off');
  const [premiumConfirm, setPremiumConfirm] = useState<null | { feature: 'pin' | 'face'; price: string }>(null);
  const [iGuestPinAccessEnabled, setIGuestPinAccessEnabled] = useState(true);
  const [iRequireDirectoryCode, setIRequireDirectoryCode] = useState(false);
  const [iDirectoryAccessCode, setIDirectoryAccessCode] = useState('');
  const [iAutoLockEnabled, setIAutoLockEnabled] = useState(false);
  // Geofence — restrict door unlocks to a circular area around the property.
  const [iGeofenceEnabled, setIGeofenceEnabled] = useState(false);
  const [iGeofenceLat, setIGeofenceLat] = useState<string>('');
  const [iGeofenceLng, setIGeofenceLng] = useState<string>('');
  const [iGeofenceRadiusM, setIGeofenceRadiusM] = useState<string>('100');
  const [iGeofenceApplyWeb, setIGeofenceApplyWeb] = useState(true);
  const [iGeofenceApplyMobile, setIGeofenceApplyMobile] = useState(true);
  const [iAutoLockStart, setIAutoLockStart] = useState('22:00');
  const [iAutoLockEnd, setIAutoLockEnd] = useState('06:00');

  // Section 4: Guest Access Controls
  const [iAllowTenantCreateGuests, setIAllowTenantCreateGuests] = useState(true);
  const [iDefaultMaxGuestsPerTenant, setIDefaultMaxGuestsPerTenant] = useState('10');
  const [iDefaultMaxGuestDurationDays, setIDefaultMaxGuestDurationDays] = useState('30');
  const [iGuestDoorsAvailableJson, setIGuestDoorsAvailableJson] = useState<number[]>([]);
  const [iGuestPinLength, setIGuestPinLength] = useState('6');
  const [iGuestAppAccessEnabled, setIGuestAppAccessEnabled] = useState(false);
  const [iNotifyManagerOnGuestCreate, setINotifyManagerOnGuestCreate] = useState(false);
  const [iNotifyTenantOnGuestPinUse, setINotifyTenantOnGuestPinUse] = useState(false);

  // Legacy resident-control fields retained for stored-record compatibility.
  const [iAllowTenantCreateSubtenants, setIAllowTenantCreateSubtenants] = useState(false);
  const [iSubtenantReceiveCalls, setISubtenantReceiveCalls] = useState(false);
  const [iSubtenantDoorAccessDefault, setISubtenantDoorAccessDefault] = useState('Same as Tenant');
  const [iMinorAgeThreshold, setIMinorAgeThreshold] = useState('18');
  const [iMinorCallBlock, setIMinorCallBlock] = useState(false);

  // Section 6: Branding & Theme
  const [iDisplayStyle, setIDisplayStyle] = useState('modern_dark');
  const [iAccentColor, setIAccentColor] = useState('#059669');
  const [iBackgroundColor, setIBackgroundColor] = useState('#101010');
  const [iLogoUrl, setILogoUrl] = useState('');
  const [iLogoSizePx, setILogoSizePx] = useState<string>('80');
  const [iGreetingMessage, setIGreetingMessage] = useState('');
  // Voice/phone-call TTS greeting — edited under "Voice IVR" on the
  // Settings tab. Separate from the visitor-flow greeting (which lives
  // in `greeting_message`) so the operator can word each independently.
  const [iVoiceGreeting, setIVoiceGreeting] = useState('');
  // ── Delivery driver controls ─────────────────────────────────────────────
  // delivery_access_enabled: master toggle. When off, deliveries can't unlock
  //   anything regardless of the door list below.
  // delivery_door_ids: device IDs deliveries can unlock with their PIN.
  //   Empty = no doors enabled.
  // delivery_message: building-wide message shown to every delivery driver on
  //   the visitor / delivery flow IN ADDITION to the tenant's own arrival note.
  const [iDeliveryAccessEnabled, setIDeliveryAccessEnabled] = useState(false);
  const [iDeliveryDoorIds, setIDeliveryDoorIds] = useState<string[]>([]);
  const [iDeliveryMessage, setIDeliveryMessage] = useState('');
  const [iVoiceName, setIVoiceName] = useState('Azure.en-US-JennyNeural');
  const [iVoiceSpeed, setIVoiceSpeed] = useState('-10%');
  const [iVoicePitch, setIVoicePitch] = useState('medium');
  const [iVoiceVolume, setIVoiceVolume] = useState('medium');
  const [iVoiceLanguage, setIVoiceLanguage] = useState('en-US');
  const [iRingTone, setIRingTone] = useState('us');
  const [iDtmfUnlockDigit, setIDtmfUnlockDigit] = useState('9');
  const [iPreviewLoading, setIPreviewLoading] = useState(false);
  const [iCustomNoAnswerText, setICustomNoAnswerText] = useState('');
  const [iCustomFooterText, setICustomFooterText] = useState('');

  // Section 7: Privacy & Security
  const [iPrivateDirectory, setIPrivateDirectory] = useState(false);
  const [iHideTenantCountFromVisitors, setIHideTenantCountFromVisitors] = useState(false);
  const [iLogAllVisitorAccess, setILogAllVisitorAccess] = useState(false);
  const [iVisitorRateLimit, setIVisitorRateLimit] = useState('5');
  const [iBlockRepeatedNoAnswers, setIBlockRepeatedNoAnswers] = useState(false);
  const [iMaxNoAnswersBeforeBlock, setIMaxNoAnswersBeforeBlock] = useState('3');

  // ── Settings tab state ─────────────────────────────────────────────────────

  const fetchUnits = useCallback(async () => {
    try {
      const response = await getUnits({ property_id: id, per_page: 500 });
      const data = Array.isArray(response) ? response : [];
      const allUnits = Array.isArray(data) ? data : [];
      setUnits(allUnits.filter((u: any) => String(u.property_id) === String(id)));
    } catch (e) {
      console.error(e);
      setUnits([]);
    }
  }, [id]);

  const fetchTenants = useCallback(async () => {
    try {
      const { people: filtered, junctionRows } = await loadPropertyPeople(id!);
      // Enrich with activation_code from app_activations so the People
      // table can render an actual code instead of "—" for tenants
      // that already have one.
      try {
        // Backend requires property_id (or person_id) as a query param —
        // calling it bare returns 400. Pass property_id so the GSI lookup
        // returns only this property's activations.
        const activations = await apiList('app_activations', { property_id: String(id) });
        const acts = (Array.isArray(activations) ? activations : [])
          .filter((a: any) => String(a.property_id) === String(id));
        const codeByTenant = new Map<string, string>();
        for (const a of acts as any[]) {
          const key = String(a.tenant_id || '');
          if (!key) continue;
          const code = a.video_activation_code || a.activation_code;
          if (code && !codeByTenant.has(key)) codeByTenant.set(key, code);
        }
        // Per-tenant Call status — `tenant_properties.online_status` is the
        // source of truth for the tenant's visitor-call toggle at this
        // building, set from the iOS app. Keyed by every plausible person
        // identifier so we can match regardless of which API path produced
        // the tenant row.
        const callByPersonId = new Map<string, 'online' | 'offline'>();
        for (const tp of (Array.isArray(junctionRows) ? junctionRows : [])) {
          if (tp.status === 'revoked') continue;
          const raw = tp.online_status;
          const isOnline = raw === true || raw === 'online' || raw === 1 || raw === '1';
          const status: 'online' | 'offline' = isOnline ? 'online' : 'offline';
          for (const key of [tp.person_id, tp.tenant_id, tp.person_uuid, tp.tenant_uuid]) {
            if (key !== undefined && key !== null && key !== '') {
              callByPersonId.set(String(key), status);
            }
          }
        }
        const merged = filtered.map((t: any) => {
          const lookups = [t.id, t.uuid, t.person_id, t.person_uuid]
            .filter((v) => v !== undefined && v !== null && v !== '')
            .map(String);
          const callStatus = lookups.map((k) => callByPersonId.get(k)).find(Boolean) ?? 'offline';
          return {
            ...t,
            activation_code: t.activation_code || codeByTenant.get(String(t.uuid || t.id)) || '',
            call_status: callStatus,
          };
        });
        setAllTenants(merged);
      } catch {
        setAllTenants(filtered.map((t: any) => ({ ...t, call_status: 'offline' })));
      }
    } catch (e) {
      console.error(e);
      setAllTenants([]);
    }
  }, [id]);

  const fetchCalls = useCallback(async () => {
    try {
      const response = await getCalls({ property_id: id, per_page: 50 });
      const data = Array.isArray(response) ? response : [];
      const filtered = Array.isArray(data) ? data : [];
      // Pass through everything the call-history table needs (call_type,
      // door_unlocked, door_name, visitor_name, etc.) — backend already
      // normalizes these. The result column derives answered/missed/declined
      // from status + duration at render time, so don't collapse status here.
      const mapped = filtered.map((call: any): CallRecord => {
        const duration = Number(call.duration ?? call.duration_seconds ?? 0);
        const timestamp = call.timestamp || call.created_at || call.start_time || new Date().toISOString();
        return {
          ...call,
          id: call.id ?? call.uuid,
          property_id: call.property_id,
          unit_id: call.unit_id,
          unit_number: call.unit_number || '',
          tenant_name: call.tenant_name || '',
          timestamp,
          duration,
          status: call.status ?? '',
          call_type: call.call_type ?? '',
          door_unlocked: !!call.door_unlocked,
          door_name: call.door_name ?? call.door_id ?? '',
          assigned_door_names: Array.isArray(call.assigned_door_names) ? call.assigned_door_names : [],
          unlocked_door_names: Array.isArray(call.unlocked_door_names) ? call.unlocked_door_names : [],
          visitor_name: call.visitor_name ?? '',
          from_number: call.from_number ?? '',
        };
      });
      mapped.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
      setCalls(mapped);
    } catch (e) {
      console.error(e);
      setCalls([]);
    }
  }, [id]);

  const refreshProperty = useCallback(async () => {
    try {
      const [propData, latestIntercoms] = await Promise.all([
        getPropertyById(id!),
        getIntercomConfigs(id!),
      ]);
      setProperty(propData);
      setIntercoms(latestIntercoms);
      // Overlay the selected intercom's overrides so the toggles reflect
      // the actual effective values for the chosen scope. Without this,
      // saving a per-intercom override and then re-rendering would show
      // the property fallback instead of the intercom's specific value.
      let scoped = propData;
      if (selectedIntercomId) {
        const ic = latestIntercoms.find((i) => String(i.id) === String(selectedIntercomId));
        if (ic) scoped = {
          ...propData,
          ...Object.fromEntries(Object.entries(ic).filter(([, value]) => value != null)),
        };
      }
      const _src = scoped; // alias used below for clarity in setI* calls
      setEditGreeting(propData?.greeting_message || propData?.welcome_message || 'Welcome! Please select a unit to call.');
      setEditTheme(propData?.display_style || 'Modern Dark');
      setEditDirectoryDisplay(propData?.directory_display || propData?.name || '');
      setEditShowLogo(propData?.show_logo !== false);
      setEditSearchUnit(propData?.search_by_unit !== false);
      setEditSearchTenant(propData?.search_by_tenant !== false);
      setEditCallTimeout(String(propData?.call_timeout || '45'));
      setEditRingDuration(String(propData?.ring_duration || '30'));
      setEditAllowUnlock(propData?.allow_unlock !== false);
      setEditRecordCalls(propData?.record_calls !== false);
      setEditRequirePhone(propData?.require_phone === true);

      // Comprehensive intercom controls
      // Section 1: Directory Display — read via `_src` so per-intercom
      // overrides surface when an intercom is selected. Falls back to the
      // property row when the intercom config has no override yet.
      setIDirectoryDisplay(_src?.directory_display || 'tenant_name');
      setIDirectoryNameFormat(_src?.directory_name_format || 'First Last');
      // Default-ON helpful display toggles — `!== false` so undefined/null
      // resolves to ON (matches the new column DEFAULTs). Using `=== true`
      // would flip them OFF after every save when the API echoed back the
      // (truthy-but-not-strict-true) value.
      setIShowPhoneOnDirectory(_src?.show_phone_on_directory !== false);
      setIShowFloorGrouping(_src?.show_floor_grouping !== false);
      setIUnitSortOrder(_src?.unit_sort_order || 'Numerical');
      setIHideVacantUnits(_src?.hide_vacant_units !== false);
      setIShowTenantCount(_src?.show_tenant_count !== false);
      // Section 2: Call Settings — overlay selected-intercom overrides
      setIAllowVideoCalls((_src?.allow_video_call ?? _src?.allow_video_calls) !== false);
      setIAllowPhoneCalls((_src?.allow_phone_call ?? _src?.allow_phone_calls) !== false);
      setICallTimeoutSeconds(String(_src?.call_timeout_seconds ?? _src?.call_timeout ?? '45'));
      setIRingDurationSeconds(String(_src?.ring_duration_seconds ?? _src?.ring_duration ?? '30'));
      setISimultaneousRing(_src?.simultaneous_ring !== false);
      setIAllowDoorUnlockInCall(_src?.allow_door_unlock_in_call !== false);
      setIRecordVideoCalls(_src?.record_video_calls === true);
      setIRequireVisitorName(_src?.require_visitor_name === true);
      setIRequireVisitorPhone(_src?.require_visitor_phone === true);
      setINoAnswerMessage(_src?.no_answer_message || '');
      setICallBlockedMessage(_src?.call_blocked_message || '');
      // Section 3: Access Controls — overlay selected-intercom overrides.
      // PIN + Face are premium opt-in; treat undefined as OFF.
      setIPinAccessEnabled(_src?.pin_access_enabled === true);
      setIFaceAccessEnabled(_src?.face_access_enabled === true);
      setVisitorPassEntryPhotoMode(['off', 'optional', 'required'].includes(propData?.visitor_pass_entry_photo_mode)
        ? propData.visitor_pass_entry_photo_mode : 'off');
      setIGuestPinAccessEnabled(_src?.guest_pin_access_enabled !== false);
      setIRequireDirectoryCode(_src?.require_directory_code === true);
      setIDirectoryAccessCode(_src?.directory_access_code || '');
      setIAutoLockEnabled(_src?.auto_lock_enabled === true);
      setIAutoLockStart(_src?.auto_lock_start || '22:00');
      setIAutoLockEnd(_src?.auto_lock_end || '06:00');
      // Geofence
      setIGeofenceEnabled(propData?.geofence_enabled === true);
      setIGeofenceLat(propData?.geofence_lat ? String(propData.geofence_lat) : '');
      setIGeofenceLng(propData?.geofence_lng ? String(propData.geofence_lng) : '');
      setIGeofenceRadiusM(String(propData?.geofence_radius_m ?? '100'));
      const apply = Array.isArray(propData?.geofence_apply_to) ? propData.geofence_apply_to : ['web', 'mobile'];
      setIGeofenceApplyWeb(apply.includes('web'));
      setIGeofenceApplyMobile(apply.includes('mobile'));
      // Section 4: Guest Access Controls
      setIAllowTenantCreateGuests(propData?.allow_tenant_create_guests !== false);
      setIDefaultMaxGuestsPerTenant(String(propData?.default_max_guests_per_tenant || '10'));
      setIDefaultMaxGuestDurationDays(String(propData?.default_max_guest_duration_days || '30'));
      try {
        const guestDoors = propData?.guest_doors_available_json;
        setIGuestDoorsAvailableJson(guestDoors ? (typeof guestDoors === 'string' ? JSON.parse(guestDoors) : guestDoors) : []);
      } catch { setIGuestDoorsAvailableJson([]); }
      setIGuestPinLength(String(propData?.guest_pin_length || '6'));
      setIGuestAppAccessEnabled(propData?.guest_app_access_enabled === true);
      setINotifyManagerOnGuestCreate(propData?.notify_manager_on_guest_create === true);
      setINotifyTenantOnGuestPinUse(propData?.notify_tenant_on_guest_pin_use === true);
      // Legacy resident-control fields.
      setIAllowTenantCreateSubtenants(propData?.allow_tenant_create_subtenants !== false);
      setISubtenantReceiveCalls(propData?.subtenant_receive_calls === true);
      setISubtenantDoorAccessDefault(propData?.subtenant_door_access_default || 'Same as Tenant');
      setIMinorAgeThreshold(String(propData?.minor_age_threshold || '18'));
      setIMinorCallBlock(propData?.minor_call_block === true);
      // Section 6: Branding & Theme
      setIDisplayStyle(_src?.display_style || 'modern_dark');
      setIAccentColor(_src?.accent_color || '#059669');
      setIBackgroundColor(_src?.background_color || '#101010');
      setILogoUrl(_src?.logo_url || '');
      setILogoSizePx(String(_src?.logo_size_px ?? 80));
      setIGreetingMessage(_src?.greeting_message || _src?.welcome_message || '');
      // Delivery controls — always read from the property record (not the
      // per-intercom override) since deliveries are a property-wide policy.
      setIDeliveryAccessEnabled(!!propData?.delivery_access_enabled);
      setIDeliveryMessage(propData?.delivery_message || '');
      setIDeliveryDoorIds(Array.isArray(propData?.delivery_door_ids) ? propData.delivery_door_ids : []);
      // Voice IVR — now per-intercom. Read via `_src` so per-intercom
      // overrides surface; falls back to property defaults when the
      // intercom row hasn't set a value yet.
      setIVoiceGreeting(_src?.voice_greeting_message || '');
      setIVoiceName(_src?.voice_name || 'Azure.en-US-JennyNeural');
      setIVoiceSpeed(_src?.voice_speed || '-10%');
      setIVoicePitch(_src?.voice_pitch || 'medium');
      setIVoiceVolume(_src?.voice_volume || 'medium');
      setIVoiceLanguage(_src?.voice_language || 'en-US');
      setIRingTone(_src?.ring_tone || 'us');
      setIDtmfUnlockDigit(_src?.dtmf_unlock_digit || _src?.dtmf_action_digit || '9');
      setICustomNoAnswerText(_src?.custom_no_answer_text || '');
      setICustomFooterText(_src?.custom_footer_text || '');
      // Section 7: Privacy & Security
      setIPrivateDirectory(_src?.private_directory === true);
      setIHideTenantCountFromVisitors(propData?.hide_tenant_count_from_visitors === true);
      setILogAllVisitorAccess(_src?.log_all_visitor_access === true);
      setIVisitorRateLimit(String(_src?.visitor_rate_limit || '5'));
      setIBlockRepeatedNoAnswers(_src?.block_repeated_no_answers === true);
      setIMaxNoAnswersBeforeBlock(String(_src?.max_no_answers_before_block || '3'));

      // Load contacts from JSON field
      try {
        const contactsJson = propData?.contacts_json;
        const contacts = contactsJson ? (typeof contactsJson === 'string' ? JSON.parse(contactsJson) : contactsJson) : [];
        setPropertyContacts(Array.isArray(contacts) ? contacts : []);
      } catch {
        setPropertyContacts([]);
      }
    } catch (e) {
      console.error('Failed to refresh property:', e);
    }
  }, [id, selectedIntercomId]);

  // Re-overlay scoped settings whenever the user changes the Settings Scope picker
  // so the toggles immediately reflect the chosen intercom's state.
  useEffect(() => {
    if (id && intercoms.length > 0) refreshProperty();
    // Intentionally only triggered by selectedIntercomId — adding refreshProperty
    // to deps would cause an infinite loop because refreshProperty changes when
    // intercoms updates inside it.
  }, [selectedIntercomId]); // eslint-disable-line

  const fetchMobileAccessData = useCallback(async () => {
    if (!id) return;
    setLoadingMobile(true);
    try {
      // Fetch all tenants for this property
      const tenantsResponse = await apiList('tenants', { property_id: id });
      const propertyTenants = Array.isArray(tenantsResponse) ? tenantsResponse : [];

      // Fetch all app_activations for this property
      const activationsResponse = await apiList('app_activations', { property_id: id });
      const activations = Array.isArray(activationsResponse) ? activationsResponse : [];
      
      // Fetch units to get unit numbers
      const unitsResponse = await getUnits({ property_id: id, per_page: 500 });
      const unitsData = Array.isArray(unitsResponse) ? unitsResponse : [];
      const propertyUnits = Array.isArray(unitsData) ? unitsData.filter((u: any) => String(u.property_id) === String(id)) : [];
      
      // Create a map of tenant_id -> activation
      const activationMap = new Map();
      activations.forEach((act: any) => {
        if (act.tenant_id) {
          activationMap.set(act.tenant_id, act);
        }
      });
      
      // Create a map of unit_id -> unit_number
      const unitMap = new Map();
      propertyUnits.forEach((u: any) => {
        if (u.id && u.unit_number) {
          unitMap.set(u.id, u.unit_number);
        }
      });
      
      // Merge data
      const merged = propertyTenants.map((tenant: any) => {
        const activation = activationMap.get(tenant.id);
        let status = 'Not Invited';
        let statusColor = 'gray';

        if (activation) {
          if (activation.deactivated === true) {
            status = 'Revoked';
            statusColor = 'gray';
          } else if (activation.activated === true) {
            status = 'Activated';
            statusColor = 'green';
          } else {
            status = 'Pending';
            statusColor = 'yellow';
          }
        }

        return {
          tenant_id: tenant.id,
          tenant_name: `${tenant.first_name || ''} ${tenant.last_name || ''}`.trim(),
          unit_number: tenant.unit_number || unitMap.get(tenant.unit_id) || '—',
          status,
          statusColor,
          platform: activation?.platform || '—',
          activated_date: activation?.activated_at || activation?.created_at || '—',
          activation_code: activation?.video_activation_code || activation?.activation_code || '',
          video_activation_code: activation?.video_activation_code || '',
          access_pin: tenant.access_pin || '',
          activated: activation?.activated === true,
          deactivated: activation?.deactivated === true,
          has_activation: !!activation,
          activation_id: activation?.id || null,
          device_model: activation?.device_model || '—',
          device_os: activation?.device_os || '—',
          device_fingerprint: activation?.device_fingerprint || '',
          suspended_by_device: activation?.suspended_by_device || false,
        };
      });
      
      setMobileAccessData(merged);
    } catch (e) {
      console.error('Failed to fetch mobile access data:', e);
      setMobileAccessData([]);
    } finally {
      setLoadingMobile(false);
    }
  }, [id]);

  const fetchGuestPins = useCallback(async () => {
    if (!id) return;
    setLoadingGuests(true);
    try {
      try {
        const passes = await apiRequestGet(`/visitors/passes?property_id=${encodeURIComponent(String(id))}`);
        setPropertyPasses(Array.isArray(passes) ? passes : []);
        setPropertyPassLoadError('');
      } catch (passError: any) {
        setPropertyPassLoadError(`Secure visitor passes could not be loaded: ${passError?.message || passError}`);
      }
      const pins = await apiList('guest_pins', { property_id: id });
      const arr: any[] = Array.isArray(pins) ? pins : [];

      // Enrich each guest_pin with its most-recent app_activation so the
      // table's Mobile App + Generate Code columns reflect the latest
      // minted code (and whether it's been consumed). Without this the
      // guest_pin row itself never gains an activation_code field — only
      // the activation record does — so the cell would stay "—".
      try {
        const acts: any[] = await apiList('app_activations', { property_id: id });
        if (Array.isArray(acts) && acts.length) {
          // Newest activation wins per guest_pin_id.
          const sorted = [...acts].sort((a: any, b: any) => {
            const ta = Date.parse(String(a.created_at || a.video_code_expires_at || ''));
            const tb = Date.parse(String(b.created_at || b.video_code_expires_at || ''));
            return tb - ta;
          });
          const byPin = new Map<string, any>();
          for (const a of sorted) {
            const gpi = String(a.guest_pin_id || '');
            if (gpi && !byPin.has(gpi)) byPin.set(gpi, a);
          }
          for (let i = 0; i < arr.length; i++) {
            const g = arr[i];
            const a = byPin.get(String(g.uuid || g.id));
            if (a) {
              arr[i] = {
                ...g,
                activation_code: a.activation_code || a.video_activation_code || g.activation_code || '',
                activation_activated: a.activated === true,
                activation_expires_at: a.video_code_expires_at || g.activation_expires_at,
              };
            }
          }
        }
      } catch (err) {
        console.warn('[fetchGuestPins] activation enrichment failed:', err);
      }

      setGuestPins(arr);
    } catch (e) {
      console.error('Failed to fetch guest pins:', e);
      setGuestPins([]);
    } finally {
      setLoadingGuests(false);
    }
  }, [id]);

  // Helper to POST to /intercom/* endpoints with the admin user's JWT.
  const intercomApiPost = useCallback(async (path: string, body: any) => {
    const API_BASE = process.env.REACT_APP_API_URL || 'https://api.intrlock.io';
    const token = localStorage.getItem('intrlock_token') || localStorage.getItem('jwt') || localStorage.getItem('token') || '';
    const res = await fetch(`${API_BASE}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data?.error || data?.message || `HTTP ${res.status}`);
    return data;
  }, []);

  // Per-row reveal state for guest/delivery PINs. Keyed by guest id;
  // value is "revealed" so the masked default is safe to leave open.
  const [revealedPinIds, setRevealedPinIds] = useState<Set<string>>(new Set());
  const togglePinReveal = useCallback((id: string) => {
    setRevealedPinIds((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id); else {
        next.add(id);
        // Auto-hide after 10s so a forgotten reveal in a public space
        // doesn't leak the code.
        setTimeout(() => {
          setRevealedPinIds((m) => { const c = new Set(m); c.delete(id); return c; });
        }, 10000);
      }
      return next;
    });
  }, []);

  const regenerateGuestPin = useCallback(async (g: any) => {
    const id = String(g.uuid || g.id || '');
    if (!id) return;
    const isDelivery = String(g.kind || '').toLowerCase() === 'delivery';
    if (!window.confirm(`Regenerate this ${isDelivery ? 'delivery' : 'guest'} PIN? The current code will stop working immediately and a new one will be issued.`)) return;
    try {
      const r = await intercomApiPost('/intercom/regenerate-guest-pin', { guest_id: id });
      await fetchGuestPins();
      refreshAllCounters();
      // Auto-reveal the freshly minted PIN so the PM can copy/share it.
      setRevealedPinIds((cur) => { const next = new Set(cur); next.add(id); return next; });
      window.alert(`New ${isDelivery ? 'delivery' : 'guest'} PIN: ${r?.pin_code}`);
    } catch (err: any) {
      window.alert(`Failed to regenerate: ${err?.message || err}`);
    }
  }, [intercomApiPost, fetchGuestPins]);

  const submitCreateGuest = useCallback(async () => {
    setCreateGuestErr(null);
    setCreateGuestResult(null);
    const f = createGuestForm;
    if (!f.first_name.trim() || !f.last_name.trim() || !f.phone.trim()) { setCreateGuestErr('First name, last name, and phone are required'); return; }
    if (f.access_mode === 'selected' && f.device_ids.length === 0) { setCreateGuestErr('Select at least one authorized door'); return; }
    if (f.consent_enabled && !f.consent_text.trim()) { setCreateGuestErr('Enter the consent disclosure or turn off required consent'); return; }
    setCreateGuestBusy(true);
    try {
      const startsIso = f.starts_at ? new Date(f.starts_at).toISOString() : new Date().toISOString();
      const endsIso   = f.expires_at ? new Date(f.expires_at).toISOString() : new Date(new Date(startsIso).getTime() + 24*60*60*1000).toISOString();
      const selectedTenant = allTenants.find((tenant: any) => String(tenant.uuid || tenant.id) === String(f.tenant_id));
      const r = await apiPost('/visitors/passes', buildVisitorPassPayload({
        propertyId: String(id), unitId: selectedTenant?.unit_id || undefined,
        visitorName: `${f.first_name.trim()} ${f.last_name.trim()}`,
        phone: f.phone, email: f.email,
        validFrom: new Date(startsIso), validUntil: new Date(endsIso),
        passType: 'visitor', accessMode: f.access_mode, deviceIds: f.device_ids,
        consentForm: f.consent_enabled ? { enabled: true, title: f.consent_title.trim(), text: f.consent_text.trim(), acknowledgement: f.consent_acknowledgement.trim() } : undefined,
      }));
      setCreateGuestResult({ pin: r?.directory_code || '—', passUrl: r?.pass_url, name: `${f.first_name} ${f.last_name}` });
      await fetchGuestPins();
      refreshAllCounters();
      setCreateGuestForm((cur) => ({ ...cur, first_name: '', last_name: '', phone: '', email: '' }));
    } catch (err: any) {
      setCreateGuestErr(err?.message || 'Failed to create guest PIN');
    } finally {
      setCreateGuestBusy(false);
    }
  }, [allTenants, createGuestForm, fetchGuestPins, id]);

  const submitCreateDelivery = useCallback(async () => {
    setCreateDeliveryErr(null);
    setCreateDeliveryResult(null);
    const f = createDeliveryForm;
    if (!f.arrival_at) { setCreateDeliveryErr('Expected arrival is required'); return; }
    if (!f.tenant_id) { setCreateDeliveryErr('Select the resident receiving this delivery'); return; }
    if (!f.courier_phone.trim()) { setCreateDeliveryErr('Enter the delivery contact phone used to verify the secure pass'); return; }
    if (f.access_mode === 'selected' && f.device_ids.length === 0) { setCreateDeliveryErr('Select at least one authorized door'); return; }
    const selectedTenant = allTenants.find((tenant: any) => String(tenant.uuid || tenant.id) === String(f.tenant_id));
    const phone = f.courier_phone.trim();
    setCreateDeliveryBusy(true);
    try {
      const validFrom = new Date(f.arrival_at);
      const validUntil = f.expires_at ? new Date(f.expires_at) : new Date(validFrom.getTime() + 24 * 60 * 60 * 1000);
      const recipientName = [selectedTenant?.first_name, selectedTenant?.last_name].filter(Boolean).join(' ') || 'resident';
      const r = await apiPost('/visitors/passes', buildVisitorPassPayload({
        propertyId: String(id), unitId: selectedTenant?.unit_id || undefined,
        visitorName: f.carrier.trim() ? `${f.carrier.trim()} delivery for ${recipientName}` : `Delivery for ${recipientName}`,
        phone,
        validFrom, validUntil, passType: 'delivery',
        deliveryInstructions: f.notes,
        accessMode: f.access_mode, deviceIds: f.device_ids,
      }));
      setCreateDeliveryResult({ pin: r?.directory_code || '—', passUrl: r?.pass_url, arrival: validFrom.toLocaleString() });
      await fetchGuestPins();
      refreshAllCounters();
      setCreateDeliveryForm((cur) => ({ ...cur, carrier: '', courier_phone: '', notes: '' }));
    } catch (err: any) {
      setCreateDeliveryErr(err?.message || 'Failed to create delivery pass');
    } finally {
      setCreateDeliveryBusy(false);
    }
  }, [allTenants, createDeliveryForm, fetchGuestPins, id]);

  const openGuestEditor = useCallback((g: any) => {
    const doorJson = typeof g.door_access_json === 'string'
      ? (() => { try { return JSON.parse(g.door_access_json); } catch { return []; } })()
      : (Array.isArray(g.door_access_json) ? g.door_access_json : []);
    const doorIds: string[] = Array.isArray(doorJson)
      ? doorJson.map(String)
      : (Array.isArray((doorJson as any)?.device_ids) ? (doorJson as any).device_ids.map(String) : []);
    const toLocalInput = (iso: string | null | undefined) => {
      if (!iso) return '';
      const d = new Date(iso);
      if (isNaN(d.getTime())) return '';
      const pad = (n: number) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    };
    setEditGuestForm({
      first_name: g.guest_first_name || g.first_name || '',
      last_name:  g.guest_last_name  || g.last_name  || '',
      phone:      g.guest_phone || g.phone || '',
      email:      g.guest_email || g.email || '',
      starts_at:  toLocalInput(g.starts_at),
      expires_at: toLocalInput(g.expires_at || g.delivery_arrival_at),
      device_ids: doorIds,
      carrier:    g.delivery_carrier || '',
      notes:      g.delivery_notes   || '',
    });
    setEditGuestErr(null);
    setEditingGuest(g);
  }, []);

  const submitEditGuest = useCallback(async () => {
    if (!editingGuest) return;
    setEditGuestErr(null);
    const f = editGuestForm;
    const isDelivery = String(editingGuest.kind || '').toLowerCase() === 'delivery';
    if (!isDelivery && (!f.first_name.trim() || !f.last_name.trim())) {
      setEditGuestErr('First and last name required'); return;
    }
    setEditGuestBusy(true);
    try {
      await intercomApiPost('/intercom/update-guest-pin', {
        guest_id: editingGuest.uuid || editingGuest.id,
        ...(isDelivery ? {} : {
          first_name: f.first_name.trim(),
          last_name: f.last_name.trim(),
          phone: f.phone || '',
          email: f.email || '',
        }),
        device_ids: f.device_ids,
        starts_at: f.starts_at ? new Date(f.starts_at).toISOString() : undefined,
        expires_at: f.expires_at ? new Date(f.expires_at).toISOString() : undefined,
        ...(isDelivery ? { delivery_carrier: f.carrier, delivery_notes: f.notes, delivery_arrival_at: f.starts_at ? new Date(f.starts_at).toISOString() : undefined } : {}),
      });
      await fetchGuestPins();
      refreshAllCounters();
      setEditingGuest(null);
    } catch (err: any) {
      setEditGuestErr(err?.message || 'Failed to save');
    } finally {
      setEditGuestBusy(false);
    }
  }, [editingGuest, editGuestForm, intercomApiPost, fetchGuestPins]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [propData, devData] = await Promise.all([getPropertyById(id!), getDevices({ property_id: id, per_page: 500 })]);
        setProperty(propData);
        // Initialize editable field state from loaded property data
        setEditGreeting(propData?.greeting_message || propData?.welcome_message || 'Welcome! Please select a unit to call.');
        setEditTheme(propData?.display_style || 'Modern Dark');
        setEditDirectoryDisplay(propData?.directory_display || propData?.name || '');
        setEditShowLogo(propData?.show_logo !== false);
        setEditSearchUnit(propData?.search_by_unit !== false);
        setEditSearchTenant(propData?.search_by_tenant !== false);
        setEditCallTimeout(String(propData?.call_timeout || '45'));
        setEditRingDuration(String(propData?.ring_duration || '30'));
        setEditAllowUnlock(propData?.allow_unlock !== false);
        setEditRecordCalls(propData?.record_calls !== false);
        setEditRequirePhone(propData?.require_phone === true);

        // Comprehensive intercom controls – initial load
        // Section 1: Directory Display
        setIDirectoryDisplay(propData?.directory_display || 'tenant_name');
        setIDirectoryNameFormat(propData?.directory_name_format || 'First Last');
        // Default-ON helpful display toggles — `!== false` so the new
        // column DEFAULTs (TRUE) carry through when no explicit value yet.
        setIShowPhoneOnDirectory(propData?.show_phone_on_directory !== false);
        setIShowFloorGrouping(propData?.show_floor_grouping !== false);
        setIUnitSortOrder(propData?.unit_sort_order || 'Numerical');
        setIHideVacantUnits(propData?.hide_vacant_units !== false);
        setIShowTenantCount(propData?.show_tenant_count !== false);
        // Section 2: Call Settings — postgres columns are SINGULAR.
        setIAllowVideoCalls((propData?.allow_video_call ?? propData?.allow_video_calls) !== false);
        setIAllowPhoneCalls((propData?.allow_phone_call ?? propData?.allow_phone_calls) !== false);
        setICallTimeoutSeconds(String(propData?.call_timeout_seconds || propData?.call_timeout || '45'));
        setIRingDurationSeconds(String(propData?.ring_duration_seconds || propData?.ring_duration || '30'));
        setISimultaneousRing(propData?.simultaneous_ring !== false);
        setIAllowDoorUnlockInCall(propData?.allow_door_unlock_in_call !== false);
        setIRecordVideoCalls(propData?.record_video_calls === true);
        setIRequireVisitorName(propData?.require_visitor_name === true);
        setIRequireVisitorPhone(propData?.require_visitor_phone === true);
        setINoAnswerMessage(propData?.no_answer_message || '');
        setICallBlockedMessage(propData?.call_blocked_message || '');
        // Section 3: Access Controls — PIN + Face premium opt-in (default OFF)
        setIPinAccessEnabled(propData?.pin_access_enabled === true);
        setIFaceAccessEnabled(propData?.face_access_enabled === true);
        setVisitorPassEntryPhotoMode(['off', 'optional', 'required'].includes(propData?.visitor_pass_entry_photo_mode)
          ? propData.visitor_pass_entry_photo_mode : 'off');
        setIGuestPinAccessEnabled(propData?.guest_pin_access_enabled !== false);
        setIRequireDirectoryCode(propData?.require_directory_code === true);
        setIDirectoryAccessCode(propData?.directory_access_code || '');
        setIAutoLockEnabled(propData?.auto_lock_enabled === true);
        setIAutoLockStart(propData?.auto_lock_start || '22:00');
        setIAutoLockEnd(propData?.auto_lock_end || '06:00');
        setIGeofenceEnabled(propData?.geofence_enabled === true);
        setIGeofenceLat(propData?.geofence_lat ? String(propData.geofence_lat) : '');
        setIGeofenceLng(propData?.geofence_lng ? String(propData.geofence_lng) : '');
        setIGeofenceRadiusM(String(propData?.geofence_radius_m ?? '100'));
        {
          const apply = Array.isArray(propData?.geofence_apply_to) ? propData.geofence_apply_to : ['web', 'mobile'];
          setIGeofenceApplyWeb(apply.includes('web'));
          setIGeofenceApplyMobile(apply.includes('mobile'));
        }
        // Section 4: Guest Access Controls
        setIAllowTenantCreateGuests(propData?.allow_tenant_create_guests !== false);
        setIDefaultMaxGuestsPerTenant(String(propData?.default_max_guests_per_tenant || '10'));
        setIDefaultMaxGuestDurationDays(String(propData?.default_max_guest_duration_days || '30'));
        try {
          const guestDoors = propData?.guest_doors_available_json;
          setIGuestDoorsAvailableJson(guestDoors ? (typeof guestDoors === 'string' ? JSON.parse(guestDoors) : guestDoors) : []);
        } catch { setIGuestDoorsAvailableJson([]); }
        setIGuestPinLength(String(propData?.guest_pin_length || '6'));
        setIGuestAppAccessEnabled(propData?.guest_app_access_enabled === true);
        setINotifyManagerOnGuestCreate(propData?.notify_manager_on_guest_create === true);
        setINotifyTenantOnGuestPinUse(propData?.notify_tenant_on_guest_pin_use === true);
        // Legacy resident-control fields.
        setIAllowTenantCreateSubtenants(propData?.allow_tenant_create_subtenants !== false);
        setISubtenantReceiveCalls(propData?.subtenant_receive_calls === true);
        setISubtenantDoorAccessDefault(propData?.subtenant_door_access_default || 'Same as Tenant');
        setIMinorAgeThreshold(String(propData?.minor_age_threshold || '18'));
        setIMinorCallBlock(propData?.minor_call_block === true);
        // Section 6: Branding & Theme
        setIDisplayStyle(propData?.display_style || 'modern_dark');
        setIAccentColor(propData?.accent_color || '#059669');
        setIBackgroundColor(propData?.background_color || '#101010');
        setILogoUrl(propData?.logo_url || '');
      setILogoSizePx(String(propData?.logo_size_px ?? 80));
        setIGreetingMessage(propData?.greeting_message || propData?.welcome_message || '');
      setIVoiceGreeting(propData?.voice_greeting_message || '');
        setIDeliveryAccessEnabled(!!propData?.delivery_access_enabled);
        setIDeliveryMessage(propData?.delivery_message || '');
        setIDeliveryDoorIds(Array.isArray(propData?.delivery_door_ids) ? propData.delivery_door_ids : []);
        setICustomNoAnswerText(propData?.custom_no_answer_text || '');
        setICustomFooterText(propData?.custom_footer_text || '');
        // Section 7: Privacy & Security
        setIPrivateDirectory(propData?.private_directory === true);
        setIHideTenantCountFromVisitors(propData?.hide_tenant_count_from_visitors === true);
        setILogAllVisitorAccess(propData?.log_all_visitor_access === true);
        setIVisitorRateLimit(String(propData?.visitor_rate_limit || '5'));
        setIBlockRepeatedNoAnswers(propData?.block_repeated_no_answers === true);
        setIMaxNoAnswersBeforeBlock(String(propData?.max_no_answers_before_block || '3'));

        // Load contacts from JSON field
        try {
          const contactsJson = propData?.contacts_json;
          const contacts = contactsJson ? (typeof contactsJson === 'string' ? JSON.parse(contactsJson) : contactsJson) : [];
          setPropertyContacts(Array.isArray(contacts) ? contacts : []);
        } catch {
          setPropertyContacts([]);
        }

        // Intercoms — drives the Settings-tab scope picker so you can choose
        // which intercom the directory / call / voice / etc. settings apply to.
        try {
          const ic = await getIntercomConfigs(id!);
          setIntercoms(ic);
          if (ic.length > 0 && !selectedIntercomId) setSelectedIntercomId(ic[0].id);
        } catch {}

        // Mobile activations for this property — drives Mobile App + OS columns
        // on the Tenants tab. Keep most-recent active row per tenant.
        try {
          const API = process.env.REACT_APP_API_URL || 'https://api.intrlock.io';
          const token = localStorage.getItem('intrlock_token') || localStorage.getItem('jwt') || localStorage.getItem('token') || '';
          const res = await fetch(`${API}/activations?property_id=${encodeURIComponent(String(id))}`, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          });
          if (res.ok) {
            const acts = await res.json();
            const activeMap: Record<string, any> = {};
            const latestMap: Record<string, any> = {};
            (Array.isArray(acts) ? acts : []).forEach((a: any) => {
              const tid = String(a.tenant_id || a.person_id || '');
              if (!tid) return;
              const tsA = new Date(a.updatedAt || a.updated_at || a.created_at || 0).getTime();
              // Track most-recent of any activation row (for showing codes).
              const existingAny = latestMap[tid];
              const tsAnyE = existingAny ? new Date(existingAny.updatedAt || existingAny.updated_at || existingAny.created_at || 0).getTime() : 0;
              if (!existingAny || tsA > tsAnyE) latestMap[tid] = a;
              // Separately track most-recent currently-active activation (for ✓ Active badge).
              const isActive = a.activated === true && !a.deactivated && !a.revoked_at && a.status !== 'revoked';
              if (!isActive) return;
              const existingActive = activeMap[tid];
              const tsAcE = existingActive ? new Date(existingActive.updatedAt || existingActive.updated_at || existingActive.created_at || 0).getTime() : 0;
              if (!existingActive || tsA > tsAcE) activeMap[tid] = a;
            });
            setActivationsByTenant(activeMap);
            setLatestActByTenant(latestMap);
          }
        } catch {}

        // Web users with VISIBILITY into this property — count for tab badge.
        // Mirrors UsersList's property-scope filter: PMs tied to this
        // property, customer-role users for this customer, and dealer-role
        // users for this dealer.
        try {
          const allUsers = await apiList('users');
          const candidateIds = [id, propData?.uuid, propData?.id].filter(Boolean).map(String);
          const dId = propData?.dealer_id ? String(propData.dealer_id) : null;
          const cId = propData?.customer_id ? String(propData.customer_id) : null;
          const propUsers = (allUsers || []).filter((u: any) => {
            const role = (u.role ?? '').toLowerCase();
            if (role === 'property_manager' || role === 'property' || role === 'manager') {
              if (u.property_id && candidateIds.includes(String(u.property_id))) return true;
              const pids = u.property_ids ?? u.property_ids_json;
              const arr = Array.isArray(pids)
                ? pids
                : (typeof pids === 'string' ? (() => { try { return JSON.parse(pids) || []; } catch { return []; } })() : []);
              return arr.some((pid: any) => candidateIds.includes(String(pid)));
            }
            if (role === 'customer' || role === 'customer_admin') {
              return cId != null && String(u.customer_id) === cId;
            }
            if (role === 'dealer' || role === 'dealer_admin' || role === 'integrator' || role === 'integrator_admin') {
              return dId != null && (String(u.dealer_id) === dId || String(u.integrator_id) === dId);
            }
            return false;
          });
          setWebUsersCount(propUsers.length);
        } catch {
          setWebUsersCount(0);
        }

        const devAll = Array.isArray(devData) ? devData : [];
        const devArr = devAll.filter((d: any) => String(d.property_id) === String(id));

        // Add Bridge relay channels as virtual devices
        try {
          const bridgeData = await apiList('bridge_devices');
          const bridgeChannels = (Array.isArray(bridgeData) ? bridgeData : [])
            .filter((b: any) => b.claimed && String(b.property_id) === String(id))
            .flatMap((b: any) => {
              const names = b.channel_config_json?.channel_names || {};
              const defaults: Record<number, string> = { 1: 'J2', 2: 'J3', 3: 'J4', 4: 'J5' };
              const serial = b.serial_number || b.bridge_device_id || b.id;
              return Array.from({ length: b.channel_count || 0 }, (_, i) => ({
                id: `bridge-${b.id}-ch${i + 1}`,
                name: names[i + 1] || defaults[i + 1] || `Channel ${i + 1}`,
                type: 'relay',
                device_type: 'relay',
                property_id: b.property_id,
                status: 'active',
                acs_provider: 'bridge',
                acs_device_id: `${serial}:ch${i + 1}`,
                connection_status: b.status === 'online' ? 'online' : 'offline',
                manufacturer: 'Intrlock Bridge',
                model: b.name || 'Bridge Relay',
              }));
            });
          devArr.push(...bridgeChannels);
        } catch {}

        // /devices already merges legacy + ACS devices for this property,
        // and bridgeChannels was pushed above. No further fetch needed —
        // PropertyDetail shows every device assigned to the property
        // regardless of source.
        setDevices(devArr);
        // Fetch live device statuses
        if (devArr.length > 0) fetchDeviceStatuses(devArr, propData);
        // Fetch customer for billing buttons + header context.
        // Then chase the dealer so super-admins can see the full ownership chain at a glance.
        if (propData?.customer_id) {
          try {
            const allCusts = await getCustomers();
            const cust = (allCusts || []).find((c: any) => c.id === propData.customer_id);
            if (cust) {
              setCustomerData(cust);
              const dealerId = cust.dealer_id || cust.integrator_id;
              if (dealerId) {
                try {
                  const dealer = await getIntegratorById(dealerId);
                  if (dealer) setDealerData(dealer);
                } catch {}
              }
            }
          } catch {}
        }
      } catch (e) {
        console.error('Failed to load property:', e);
        setProperty({
          id: id!,
          name: 'Property',
          address: '',
          city: '',
          state: '',
          zip: '',
          customer_name: '',
          phone: '',
          unit_count: 0,
          device_count: 0,
          status: 'active',
        });
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    fetchUnits();
    fetchTenants();
    fetchCalls();
  }, [id, fetchUnits, fetchTenants, fetchCalls]);

  // Calculate tenant count for each unit
  const unitsWithTenantCount = units.map((unit) => {
    const unitPeople = allTenants.filter((t) =>
      !!t.unit_id && (t.unit_id === (unit as any).uuid || String(t.unit_id) === String(unit.id))
    );
    const tenants = unitPeople.filter((t: any) => t.person_type !== 'subtenant');
    const subTenants = unitPeople.filter((t: any) => t.person_type === 'subtenant');
    // Count guest pins for this unit from the guests array (if loaded).
    // Exclude revoked AND expired pins so the per-unit count matches what
    // a PM would expect ("how many guests can use the door right now?").
    const now = Date.now();
    const unitGuests = (guestPins || []).filter((g: any) => {
      if (!g.unit_id) return false;
      if (g.unit_id !== (unit as any).uuid && String(g.unit_id) !== String(unit.id)) return false;
      const status = String(g.status ?? '').toLowerCase();
      if (status === 'revoked' || status === 'expired') return false;
      if (g.expires_at && new Date(g.expires_at).getTime() < now) return false;
      return true;
    });
    return {
      ...unit,
      tenant_count: tenants.length,
      sub_tenant_count: subTenants.length,
      guest_count: unitGuests.length,
      status: unitPeople.length > 0 ? ('occupied' as const) : ('vacant' as const),
    };
  }).sort((a: any, b: any) => String(a.unit_number || a.name || '').localeCompare(
    String(b.unit_number || b.name || ''),
    undefined,
    { numeric: true, sensitivity: 'base' },
  ));

  const isSeamDevice = (d: any) => d.acs_provider === 'seam' && d.acs_device_id;
  const isBridgeDevice = (d: any) => d.acs_provider === 'bridge' && d.acs_device_id;
  const isBrivoDevice = (d: any) => d.acs_provider === 'brivo' && d.acs_device_id;
  const isSchlageDevice = (d: any) =>
    String(d.smart_lock_provider || d.acs_provider || '').toLowerCase() === 'schlage' &&
    !!(d.smart_lock_device_id || d.acs_device_id) &&
    !!(d.smart_lock_system_id || d.acs_system_id);
  const isPDKDevice = (d: any) =>
    (d.source !== 'acs' && !!d.integration_device_id) ||
    (d.pdk_device_id && d.pdk_system_id) ||
    (d.acs_provider === 'pdk' && d.acs_device_id) ||
    (property?.pdk_system_id && pdkDeviceIdMap[d.id]);
  const isAcsDoor = (d: any) => {
    if (d.source !== 'acs' && !d.acs_device_record_id) return false;
    const type = String(d.device_type || d.type || '').toLowerCase();
    const capabilities = Array.isArray(d.raw?.capabilities) ? d.raw.capabilities.map(String) : [];
    const linkedReader = d.raw?.unifi_resource === 'device' && !!d.raw?.location_id &&
      capabilities.some((value: string) => /(^|_)is_reader$/.test(value));
    return d.raw?.unifi_resource === 'door' || linkedReader || type === 'door' || type === 'lock';
  };
  // §28.518.15 — vendor-agnostic: canonical capabilities-based check. Any
  // device with 'unlock' in capabilities is controllable via opaque endpoint.
  const isControllableDevice = (d: any) => (Array.isArray(d.capabilities) && d.capabilities.includes('unlock')) || isAcsDoor(d) || isPDKDevice(d) || isSeamDevice(d) || isBridgeDevice(d) || isBrivoDevice(d) || isSchlageDevice(d);
  const isCameraDevice = (d: any) => {
    const t = String(d?.device_type ?? d?.type ?? '').toLowerCase();
    return ['camera', 'doorbell', 'ipcamera'].includes(t) || d?.acs_provider === 'turing';
  };
  const playTuringStream = async (d: any) => {
    if (d.acs_provider !== 'turing' || !d.acs_device_id) {
      setTuringPlay({ systemId: '', cameraId: 0, streamUrl: null, error: 'This camera does not have a streamable connection yet.' });
      return;
    }
    // Find the Turing system attached to this property.
    let systemId = '';
    try {
      const r = await fetch(`${process.env.REACT_APP_API_URL || 'https://api.intrlock.io'}/integrations/turing/systems`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('intrlock_token') ?? ''}` },
      });
      const systems = await r.json();
      const match = cameraSystemForProperty(systems || [], d.property_id);
      systemId = match?.id ?? '';
    } catch { /* fall through */ }
    if (!systemId) {
      setTuringPlay({ systemId: '', cameraId: 0, streamUrl: null, error: 'No Turing system attached to this property.' });
      return;
    }
    const cameraId = Number(d.acs_device_id);
    setTuringPlay({ systemId, cameraId, streamUrl: null, error: null });
    try {
      const r = await fetch(`${process.env.REACT_APP_API_URL || 'https://api.intrlock.io'}/integrations/turing/systems/${systemId}/stream-url`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('intrlock_token') ?? ''}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ camera_id: cameraId, resolution: 'sub' }),
      });
      const data = await r.json();
      if (data.error === 'license_required') {
        setTuringPlay({ systemId, cameraId, streamUrl: null, error: data.message || 'Camera does not have a Core AI license.' });
      } else if (data.play_url) {
        setTuringPlay({ systemId, cameraId, streamUrl: data.play_url, error: null });
      } else {
        setTuringPlay({ systemId, cameraId, streamUrl: null, error: 'No stream URL returned.' });
      }
    } catch (e: any) {
      setTuringPlay({ systemId, cameraId, streamUrl: null, error: String(e?.message ?? e).slice(0, 200) });
    }
  };

  const fetchDeviceStatuses = useCallback(async (devArr: any[], prop: any) => {
    const statuses: Record<number, { locked?: boolean; doorOpen?: boolean; online?: boolean }> = {};
    const idMap: Record<number, string> = {};

    // Use the new /devices/live endpoint for batch live status via connector
    if (prop?.id) {
      try {
        const token = localStorage.getItem('intrlock_token') || '';
        const res = await fetch(`${process.env.REACT_APP_API_URL || 'https://api.intrlock.io'}/devices/live?property_id=${encodeURIComponent(prop.id)}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(`Device status HTTP ${res.status}`);
        const liveDevices = await res.json();
        if (Array.isArray(liveDevices)) {
          for (const ld of liveDevices) {
            statuses[ld.id] = {
              locked: ld.locked,
              doorOpen: ld.door_open,
              online: ld.online ?? ld.connection_status === 'online',
            };
            if (ld.integration_device_id) idMap[ld.id] = ld.integration_device_id;
          }
          setPdkDeviceIdMap(idMap);
        }
      } catch (e) {
        console.error('Failed to fetch live device statuses:', e);
      }
    }

    // Fallback: Seam devices individually (legacy)
    for (const d of devArr.filter((d: any) => d.acs_provider === 'seam' && d.acs_device_id)) {
      try {
        const device = await getSeamDevice(d.acs_device_id);
        const props = device?.properties || {};
        statuses[d.id] = { locked: props.locked, doorOpen: props.door_open, online: props.online };
      } catch (e) {
        console.error(`Status fetch failed for device ${d.id}:`, e);
      }
    }
    setDeviceStatuses(statuses);
  }, []);

  // Auto-poll device statuses every 30s while devices tab is active
  useEffect(() => {
    if (tab !== 'devices' || devices.length === 0 || !property) return;
    const poll = () => fetchDeviceStatuses(devices, property);
    poll(); // immediate first poll
    const interval = setInterval(poll, 30000);
    return () => clearInterval(interval);
  }, [tab, devices.length, property, fetchDeviceStatuses]);

  const handleLockToggle = async (device: any) => {
    const devId = device.id;
    const status = deviceStatuses[devId];
    const currentlyLocked = status?.locked !== undefined ? status.locked : true;

    setLockLoading((prev) => ({ ...prev, [devId]: true }));
    try {
      // Send only Intrlock's opaque device UUID. Provider/system/device IDs
      // are resolved server-side after the caller's property scope is checked.
      const action = currentlyLocked ? 'unlock' : 'lock';
      const apiBase = process.env.REACT_APP_API_URL || 'https://api.intrlock.io';
      const tk = localStorage.getItem('intrlock_token') || '';
      const res = await fetch(`${apiBase}/devices/${encodeURIComponent(String(devId))}/actions/${action}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tk}` },
        body: JSON.stringify({ duration_ms: 5000 }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.success === false || (action === 'unlock' && data.door_unlocked === false)) {
        throw new Error(data?.error || data?.detail || `${action} failed (${res.status})`);
      }
      setDeviceStatuses((prev) => ({
        ...prev,
        [devId]: { ...prev[devId], locked: false },
      }));
      // Auto-reset to locked after 5 seconds (momentary unlock)
      setTimeout(() => {
        setDeviceStatuses((prev) => ({
          ...prev,
          [devId]: { ...prev[devId], locked: true },
        }));
      }, 5000);
      // §28.518.80 — P-OPT-2: fire-and-forget post-action poll scoped to this
      // property only. No await — button UI doesn't block on the 4.5s window.
      pollAfterAction(
        () => fetchDeviceStatuses(devices, property),
        String(devId),
        currentlyLocked ? 'unlock' : 'lock',
      );
    } catch (e: any) {
      console.error('Lock control failed:', e);
      alert(`Unlock failed: ${e.message || 'Unknown error'}`);
    } finally {
      setLockLoading((prev) => ({ ...prev, [devId]: false }));
    }
  };

  // Unit CRUD handlers
  const handleCreateUnit = async (data: any) => {
    try {
      await createUnit(data);
      refreshAllCounters();
      await fetchUnits();
      setUnitModal(false);
    } catch (error) {
      console.error('Failed to create unit:', error);
      alert('Failed to create unit. Please try again.');
    }
  };

  const handleUpdateUnit = async (data: any) => {
    if (!editingUnit) return;
    try {
      await updateUnit(editingUnit.id, data);
      refreshAllCounters();
      await fetchUnits();
      setEditingUnit(null);
    } catch (error) {
      console.error('Failed to update unit:', error);
      alert('Failed to update unit. Please try again.');
    }
  };

  const handleDeleteUnit = async () => {
    if (!deletingUnit) return;
    try {
      await deleteUnit(deletingUnit.id);
      refreshAllCounters();
      await fetchUnits();
      setDeletingUnit(null);
    } catch (error) {
      console.error('Failed to delete unit:', error);
      alert('Failed to delete unit. Please try again.');
    }
  };

  // Fetch guest pins and mobile access data on page load so counters are accurate
  useEffect(() => {
    fetchGuestPins();
    fetchMobileAccessData();
  }, [fetchGuestPins, fetchMobileAccessData]);

  // Fetch mobile access data when tab is selected
  useEffect(() => {
    if (tab === 'tenants') {
      fetchMobileAccessData();
    }
  }, [tab, fetchMobileAccessData]);

  // Fetch face count when access section is expanded and face access is enabled
  useEffect(() => {
    if (tab === 'settings' && expandedSections.access && iFaceAccessEnabled) {
      fetchFaceCount();
    }
  }, [tab, expandedSections.access, iFaceAccessEnabled, fetchFaceCount]);

  if (loading || !property) {
    return <div className="p-8 text-center text-gray-500">Loading property...</div>;
  }

  const isSubtenantRecord = (t: any) =>
    !!t.primary_tenant_id || t.is_subtenant === true || t.person_type === 'subtenant';
  const primaryTenants = allTenants.filter((t: any) => !isSubtenantRecord(t));
  const subTenants = allTenants.filter((t: any) => isSubtenantRecord(t));
  const isDeliveryPin = (g: any) => String(g.kind ?? '').toLowerCase() === 'delivery';
  const guestPasses = propertyPasses.filter((pass: any) => pass.pass_type !== 'delivery');
  const deliveryPasses = propertyPasses.filter((pass: any) => pass.pass_type === 'delivery');
  const renderPropertyPasses = (passes: any[], label: string) => (
    <div className="border-b border-gray-300 p-4">
      <p className="mb-3 text-xs font-bold uppercase tracking-wider text-gray-600">Secure {label} passes ({passes.length})</p>
      {propertyPassLoadError && <p role="alert" className="mb-3 border border-red-300 bg-red-50 p-2 text-sm text-red-700">{propertyPassLoadError}</p>}
      {passes.length === 0 ? <p className="text-sm text-gray-500">No secure {label} passes for this property.</p> :
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">{passes.map((pass: any) => {
          const passId = String(pass.id);
          const revealedPin = revealedPropertyPassPins[passId];
          return <div key={passId} className="border border-gray-300 bg-white p-3 text-sm">
            <div className="flex items-start justify-between gap-2 border-b border-gray-300 pb-2"><div><p className="font-semibold text-gray-900">{pass.visitor_name || 'Visitor'}</p><p className="text-xs text-gray-600">{pass.unit_number ? `Unit ${pass.unit_number}` : 'Property access'} · Granted by {pass.granted_by_name || 'authorized user'}</p></div><Badge color={pass.status === 'active' ? 'green' : pass.status === 'revoked' ? 'red' : 'gray'}>{pass.status || 'unknown'}</Badge></div>
            <p className="mt-2 text-xs text-gray-700">{formatDateTime(pass.valid_from)} – {formatDateTime(pass.valid_until)}</p>
            <p className="mt-1 text-xs text-gray-700">Door access: {Array.isArray(pass.access_scope) && pass.access_scope.includes('*') ? 'All eligible doors' : Array.isArray(pass.access_scope) && pass.access_scope.length ? `${pass.access_scope.length} selected doors` : 'No door access'}</p>
            <div className="mt-3 flex flex-wrap items-center gap-3 border-t border-gray-200 pt-2"><span className="font-mono font-semibold">{revealedPin || '••••••'}</span><button type="button" className="text-xs font-semibold text-emerald-700" onClick={async () => {
              if (revealedPin) { setRevealedPropertyPassPins((current) => { const next = { ...current }; delete next[passId]; return next; }); return; }
              try { const result = await apiRequestGet(`/visitors/passes/${encodeURIComponent(passId)}/pin`); if (result?.pin_code) { setRevealedPropertyPassPins((current) => ({ ...current, [passId]: String(result.pin_code) })); window.setTimeout(() => setRevealedPropertyPassPins((current) => { const next = { ...current }; delete next[passId]; return next; }), 10000); } } catch (error: any) { alert(`Code could not be loaded: ${error?.message || error}`); }
            }}>{revealedPin ? 'Hide code' : 'View code'}</button>
              {pass.status !== 'revoked' && <button type="button" className="text-xs font-semibold text-red-700" onClick={async () => { if (!window.confirm(`Revoke this ${label} pass?`)) return; try { await apiPost(`/visitors/pass/${encodeURIComponent(String(pass.public_id))}/revoke`, {}); await fetchGuestPins(); } catch (error: any) { alert(`Pass could not be revoked: ${error?.message || error}`); } }}>Revoke</button>}
            </div>
          </div>;
        })}</div>}
    </div>
  );
  const activeGuests = guestPins.filter((g: any) => {
    if (isDeliveryPin(g)) return false;
    if (String(g.status ?? '').toLowerCase() !== 'active') return false;
    if (g.expires_at && new Date(g.expires_at).getTime() < Date.now()) return false;
    return true;
  });
  const deliveryPins = guestPins.filter(isDeliveryPin);
  const activeDeliveries = deliveryPins.filter((g: any) => {
    if (String(g.status ?? '').toLowerCase() !== 'active') return false;
    if (g.expires_at && new Date(g.expires_at).getTime() < Date.now()) return false;
    return true;
  });

  const tabs: { key: Tab; label: string }[] = [
    { key: 'overview', label: 'Overview' },
    { key: 'units', label: `Units (${unitsWithTenantCount.length})` },
    { key: 'tenants', label: `Tenants (${allTenants.length})` },
    { key: 'intercoms', label: `Intercoms (${intercoms.length})` },
    { key: 'devices', label: `Devices (${devices.length})` },
    { key: 'users', label: `Web Users (${webUsersCount})` },
    { key: 'guests', label: `Guests (${activeGuests.length} Active)` },
    { key: 'deliveries', label: `Deliveries (${activeDeliveries.length} Active)` },
    { key: 'vendors', label: 'Vendors' },
    { key: 'contacts', label: `Contacts (${propertyContacts.length})` },
    // Billing Rates lives only on admin.intrlock.io now to avoid
    // bleeding super-admin pricing data into the dealer/customer/PM
    // surface. The tab is hidden on the regular app even for users
    // who are technically super_admins.
    ...(isSuperAdmin(role) && (typeof window !== 'undefined' && window.location.hostname.startsWith('admin.'))
        ? [{ key: 'billing_rates' as Tab, label: 'Billing Rates' }]
        : []),
    // 'settings' tab retired — every setting now lives in a per-intercom
    // modal on the Intercoms tab. The render block for tab === 'settings'
    // remains in the file (kept temporarily as a rollback safety net) but
    // is no longer reachable via the tab strip.
    { key: 'calls', label: 'Call History' },
    { key: 'device_history', label: 'Device History' },
  ];

  const onlineCount = devices.filter((d: any) => (d.connection_status || d.status) === 'online').length;

  return (
    <div className="shadow-detail-page property-detail-page">
      <button onClick={() => navigate('/properties')} className="flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4">
        <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back to Properties
      </button>

      <div className="property-identity-shell">
        <Card className="property-identity-card">
          {/* Header: title + badge + edit */}
          <div className="property-identity-main flex items-center gap-3 flex-wrap">
            <h1 className="text-xl font-bold text-gray-900">{property.name}</h1>
            <Badge status={property.status}>{property.status}</Badge>
            <div className="property-identity-actions">
              <Button size="sm" onClick={() => setUnitModal(true)}>Add Unit</Button>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setEditPropertyOpen(true)}
              >
                <span className="inline-flex items-center gap-1">
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                  Edit
                </span>
              </Button>
            </div>
          </div>
          {/* Address — single line, truncate on mobile */}
          <p className="text-gray-500 mt-1 text-sm">
            {property.address}
            {property.city ? `, ${property.city}` : ''}
            {property.state ? `, ${property.state}` : ''} {property.zip}
          </p>
          {property.telnyx_number && (
            <p className="text-brand-600 mt-1 text-sm font-medium flex items-center gap-1">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                />
              </svg>
              Intercom:{' '}
              <a href={`tel:${property.telnyx_number}`} className="underline hover:text-brand-700">
                {property.telnyx_number}
              </a>
            </p>
          )}
          {/* ─── Ownership chain: Customer + Dealer (super-admin sees both fully expanded) ─── */}
          {(customerData || dealerData) && (
            <div className="property-header-ownership mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
              {customerData && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Customer</p>
                    <button
                      onClick={() => navigate(`/customers/${customerData.uuid || customerData.id}`)}
                      className="text-xs text-brand-600 hover:text-brand-700 font-medium"
                    >View →</button>
                  </div>
                  <p className="text-sm font-semibold text-gray-900">{customerData.company_name || customerData.name || '—'}</p>
                  <div className="text-xs text-gray-600 mt-1 space-y-0.5">
                    {(customerData.contact_first_name || customerData.contact_last_name) && (
                      <p>{[customerData.contact_first_name, customerData.contact_last_name].filter(Boolean).join(' ')}</p>
                    )}
                    {(customerData.contact_email || customerData.email) && (
                      <p><a href={`mailto:${customerData.contact_email || customerData.email}`} className="hover:underline">{customerData.contact_email || customerData.email}</a></p>
                    )}
                    {customerData.phone && <p><a href={`tel:${customerData.phone}`} className="hover:underline">{customerData.phone}</a></p>}
                    {[customerData.address, customerData.city, customerData.state, customerData.zip].filter(Boolean).length > 0 && (
                      <p>{[customerData.address, customerData.city, customerData.state, customerData.zip].filter(Boolean).join(', ')}</p>
                    )}
                  </div>
                </div>
              )}
              {isSuperAdmin(role) && dealerData && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Dealer</p>
                    <button
                      onClick={() => navigate(`/integrators/${dealerData.uuid || dealerData.id}`)}
                      className="text-xs text-brand-600 hover:text-brand-700 font-medium"
                    >View →</button>
                  </div>
                  <p className="text-sm font-semibold text-gray-900">{dealerData.company_name || dealerData.name || '—'}</p>
                  <div className="text-xs text-gray-600 mt-1 space-y-0.5">
                    {dealerData.contact_name && <p>{dealerData.contact_name}</p>}
                    {(dealerData.contact_email || dealerData.email) && (
                      <p><a href={`mailto:${dealerData.contact_email || dealerData.email}`} className="hover:underline">{dealerData.contact_email || dealerData.email}</a></p>
                    )}
                    {dealerData.phone && <p><a href={`tel:${dealerData.phone}`} className="hover:underline">{dealerData.phone}</a></p>}
                    {[dealerData.address, dealerData.city, dealerData.state, dealerData.zip].filter(Boolean).length > 0 && (
                      <p>{[dealerData.address, dealerData.city, dealerData.state, dealerData.zip].filter(Boolean).join(', ')}</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
          {property.pdk_system_id && (
            <p className="text-xs text-brand-600 mt-1 font-mono truncate">PDK: {property.pdk_system_id}</p>
          )}
          {/* Action buttons — wrap on mobile */}
          {/* Reassign button removed — handled exclusively on admin.intrlock.io. */}
          {/* Bird's Eye Map — OpenStreetMap via react-leaflet.
             Google Maps embed rejected with X-Frame-Options: SAMEORIGIN.
             OSM is embeddable, needs no API key, tiles on CSP allowlist. */}
          <div className="property-header-map">
          {property.address && property.address.trim() && (
            <PropertyLeafletMap
              address={`${property.address || ''}${property.city ? ', ' + property.city : ''}${
                property.state ? ', ' + property.state : ''
              }${property.zip ? ' ' + property.zip : ''}`.trim()}
              lat={(property as any).latitude || (property as any).lat}
              lng={(property as any).longitude || (property as any).lng}
            />
          )}
          </div>
          {/* Stats — responsive grid with aggregated counters */}
          <div className="property-legacy-stats grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mt-4 pt-4 border-t border-gray-100">
            <div className="bg-gray-50 rounded p-3">
              <p className="text-xs text-gray-500 uppercase tracking-wider">Units</p>
              <p className="text-xl font-bold text-gray-900">{property.total_units || unitsWithTenantCount.length}</p>
            </div>
            <div className="bg-gray-50 rounded p-3">
              <p className="text-xs text-gray-500 uppercase tracking-wider">Devices</p>
              <p className="text-xl font-bold text-gray-900">{devices.length}</p>
            </div>
            <div className="bg-gray-50 rounded p-3">
              <p className="text-xs text-gray-500 uppercase tracking-wider">Tenants</p>
              <p className="text-xl font-bold text-gray-900">{property.total_tenants || primaryTenants.length}</p>
            </div>
            <div className="bg-gray-50 rounded p-3">
              <p className="text-xs text-gray-500 uppercase tracking-wider">Guests</p>
              <p className="text-xl font-bold text-purple-600">{property.total_guests || activeGuests.length}</p>
            </div>
            <div className="bg-gray-50 rounded p-3">
              <p className="text-xs text-gray-500 uppercase tracking-wider">Phones</p>
              <p className="text-xl font-bold text-green-600">{property.total_phones || ((property as any).telnyx_number ? 1 : 0)}</p>
            </div>
            <div className="bg-gray-50 rounded p-3">
              <p className="text-xs text-gray-500 uppercase tracking-wider">ACS Systems</p>
              <p className="text-xl font-bold text-gray-900">{property.total_systems || 0}</p>
            </div>
            <div className="bg-gray-50 rounded p-3">
              <p className="text-xs text-gray-500 uppercase tracking-wider">Intercoms</p>
              <p className="text-xl font-bold text-emerald-600">{intercoms.length}</p>
            </div>
            <div className="bg-gray-50 rounded p-3">
              <p className="text-xs text-gray-500 uppercase tracking-wider">Mobile Apps</p>
              <p className="text-xl font-bold text-orange-600">{mobileAccessData.filter((d: any) => d.status === 'Activated').length}</p>
            </div>
          </div>
        </Card>
      </div>

      <section className="detail-stats property-detail-stats" aria-label="Property totals">
        {[
          ['Units', property.total_units || unitsWithTenantCount.length],
          ['Devices', devices.length],
          ['Tenants', property.total_tenants || primaryTenants.length],
          ['Guests', property.total_guests || activeGuests.length],
          ['Phones', property.total_phones || ((property as any).telnyx_number ? 1 : 0)],
          ['Intercoms', intercoms.length],
        ].map(([label, value]) => (
          <div key={String(label)}><span>{label}</span><strong>{value}</strong></div>
        ))}
      </section>

      <nav className="property-tabs" aria-label="Property detail sections">
          {tabs.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={tab === t.key ? 'active' : ''}
            >
              {t.label}
            </button>
          ))}
      </nav>

      {tab === 'overview' && (
        <>
        <div className="property-overview-grid">
          <section className="property-operations-panel">
            <header><small>PROPERTY OPERATIONS</small><h2>Current systems</h2></header>
            <div className="property-operation-row"><span>Intercom</span><div><strong>Intercom</strong><small>{property.telnyx_number || 'No phone assigned'}</small></div><Badge status={property.telnyx_number ? 'active' : 'inactive'}>{property.telnyx_number ? 'active' : 'not configured'}</Badge></div>
            <div className="property-operation-row"><span>Devices</span><div><strong>Connected devices</strong><small>{devices.length} assigned to this property</small></div><Badge status={onlineCount > 0 ? 'active' : 'inactive'}>{onlineCount} online</Badge></div>
            <div className="property-operation-row"><span>Calls</span><div><strong>Call activity</strong><small>Property-scoped call history</small></div><button onClick={() => setTab('calls')}>Open →</button></div>
            {property.address && property.address.trim() && (
              <PropertyLeafletMap address={[property.address, property.city, property.state, property.zip].filter(Boolean).join(', ')} lat={(property as any).latitude || (property as any).lat} lng={(property as any).longitude || (property as any).lng} />
            )}
          </section>
          <aside className="property-record-panel">
            <small>PROPERTY RECORD</small><h2>Property details</h2>
            <dl>
              <dt>Customer</dt><dd>{customerData?.company_name || customerData?.name || '—'}</dd>
              {isSuperAdmin(role) && dealerData && <><dt>Dealer</dt><dd>{dealerData.company_name || dealerData.name || '—'}</dd></>}
              <dt>Address</dt><dd>{[property.address, property.city, property.state, property.zip].filter(Boolean).join(', ') || '—'}</dd>
              <dt>Phone</dt><dd>{property.telnyx_number || '—'}</dd>
              <dt>Status</dt><dd>{property.status || '—'}</dd>
              <dt>Units</dt><dd>{property.total_units || unitsWithTenantCount.length}</dd>
              <dt>Devices</dt><dd>{devices.length}</dd>
              <dt>Tenants</dt><dd>{property.total_tenants || primaryTenants.length}</dd>
              <dt>Guests</dt><dd>{property.total_guests || activeGuests.length}</dd>
            </dl>
            <div className="property-record-links">
              {customerData && <button onClick={() => navigate(`/customers/${customerData.uuid || customerData.id}`)}>Open customer →</button>}
              {isSuperAdmin(role) && dealerData && <button onClick={() => navigate(`/integrators/${dealerData.uuid || dealerData.id}`)}>Open dealer →</button>}
            </div>
          </aside>
        </div>
        <div className="property-overview-support">
          <EntityUsageCard entityType="property" entityId={(property as any)?.uuid || (property as any)?.id || id} />
          <AdminReassignCard
            entityType="property"
            entityId={(property as any)?.uuid || (property as any)?.id || id || ''}
            currentCustomerId={(property as any)?.customer_id}
            currentDealerId={(property as any)?.dealer_id}
            onSaved={() => window.location.reload()}
          />
        </div>
        </>
      )}

      {tab === 'units' && (
        <Card padding={false}>
          <div className="p-4 border-b border-gray-100 flex items-center justify-between">
            <h3 className="font-semibold">Units</h3>
            <div className="flex gap-2">
              <Button size="sm" variant="secondary" onClick={() => setBulkImportMode('units')}>Bulk Add</Button>
              <Button size="sm" onClick={() => setUnitModal(true)}>Add Unit</Button>
            </div>
          </div>
          {/* Mobile cards */}
          <div className="md:hidden p-3 space-y-2">
            {unitsWithTenantCount.length === 0 ? (
              <div className="p-6 text-center text-sm text-gray-400">No units for this property.</div>
            ) : unitsWithTenantCount.map((u: any) => (
              <div key={u.id} onClick={() => setSelectedUnit(u)} className="bg-white rounded-xl border border-gray-200 p-3 cursor-pointer">
                <div className="flex items-start justify-between mb-1">
                  <span className="font-semibold text-gray-900">Unit #{u.unit_number}</span>
                  {u.floor && <span className="text-xs text-gray-500">Floor {u.floor}</span>}
                </div>
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div className="bg-gray-50 rounded p-1.5 text-center"><p className="text-gray-400 text-[10px] uppercase">Tenants</p><p className="font-semibold">{u.tenant_count || 0}</p></div>
                  <div className="bg-gray-50 rounded p-1.5 text-center"><p className="text-gray-400 text-[10px] uppercase">Devices</p><p className="font-semibold">{u.device_count || 0}</p></div>
                  <div className="bg-gray-50 rounded p-1.5 text-center"><p className="text-gray-400 text-[10px] uppercase">Guests</p><p className="font-semibold">{u.guest_count || 0}</p></div>
                </div>
              </div>
            ))}
          </div>
          {/* Desktop table */}
          {unitsWithTenantCount.length === 0 ? (
            <div className="hidden md:block p-8 text-center text-gray-400">No units for this property.</div>
          ) : (
            <div className="hidden md:block">
            <Table
              columns={[
                {
                  key: 'unit_number',
                  header: 'Unit #',
                  render: (u: Unit) => <span className="font-medium">#{u.unit_number}</span>,
                },
                { key: 'floor', header: 'Floor', render: (u: Unit) => u.floor || '—' },
                {
                  key: 'tenant_count',
                  header: 'People',
                  render: (u: any) => <span className="font-medium">{(u.tenant_count ?? 0)}</span>,
                },
                {
                  key: 'guest_count',
                  header: 'Guests',
                  render: (u: any) => <span className={u.guest_count > 0 ? 'font-medium text-purple-600' : 'text-gray-400'}>{u.guest_count ?? 0}</span>,
                },
                {
                  key: 'status',
                  header: 'Status',
                  render: (u: Unit) => <Badge status={u.status}>{u.status}</Badge>,
                },
                {
                  key: 'actions',
                  header: 'Actions',
                  render: (u: Unit) => (
                    <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingUnit(u);
                        }}
                        className="text-brand-600 hover:text-brand-700 text-sm font-medium"
                      >
                        Edit
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeletingUnit(u);
                        }}
                        className="text-red-600 hover:text-red-700 text-sm font-medium"
                      >
                        Delete
                      </button>
                    </div>
                  ),
                },
              ]}
              data={unitsWithTenantCount}
              onRowClick={(u: any) => setSelectedUnit(u)}
            />
            </div>
          )}
        </Card>
      )}

      {tab === 'tenants' && (() => {
        const filteredTenants = primaryTenants;
        return (
        <Card padding={false}>
          <div className="p-4 border-b border-gray-100 flex items-center justify-between gap-3 flex-wrap">
            <h3 className="font-semibold text-gray-900">Tenants</h3>
            <div className="flex gap-2">
              <Button size="sm" variant="secondary" onClick={() => setBulkImportMode('tenants')}>Bulk Add</Button>
              <Button size="sm" onClick={() => setAddPersonModal(true)}>Add Tenant</Button>
            </div>
          </div>
          {/* Mobile cards */}
          <div className="md:hidden p-3 space-y-2">
            {filteredTenants.length === 0 ? (
              <div className="p-6 text-center text-sm text-gray-400">{allTenants.length === 0 ? 'No tenants for this property.' : 'No matches for this filter.'}</div>
            ) : filteredTenants.map((t: any) => {
              const unit = units.find((u: any) => u.id === t.unit_id);
              const a = activationsByTenant[String(t.uuid || t.id)] || activationsByTenant[String(t.id)];
              const isSub = t.person_type === 'subtenant' || !!t.primary_tenant_id || t.is_subtenant === true;
              return (
                <div key={t.id} onClick={() => navigate(`/tenants/${t.uuid || t.id}`)} className="bg-white rounded-xl border border-gray-200 p-3 cursor-pointer">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="flex items-start gap-2 min-w-0">
                      <input
                        type="checkbox"
                        checked={selectedTenants.has(t.id)}
                        onChange={() => toggleTenant(t.id)}
                        onClick={(e: any) => e.stopPropagation()}
                        className="mt-1 w-4 h-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                      />
                      <div className="min-w-0">
                      <p className="font-semibold text-gray-900 truncate">{t.first_name} {t.last_name}</p>
                      <p className="text-xs text-gray-500">{unit ? `Unit #${unit.unit_number}` : 'No unit'}</p>
                    </div>
                    </div>
                    <div className="flex flex-col items-end gap-1 flex-shrink-0">
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-green-100 text-green-700">Tenant</span>
                      {a && <span className="text-[10px] text-green-600 font-semibold">✓ Mobile App</span>}
                    </div>
                  </div>
                  <div className="text-xs text-gray-600 space-y-0.5 mt-1">
                    {t.email && <p className="truncate">✉ {t.email}</p>}
                    {t.phone && <p>☎ {t.phone}</p>}
                  </div>
                  {t.email && (
                    <div className="mt-2 flex gap-2" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => setEmailTenant(t)}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-semibold text-white bg-accent-500 hover:bg-accent-600"
                      >Email Tenant</button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Desktop table */}
          {filteredTenants.length === 0 ? (
            <div className="hidden md:block p-8 text-center text-gray-400">{allTenants.length === 0 ? 'No tenants for this property.' : 'No matches for this filter.'}</div>
          ) : (
            <div className="hidden md:block">
            <Table
              columns={[
                {
                  key: '_select',
                  header: (
                    <input
                      type="checkbox"
                      checked={filteredTenants.length > 0 && selectedTenants.size === filteredTenants.length}
                      onChange={() => toggleAllTenants(filteredTenants.map((t: any) => t.id))}
                      className="w-4 h-4 rounded border-gray-300"
                    />
                  ) as any,
                  render: (t: any) => (
                    <input
                      type="checkbox"
                      checked={selectedTenants.has(t.id)}
                      onChange={() => toggleTenant(t.id)}
                      onClick={(e: any) => e.stopPropagation()}
                      className="w-4 h-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                    />
                  ),
                  className: 'w-8',
                },
                {
                  key: 'name',
                  header: 'Name',
                  render: (t: any) => (
                    <div>
                      <span className="font-medium">{t.first_name} {t.last_name}</span>
                    </div>
                  ),
                },
                {
                  key: 'unit',
                  header: 'Unit',
                  render: (t: any) => {
                    const unit = units.find((u: any) => u.id === t.unit_id);
                    return unit ? `#${unit.unit_number}` : '—';
                  },
                },
                {
                  key: 'person_type',
                  header: 'Type',
                  render: (t: any) => {
                    const isSub = t.person_type === 'subtenant'
                      || !!t.primary_tenant_id
                      || t.is_subtenant === true;
                    return (
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        isSub ? 'bg-emerald-100 text-emerald-700' : 'bg-green-100 text-green-700'
                      }`}>
                        Tenant
                      </span>
                    );
                  },
                },
                { key: 'phone', header: 'Phone', render: (t: any) => t.phone || '—' },
                { key: 'email', header: 'Email', render: (t: any) => t.email || '—' },
                {
                  key: 'access_pin',
                  header: 'PIN',
                  render: (t: any) => (
                    <PinCell
                      tenantId={t.uuid || t.id}
                      savedPin={t.access_pin}
                      width={80}
                      onSaved={(v) => setAllTenants(prev => prev.map(tenant => tenant.id === t.id ? { ...tenant, access_pin: v } : tenant))}
                    />
                  ),
                },
                {
                  key: 'call_status',
                  header: 'Call',
                  render: (t: any) => {
                    const online = t.call_status === 'online';
                    return (
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                          online
                            ? 'bg-green-50 text-green-700 ring-1 ring-green-200'
                            : 'bg-gray-100 text-gray-600 ring-1 ring-gray-200'
                        }`}
                      >
                        <span className={`w-1.5 h-1.5 rounded-full ${online ? 'bg-green-500' : 'bg-gray-400'}`} />
                        {online ? 'Online' : 'Offline'}
                      </span>
                    );
                  },
                },
                {
                  key: 'face_id',
                  header: 'Face ID',
                  render: (t: any) => (
                    t.face_id_enrolled
                      ? <span className="inline-flex items-center gap-1 text-green-700 text-xs font-medium">
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                          Enrolled
                        </span>
                      : <span className="text-gray-400 text-xs">Not enrolled</span>
                  ),
                },
                {
                  key: 'tenant_portal',
                  header: 'Tenant Portal',
                  render: (t: any) => {
                    const a = activationsByTenant[String(t.uuid || t.id)] || activationsByTenant[String(t.id)];
                    const activated = Array.isArray(a)
                      ? a.some((x: any) => x.activated === true || !!x.activated_at)
                      : !!(a && (a.activated === true || a.activated_at));
                    return activated
                      ? <span className="inline-flex items-center gap-1 text-green-700 text-xs font-medium">
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                          Activated
                        </span>
                      : <span className="text-gray-400 text-xs">Not activated</span>;
                  },
                },
                {
                  key: 'mobile_app',
                  header: 'Mobile App',
                  render: (t: any) => {
                    const a = activationsByTenant[String(t.uuid || t.id)] || activationsByTenant[String(t.id)];
                    if (a) {
                      return (
                        <span className="inline-flex items-center gap-1 text-green-700 text-sm font-medium" title={`Activated ${a.activated_at || a.created_at || ''}`}>
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                          Activated
                        </span>
                      );
                    }
                    return <span className="text-gray-400 text-xs">—</span>;
                  },
                },
                {
                  key: 'os',
                  header: 'OS',
                  render: (t: any) => {
                    const a = activationsByTenant[String(t.uuid || t.id)] || activationsByTenant[String(t.id)];
                    if (!a) return <span className="text-gray-400 text-xs">—</span>;
                    const platform = (a.platform || '').toLowerCase();
                    const label = platform === 'ios' ? 'iPhone' : platform === 'android' ? 'Android' : (platform || a.device_model || '—');
                    return (
                      <span className="text-xs text-gray-700">
                        {label}
                        {a.device_model && a.device_model !== label && (
                          <span className="text-gray-400 ml-1">({a.device_model})</span>
                        )}
                      </span>
                    );
                  },
                },
                {
                  key: 'activation_code',
                  header: 'Activation Code',
                  render: (t: any) => {
                    const tid = String(t.uuid || t.id);
                    const a = latestActByTenant[tid] || latestActByTenant[String(t.id)];
                    const code = a?.activation_code || a?.video_activation_code || t.activation_code;
                    const expired = a?.video_code_expires_at && new Date(a.video_code_expires_at).getTime() < Date.now();
                    if (code) {
                      const consumed = a?.activated === true;
                      const regen = async (e: React.MouseEvent) => {
                        e.stopPropagation();
                        e.preventDefault();
                        try {
                          const API = process.env.REACT_APP_API_URL || 'https://api.intrlock.io';
                          const token = localStorage.getItem('intrlock_token') || localStorage.getItem('jwt') || localStorage.getItem('token') || '';
                          if (!token) { alert('You are not signed in.'); return; }
                          const params = new URLSearchParams({ tenantId: String(t.uuid || t.id), propertyId: String(id) });
                          if (t.unit_id) params.set('unitId', String(t.unit_id));
                          params.set('regenerate', '1');
                          const res = await fetch(`${API}/intercom/activation-code?${params}`, { headers: { Authorization: `Bearer ${token}` } });
                          const j = await res.json().catch(() => ({}));
                          if (!res.ok) { alert(`Failed (${res.status}): ${j.error || res.statusText}`); return; }
                          const newCode = j.activation_code || j.code;
                          if (!newCode) { alert('Server did not return a code'); return; }
                          setLatestActByTenant((m) => ({
                            ...m,
                            [String(t.uuid || t.id)]: {
                              ...(m[String(t.uuid || t.id)] || {}),
                              activation_code: newCode,
                              video_activation_code: newCode,
                              activated: false,
                              video_code_expires_at: j.expires_at,
                            },
                          }));
                        } catch (err: any) {
                          alert(`Failed: ${err.message || err}`);
                        }
                      };
                      return (
                        <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                          <span className={`font-mono text-sm px-2 py-0.5 rounded ${consumed ? 'bg-green-50 text-green-800' : expired ? 'bg-amber-50 text-amber-800' : 'bg-gray-100 text-gray-900'}`} title={
                            consumed ? 'Code already used to activate the app' : expired ? 'Expired — regenerate' : 'Active code'
                          }>{code}</span>
                          <button
                            onClick={() => { navigator.clipboard?.writeText(String(code)); }}
                            className="text-gray-400 hover:text-gray-700"
                            title="Copy code"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/></svg>
                          </button>
                          <button
                            onClick={regen}
                            className="text-emerald-600 hover:text-emerald-800 text-xs underline"
                            title="Issue a fresh code (use to re-sync a tenant whose device lost activation)"
                          >
                            Regenerate
                          </button>
                        </div>
                      );
                    }
                    return (
                      <button
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={async (e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          try {
                            const API = process.env.REACT_APP_API_URL || 'https://api.intrlock.io';
                            const token = localStorage.getItem('intrlock_token') || localStorage.getItem('jwt') || localStorage.getItem('token') || '';
                            if (!token) {
                              alert('You are not signed in. Please log in again to generate codes.');
                              return;
                            }
                            const unitId = t.unit_id || '';
                            const params = new URLSearchParams({ tenantId: String(t.uuid || t.id), propertyId: String(id) });
                            if (unitId) params.set('unitId', String(unitId));
                            const url = `${API}/intercom/activation-code?${params}`;
                            // Activation codes are credentials. Don't log the
                            // URL (contains tenantId) or response (contains
                            // the freshly-minted code) to the browser console.
                            const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
                            const text = await res.text();
                            let j: any = {};
                            try { j = text ? JSON.parse(text) : {}; } catch { j = { raw: text }; }
                            if (!res.ok) {
                              alert(`Failed (${res.status}): ${j.error || j.message || j.raw || res.statusText}`);
                              return;
                            }
                            const newCode = j.activation_code || j.code;
                            if (!newCode) {
                              alert('Server did not return an activation code. See console for details.');
                              return;
                            }
                            setLatestActByTenant((m) => ({
                              ...m,
                              [String(t.uuid || t.id)]: {
                                ...(m[String(t.uuid || t.id)] || {}),
                                activation_code: newCode,
                                video_activation_code: newCode,
                                activated: false,
                                video_code_expires_at: j.expires_at,
                              },
                            }));
                          } catch (err: any) {
                            console.error('[generate-code] failed', err);
                            alert(`Failed to generate code: ${err.message || err}`);
                          }
                        }}
                        className="text-xs text-brand-600 hover:text-brand-700 font-medium px-2 py-1 rounded hover:bg-brand-50"
                      >Generate</button>
                    );
                  },
                },
                {
                  key: 'status',
                  header: 'Status',
                  render: (t: any) => <Badge status={t.status}>{t.status}</Badge>,
                },
                {
                  key: 'actions',
                  header: 'Actions',
                  render: (t: any) => (
                    <div onClick={(e) => e.stopPropagation()}>
                      <ActionMenu items={[
                        { label: 'View', onClick: () => navigate(`/tenants/${t.uuid || t.id}`) },
                        { label: 'Edit', onClick: () => navigate(`/tenants/${t.uuid || t.id}?edit=true`) },
                        ...(t.email ? [{ label: 'Email', onClick: () => setEmailTenant(t) }] : []),
                        { label: 'Delete', variant: 'danger' as const, onClick: () => setTenantConfirmDelete(t) },
                      ]} />
                    </div>
                  ),
                },
              ]}
              data={filteredTenants}
              onRowClick={(t) => navigate(`/tenants/${(t as any).uuid || t.id}`)}
            />
            </div>
          )}
        </Card>
      );
      })()}

      {tab === 'tenants' && (
        <BulkActions
          selectedIds={selectedTenants}
          onClearSelection={clearTenantSelection}
          onDelete={async (ids) => {
            try {
              await Promise.all(ids.map((id) => deleteTenant(id as any)));
              setAllTenants((prev) => prev.filter((t: any) => !ids.includes(t.id)));
              refreshAllCounters();
              clearTenantSelection();
            } catch (e: any) {
              alert(`Failed to remove: ${e?.message || e}`);
            }
          }}
          entityName="people"
        />
      )}

      {tab === 'intercoms' && (
        <IntercomDoorConfig
          propertyId={id!}
          devices={devices}
          telnyxNumbers={property.telnyx_numbers_multiple ? property.telnyx_numbers_multiple.split(',') : property.telnyx_number ? [property.telnyx_number] : []}
          units={units as any[]}
          propertyDefaults={property as any}
        />
      )}

      {tab === 'devices' && (() => {
        // Build a stable type list from whatever the device rows expose.
        // `device_type` first, then legacy `type`, then "other" — keeps the
        // pill row meaningful even when the data is mixed.
        // Front-end display key. Collapses PDK's primaryReader /
        // auxiliaryReader into a single "door" bucket so the filter pills
        // match how PMs reason about device types. Backend value on each
        // row is untouched — only the UI grouping changes.
        const typeKey = (d: any) => {
          const raw = String((d?.device_type ?? d?.type ?? 'other')).toLowerCase().trim() || 'other';
          if (raw === 'primaryreader' || raw === 'auxiliaryreader') return 'door';
          return raw;
        };
        const typeCounts = devices.reduce<Record<string, number>>((acc, d: any) => {
          const k = typeKey(d);
          acc[k] = (acc[k] ?? 0) + 1;
          return acc;
        }, {});
        const typeKeys = Object.keys(typeCounts).sort();
        const visibleDevices = deviceTypeFilter === 'all'
          ? devices
          : devices.filter((d) => typeKey(d) === deviceTypeFilter);

        return (
        <Card padding={false}>
          <div className="p-4 border-b border-gray-100 flex items-center justify-between">
            <h3 className="font-semibold">Devices</h3>
            <Button size="sm" onClick={() => setDeviceModal(true)}>
              Add Device
            </Button>
          </div>
          {/* Type filter pills */}
          <div className="px-4 pt-3 pb-2 flex flex-wrap gap-2 border-b border-gray-100">
            {[{ key: 'all', label: 'All', count: devices.length }, ...typeKeys.map((k) => ({ key: k, label: k.replace(/_/g, ' '), count: typeCounts[k] }))].map((opt) => {
              const active = deviceTypeFilter === opt.key;
              return (
                <button
                  key={opt.key}
                  onClick={() => setDeviceTypeFilter(opt.key)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold capitalize transition-colors ${
                    active
                      ? 'bg-brand-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <span>{opt.label}</span>
                  <span className={`text-[10px] rounded-full px-1.5 py-0.5 ${active ? 'bg-white/25' : 'bg-white text-gray-600'}`}>{opt.count}</span>
                </button>
              );
            })}
          </div>
          {/* Mobile cards */}
          <div className="md:hidden p-3 space-y-2">
            {visibleDevices.length === 0 ? (
              <div className="p-6 text-center text-sm text-gray-400">No devices for this property.</div>
            ) : visibleDevices.map((d: any) => {
              // Cameras: if we can resolve any stream URL — server-side
              // `stream_url`, a warmed entry, or a known RTSP / Turing
              // adapter — treat the device as online even when the
              // upstream's connection_status flag hasn't refreshed yet.
              const isCamera = (d.type || d.device_type || '').toLowerCase().includes('camera');
              const hasStream = !!(d.stream_url || warmedStreams[d.id] || d.acs_provider === 'rtsp' || d.acs_provider === 'turing');
              const rawStatus = (d.connection_status || d.status || 'unknown').toLowerCase();
              const status = (isCamera && hasStream) ? 'online' : rawStatus;
              const statusColor = status === 'online' ? 'bg-green-100 text-green-700' : status === 'offline' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600';
              return (
                <div key={d.id} className="bg-white rounded-xl border border-gray-200 p-3">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="min-w-0">
                      <button type="button" className="font-semibold text-gray-900 truncate hover:underline" title="Edit device name" onClick={() => setEditingDevice(d)}>{d.name}</button>
                      <p className="text-xs text-gray-500">{intrlockTypeLabel(d)}</p>
                    </div>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${statusColor}`}>{status}</span>
                  </div>
                  <div className="text-xs text-gray-600">
                    {d.acs_device_id && <p className="font-mono truncate">{(d.acs_provider || 'PDK').toUpperCase()} · {d.acs_device_id}</p>}
                  </div>
                </div>
              );
            })}
          </div>
          {/* Desktop table */}
          {visibleDevices.length === 0 ? (
            <div className="hidden md:block p-8 text-center text-gray-400">No devices for this property.</div>
          ) : (
            <div className="hidden md:block">
            <Table
              columns={[
                {
                  key: 'name',
                  header: 'Device',
                  render: (d: any) => (
                    <div>
                      <button type="button" className="font-medium text-gray-900 hover:underline" title="Edit device name" onClick={(event) => { event.stopPropagation(); setEditingDevice(d); }}>{d.name}</button>
                      <p className="text-xs text-gray-500">
                        {d.model || ''}
                        {d.serial_number ? ` · ${d.serial_number}` : ''}
                      </p>
                    </div>
                  ),
                },
                {
                  key: 'type',
                  header: 'Type',
                  render: (d: any) => {
                    // §28.518.27 — canonical Intrlock type label + color
                    const label = intrlockTypeLabel(d);
                    const rawS = d.connection_status || d.status || 'unknown';
                    return <div className="device-type-visual">{deviceTypeIcon(d, rawS === 'offline')}<span>{label}</span></div>;
                  },
                },
                {
                  key: 'status',
                  header: 'Status',
                  render: (d: any) => {
                    const isCamera = (d.type || d.device_type || '').toLowerCase().includes('camera');
                    const hasStream = !!(d.stream_url || warmedStreams[d.id] || d.acs_provider === 'rtsp' || d.acs_provider === 'turing');
                    const rawS = d.connection_status || d.status || 'unknown';
                    const s = (isCamera && hasStream) ? 'online' : rawS;
                    const liveStatus = deviceStatuses[d.id];
                    const isLocked = liveStatus?.locked;
                    const doorOpen = liveStatus?.doorOpen;
                    return (
                      <div className="flex flex-col gap-1">
                        <div className="device-status-label">
                          {s === 'unknown' ? <span className="prototype-status inactive">Unknown</span> : <><DeviceStatusIcon online={s === 'online'} /><span>{s === 'online' ? 'Online' : 'Offline'}</span></>}
                        </div>
                        {/* Last-seen sits directly under the offline badge so
                            the PM sees when the device dropped at a glance. */}
                        {s === 'offline' && d.last_seen && (
                          <span className="text-xs text-gray-500">last seen {formatDateTime(d.last_seen)}</span>
                        )}
                        {isControllableDevice(d) && isLocked !== undefined && (
                          <span className="text-xs text-gray-500">{isLocked ? '🔒 Locked' : '🔓 Unlocked'}</span>
                        )}
                        {doorOpen !== undefined && (
                          <span className={`text-xs ${doorOpen ? 'text-orange-600' : 'text-green-600'}`}>
                            {doorOpen ? '🚪 Open' : '🚪 Closed'}
                          </span>
                        )}
                      </div>
                    );
                  },
                },
                {
                  key: 'device_control',
                  header: 'Device Control',
                  render: (d: any) => {
                    // Cameras get a View Stream button instead of the
                    // lock/unlock toggle. Other devices fall through to
                    // the existing controllable-device check.
                    if (isCameraDevice(d)) {
                      return (
                        <DeviceActionButton
                          onClick={(e: any) => { e.stopPropagation(); playTuringStream(d); }}
                        >
                          View Stream
                        </DeviceActionButton>
                      );
                    }
                    if (!isControllableDevice(d)) return <span className="text-gray-300">—</span>;
                    const status = deviceStatuses[d.id];
                    const isLocked = status?.locked !== undefined ? status.locked : true;
                    const isLoading = lockLoading[d.id];
                    return (
                      <DeviceActionButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleLockToggle(d);
                        }}
                        loading={isLoading}
                        loadingLabel={isLocked ? 'Unlocking…' : 'Locking…'}
                        variant={isLocked ? 'primary' : 'secondary'}
                      >
                        {isLocked ? 'Unlock' : 'Lock'}
                      </DeviceActionButton>
                    );
                  },
                },
                {
                  key: 'tenant_access',
                  header: 'Tenant Access',
                  render: (d: any) => (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setDeviceAccessFor(d); }}
                      className="px-2.5 py-1 text-xs font-medium rounded-md border border-gray-200 text-gray-700 hover:bg-gray-50"
                      title="Manage which tenants can see this device in the mobile app"
                    >
                      Manage…
                    </button>
                  ),
                },
                {
                  key: 'routing',
                  header: 'Door Routing',
                  render: (d: any) => {
                    const pref = d.preferred_integration as string | null | undefined;
                    const label = pref === 'parallel' ? 'Parallel' : pref === 'pdk' ? 'PDK only' : pref === 'intrlock_bridge_relay' ? 'Bridge only' : 'Default';
                    const color = pref === 'parallel' ? 'purple' : pref === 'pdk' ? 'blue' : pref === 'intrlock_bridge_relay' ? 'green' : 'gray';
                    const detail = pref === 'intrlock_bridge_relay' || pref === 'parallel'
                      ? `${d.bridge_serial || '—'} ch${d.bridge_channel ?? '—'} · ${d.pulse_ms ?? 3000}ms`
                      : null;
                    return (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setRoutingDevice(d);
                          setRoutingForm({
                            preferred_integration: d.preferred_integration || '',
                            bridge_serial: d.bridge_serial || '',
                            bridge_channel: d.bridge_channel != null ? String(d.bridge_channel) : '',
                            pulse_ms: d.pulse_ms != null ? String(d.pulse_ms) : '',
                          });
                        }}
                        className="text-left hover:opacity-80"
                        title="Edit door routing"
                      >
                        <Badge color={color as any}>{label}</Badge>
                        {detail && <div className="text-[10px] text-gray-500 font-mono mt-0.5">{detail}</div>}
                      </button>
                    );
                  },
                },
                {
                  key: 'delete',
                  header: '',
                  render: (d: any) => {
                    const isBridgeVirtual = typeof d.id === 'string' && String(d.id).startsWith('bridge-');
                    if (isBridgeVirtual) return null;
                    return (
                      <button
                        onClick={async (e) => {
                          e.stopPropagation();
                          if (!window.confirm(`Remove "${d.name || d.device_name}" from this property?`)) return;
                          try {
                            await deleteDevice(d.id);
                            refreshAllCounters();
                            const refreshed = await getDevices();
                            setDevices((Array.isArray(refreshed) ? refreshed : []).filter((dev: any) => String(dev.property_id) === String(id)));
                          } catch (err) {
                            console.error('Failed to delete device:', err);
                            alert('Failed to remove device');
                          }
                        }}
                        className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                        title="Remove device"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    );
                  },
                },
              ]}
              data={visibleDevices}
            />
            </div>
          )}
        </Card>
        );
      })()}

      {tab === 'users' && (
        <div className="space-y-6">
          {/* Web Users — dealers, customers, property managers only */}
          <Card>
            <UsersList filterType="property" filterId={id!} propertyDealerId={(property as any)?.dealer_id ?? dealerData?.id} propertyCustomerId={property?.customer_id ?? customerData?.id} title="Property Managers & Administrators" />
          </Card>
        </div>
      )}

      {false && (/* OLD People section removed — now in Tenants tab */
        <div className="space-y-6">
          <Card padding={false}>
            <div className="p-4 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h3 className="font-semibold">People ({allTenants.length})</h3>
              </div>
              <Button size="sm" onClick={() => setAddPersonModal(true)}>Add Person</Button>
            </div>
            {allTenants.length === 0 ? (
              <div className="p-8 text-center text-gray-400">No people for this property.</div>
            ) : (
              <Table
                columns={[
                  {
                    key: 'name',
                    header: 'Name',
                    render: (t: any) => (
                      <span className="font-medium text-gray-900">{t.first_name} {t.last_name}</span>
                    ),
                  },
                  {
                    key: 'role',
                    header: 'Role',
                    render: (t: any) => {
                      const isSub = !!t.primary_tenant_id || t.is_subtenant === true || t.person_type === 'subtenant';
                      if (!isSub) return <Badge color="green">Tenant</Badge>;
                      const parent = allTenants.find((p: any) =>
                        p.id === t.primary_tenant_id ||
                        p.uuid === t.primary_tenant_id
                      );
                      const parentName = parent
                        ? `${parent.first_name || ''} ${parent.last_name || ''}`.trim()
                        : '';
                      return (
                        <div>
                          <Badge color="green">Tenant</Badge>
                          {parentName && (
                            <p className="text-xs text-gray-400 mt-0.5">Under {parentName}</p>
                          )}
                        </div>
                      );
                    },
                  },
                  {
                    key: 'contact',
                    header: 'Contact',
                    render: (t: any) => (
                      <div className="text-sm text-gray-600">
                        {t.email && <p>{t.email}</p>}
                        {t.phone && <p>{t.phone}</p>}
                      </div>
                    ),
                  },
                  {
                    key: 'unit',
                    header: 'Unit',
                    render: (t: any) => t.unit_number ? <span className="font-medium">#{t.unit_number}</span> : <span className="text-gray-400">—</span>,
                  },
                  {
                    key: 'permissions',
                    header: 'Permissions',
                    render: (t: any) => (
                      <div className="flex flex-col gap-1 text-xs" onClick={(e) => e.stopPropagation()}>
                        <label className="flex items-center gap-1 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={!!t.can_create_guests}
                            className="rounded border-gray-300"
                            onChange={async (e) => {
                              setAllTenants(prev => prev.map(tenant => tenant.id === t.id ? {...tenant, can_create_guests: e.target.checked} : tenant));
                              try { await apiUpdate('tenants', t.uuid || t.id, { can_create_guests: e.target.checked }); refreshAllCounters(); }
                              catch (err) { console.error('Failed to update permission:', err); }
                            }}
                          />
                          <span className="text-gray-600">Create Guests</span>
                        </label>
                        <label className="flex items-center gap-1 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={t.face_access_enabled !== false}
                            className="rounded border-gray-300"
                            onChange={async (e) => {
                              setAllTenants(prev => prev.map(tenant => tenant.id === t.id ? {...tenant, face_access_enabled: e.target.checked} : tenant));
                              try { await apiUpdate('tenants', t.uuid || t.id, { face_access_enabled: e.target.checked }); refreshAllCounters(); }
                              catch (err) { console.error('Failed to update face_access_enabled:', err); }
                            }}
                          />
                          <span className="text-gray-600">Face Access</span>
                        </label>
                        <div className="flex items-center gap-1">
                          <span className="text-gray-500">PIN:</span>
                          <PinCell
                            tenantId={t.uuid || t.id}
                            savedPin={t.access_pin}
                            width={96}
                            onSaved={(v) => setAllTenants(prev => prev.map(tenant => tenant.id === t.id ? { ...tenant, access_pin: v } : tenant))}
                            onAfterSave={refreshAllCounters}
                          />
                        </div>
                      </div>
                    ),
                  },
                  {
                    key: 'limits',
                    header: 'Limits',
                    render: (t: any) => (
                      <div className="flex flex-col gap-1 text-xs" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-1">
                          <span className="text-gray-500 w-20">Max Guests:</span>
                          <input
                            type="number"
                            min={0}
                            value={t.max_guests ?? ''}
                            placeholder="∞"
                            className="w-14 px-1 py-0.5 border border-gray-300 rounded text-xs text-center"
                            onChange={(e) => {
                              setAllTenants(prev => prev.map(tenant => tenant.id === t.id ? {...tenant, max_guests: e.target.value === '' ? null : Number(e.target.value)} : tenant));
                            }}
                            onBlur={async (e) => {
                              const val = e.target.value === '' ? null : Number(e.target.value);
                              try { await apiUpdate('tenants', t.uuid || t.id, { max_guests: val }); refreshAllCounters(); }
                              catch (err) { console.error('Failed to update max_guests:', err); }
                            }}
                          />
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="text-gray-500 w-20">Max Days:</span>
                          <input
                            type="number"
                            min={0}
                            value={t.max_guest_duration_days ?? ''}
                            placeholder="∞"
                            className="w-14 px-1 py-0.5 border border-gray-300 rounded text-xs text-center"
                            onChange={(e) => {
                              setAllTenants(prev => prev.map(tenant => tenant.id === t.id ? {...tenant, max_guest_duration_days: e.target.value === '' ? null : Number(e.target.value)} : tenant));
                            }}
                            onBlur={async (e) => {
                              const val = e.target.value === '' ? null : Number(e.target.value);
                              try { await apiUpdate('tenants', t.uuid || t.id, { max_guest_duration_days: val }); refreshAllCounters(); }
                              catch (err) { console.error('Failed to update max_guest_duration_days:', err); }
                            }}
                          />
                        </div>
                      </div>
                    ),
                  },
                ]}
                data={allTenants}
              />
            )}
          </Card>
          {/* Web/App Users */}
          <Card>
            <UsersList filterType="property" filterId={id!} propertyDealerId={(property as any)?.dealer_id ?? dealerData?.id} propertyCustomerId={property?.customer_id ?? customerData?.id} title="Web/App Users" />
          </Card>
        </div>
      )}

      {false && (/* Old Mobile Access tab — merged into Tenants */
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Tenants</p>
                  <p className="text-2xl font-bold text-gray-900">{mobileAccessData.length}</p>
                </div>
                <div className="p-3 bg-emerald-100 rounded-lg">
                  <svg className="w-6 h-6 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
              </div>
            </Card>
            <Card>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Activated</p>
                  <p className="text-2xl font-bold text-green-600">{mobileAccessData.filter(d => d.status === 'Activated').length}</p>
                </div>
                <div className="p-3 bg-green-100 rounded-lg">
                  <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
            </Card>
            <Card>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Pending</p>
                  <p className="text-2xl font-bold text-yellow-600">{mobileAccessData.filter(d => d.status === 'Pending').length}</p>
                </div>
                <div className="p-3 bg-yellow-100 rounded-lg">
                  <svg className="w-6 h-6 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
            </Card>
            <Card>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Not Invited</p>
                  <p className="text-2xl font-bold text-gray-600">{mobileAccessData.filter(d => d.status === 'Not Invited').length}</p>
                </div>
                <div className="p-3 bg-gray-100 rounded-lg">
                  <svg className="w-6 h-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                  </svg>
                </div>
              </div>
            </Card>
          </div>

          {/* Adoption Rate */}
          <Card>
            <h3 className="font-semibold text-gray-900 mb-3">Adoption Rate</h3>
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <div className="flex justify-between mb-2">
                  <span className="text-sm text-gray-600">
                    {mobileAccessData.filter(d => d.status === 'Activated').length} of {mobileAccessData.length} tenants activated
                  </span>
                  <span className="text-sm font-semibold text-brand-600">
                    {mobileAccessData.length > 0 
                      ? Math.round((mobileAccessData.filter(d => d.status === 'Activated').length / mobileAccessData.length) * 100)
                      : 0}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div 
                    className="bg-gradient-to-r from-green-500 to-green-600 h-3 rounded-full transition-all duration-500"
                    style={{ 
                      width: `${mobileAccessData.length > 0 
                        ? (mobileAccessData.filter(d => d.status === 'Activated').length / mobileAccessData.length) * 100
                        : 0}%` 
                    }}
                  />
                </div>
              </div>
            </div>
          </Card>

          {/* Bulk Actions Toolbar */}
          <Card>
            <div className="flex flex-wrap gap-3">
              <Button 
                onClick={async () => {
                  if (!window.confirm('Send invitations to all tenants who have not been invited?')) return;
                  setActionLoading({ bulk: true });
                  try {
                    const response = await fetch(`${apiOrigin()}/intercom/bulk-invite`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ property_id: id }),
                    });
                    if (!response.ok) throw new Error('Bulk invite failed');
                    alert('Bulk invitations sent successfully!');
                    await fetchMobileAccessData();
                  } catch (e) {
                    console.error(e);
                    alert('Failed to send bulk invitations');
                  } finally {
                    setActionLoading({});
                  }
                }}
                disabled={actionLoading.bulk}
                className="bg-brand-600 hover:bg-brand-700"
              >
                <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                {actionLoading.bulk ? 'Sending...' : 'Invite All'}
              </Button>
              <Button 
                variant="secondary"
                onClick={() => {
                  const csvRows = [
                    ['Tenant Name', 'Unit Number', 'Status', 'Platform', 'Activated Date'].join(','),
                    ...mobileAccessData.map(d => [
                      `"${d.tenant_name}"`,
                      d.unit_number,
                      d.status,
                      d.platform,
                      d.activated_date !== '—' ? new Date(d.activated_date).toLocaleDateString() : '—'
                    ].join(','))
                  ];
                  const csv = csvRows.join('\n');
                  const blob = new Blob([csv], { type: 'text/csv' });
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `${property?.name || 'property'}-mobile-access-${new Date().toISOString().split('T')[0]}.csv`;
                  a.click();
                  window.URL.revokeObjectURL(url);
                }}
              >
                <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Export CSV
              </Button>
            </div>
          </Card>

          {/* Tenant Table */}
          <Card padding={false}>
            <div className="p-4 border-b border-gray-100">
              <h3 className="font-semibold">Tenant Mobile Access Status</h3>
            </div>
            {loadingMobile ? (
              <div className="p-8 text-center text-gray-400">Loading mobile access data...</div>
            ) : mobileAccessData.length === 0 ? (
              <div className="p-8 text-center text-gray-400">No tenants found for this property.</div>
            ) : (
              <Table
                columns={[
                  {
                    key: 'tenant_name',
                    header: 'Tenant Name',
                    render: (d: any) => <span className="font-medium text-gray-900">{d.tenant_name || 'Unknown'}</span>,
                  },
                  {
                    key: 'unit_number',
                    header: 'Unit',
                    render: (d: any) => <span className="font-medium">#{d.unit_number}</span>,
                  },
                  {
                    key: 'status',
                    header: 'Status',
                    render: (d: any) => {
                      const icons = {
                        'Activated': '✅',
                        'Pending': '⏳',
                        'Not Invited': '❌',
                        'Revoked': '🚫'
                      };
                      return (
                        <div className="flex items-center gap-2">
                          <span>{icons[d.status as keyof typeof icons] || '—'}</span>
                          <Badge color={d.statusColor}>{d.status}</Badge>
                        </div>
                      );
                    },
                  },
                  {
                    key: 'platform',
                    header: 'Platform',
                    render: (d: any) => d.status === 'Activated' && d.platform !== '—' ? (
                      <span className="text-sm text-gray-600">{d.platform}</span>
                    ) : (
                      <span className="text-gray-400">—</span>
                    ),
                  },
                  {
                    key: 'activated_date',
                    header: 'Activated Date',
                    render: (d: any) => d.status === 'Activated' && d.activated_date !== '—' ? (
                      <span className="text-sm text-gray-600">{formatDateTime(d.activated_date)}</span>
                    ) : (
                      <span className="text-gray-400">—</span>
                    ),
                  },
                  {
                    key: 'activation_code',
                    header: 'Activation Code',
                    render: (d: any) => {
                      const code = d.activation_code || d.video_activation_code;
                      if (!code) return <span className="text-gray-400">—</span>;
                      return (
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-sm font-bold text-brand-700 bg-brand-50 px-2 py-1 rounded">{code}</span>
                          <button
                            onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(code); }}
                            className="text-gray-400 hover:text-gray-600"
                            title="Copy code"
                          >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                          </button>
                        </div>
                      );
                    },
                  },
                  {
                    key: 'access_pin',
                    header: 'Access PIN',
                    render: (d: any) => {
                      const [editing, setEditing] = React.useState(false);
                      const [pinVal, setPinVal] = React.useState(d.access_pin || '');
                      const [saving, setSaving] = React.useState(false);

                      const savePin = async () => {
                        setSaving(true);
                        try {
                          await apiUpdate('tenants', d.tenant_uuid || d.tenant_id, { access_pin: pinVal });
                          refreshAllCounters();
                          setEditing(false);
                          d.access_pin = pinVal;
                        } catch (e) {
                          console.error('Failed to save PIN:', e);
                        } finally {
                          setSaving(false);
                        }
                      };

                      const generatePin = () => {
                        const newPin = String(Math.floor(1000 + Math.random() * 9000));
                        setPinVal(newPin);
                      };

                      if (editing) {
                        return (
                          <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="text"
                              value={pinVal}
                              onChange={(e) => setPinVal(e.target.value.replace(/\D/g, '').slice(0, 6))}
                              className="w-20 px-2 py-1 text-sm border border-gray-300 rounded font-mono text-center"
                              placeholder="1234"
                              maxLength={6}
                            />
                            <button onClick={generatePin} className="text-gray-400 hover:text-brand-600" title="Generate random PIN">
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                              </svg>
                            </button>
                            <button onClick={savePin} disabled={saving || !pinVal} className="text-green-600 hover:text-green-700 disabled:opacity-50" title="Save">
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            </button>
                            <button onClick={() => { setEditing(false); setPinVal(d.access_pin || ''); }} className="text-gray-400 hover:text-gray-600" title="Cancel">
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                        );
                      }

                      return (
                        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                          {d.access_pin ? (
                            <span className="font-mono text-sm font-bold text-gray-700 bg-gray-100 px-2 py-1 rounded">{d.access_pin}</span>
                          ) : (
                            <span className="text-gray-400 text-sm">No PIN</span>
                          )}
                          <button onClick={() => setEditing(true)} className="text-gray-400 hover:text-brand-600" title="Set PIN">
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                            </svg>
                          </button>
                        </div>
                      );
                    },
                  },
                  {
                    key: 'device',
                    header: 'Device',
                    render: (d: any) => {
                      if (!d.has_activation) return <span className="text-gray-400">—</span>;
                      return (
                        <div className="text-xs text-gray-600">
                          <p>{d.device_model !== '—' ? d.device_model : '—'}</p>
                          {d.device_os !== '—' && <p className="text-gray-400">{d.device_os}</p>}
                        </div>
                      );
                    },
                  },
                  {
                    key: 'fingerprint',
                    header: 'Fingerprint',
                    render: (d: any) => d.device_fingerprint
                      ? <span className="font-mono text-xs text-gray-500">{d.device_fingerprint.slice(0, 8)}</span>
                      : <span className="text-gray-400">—</span>,
                  },
                  {
                    key: 'actions',
                    header: 'Actions',
                    render: (d: any) => {
                      const isLoading = actionLoading[d.tenant_id];

                      const handleAction = async (action: string, endpoint: string) => {
                        setActionLoading({ ...actionLoading, [d.tenant_id]: true });
                        try {
                          const response = await fetch(`${apiOrigin()}${endpoint}`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                              tenant_id: d.tenant_id,
                              property_id: id,
                              method: 'email'
                            }),
                          });
                          if (!response.ok) throw new Error(`${action} failed`);
                          const result = await response.json().catch(() => ({}));
                          if (result.activation_code) {
                            alert(`${action} successful! Code: ${result.activation_code}`);
                          } else {
                            alert(`${action} successful!`);
                          }
                          await fetchMobileAccessData();
                        } catch (e) {
                          console.error(e);
                          alert(`Failed to ${action.toLowerCase()}`);
                        } finally {
                          setActionLoading({ ...actionLoading, [d.tenant_id]: false });
                        }
                      };

                      const handleGenerateCode = async () => {
                        setActionLoading({ ...actionLoading, [d.tenant_id]: true });
                        try {
                          const response = await fetch(`${apiOrigin()}/intercom/generate-code`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ tenant_id: d.tenant_id, property_id: id }),
                          });
                          if (!response.ok) throw new Error('Generate code failed');
                          const result = await response.json();
                          alert(`Activation code: ${result.activation_code}\n\nShare this 6-digit code with the tenant to activate their mobile app.`);
                          await fetchMobileAccessData();
                        } catch (e) {
                          console.error(e);
                          alert('Failed to generate activation code');
                        } finally {
                          setActionLoading({ ...actionLoading, [d.tenant_id]: false });
                        }
                      };

                      return (
                        <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                          {(d.status === 'Not Invited' || d.status === 'Pending') && (
                            <button
                              onClick={handleGenerateCode}
                              disabled={isLoading}
                              className="text-brand-600 hover:text-brand-700 text-sm font-medium disabled:opacity-50"
                            >
                              {isLoading ? 'Generating...' : 'Generate Code'}
                            </button>
                          )}
                          {d.status === 'Not Invited' && (
                            <button
                              onClick={() => handleAction('Invite', '/intercom/invite')}
                              disabled={isLoading}
                              className="text-emerald-600 hover:text-emerald-700 text-sm font-medium disabled:opacity-50"
                            >
                              {isLoading ? 'Sending...' : 'Email Invite'}
                            </button>
                          )}
                          {d.status === 'Pending' && (
                            <button
                              onClick={() => handleAction('Resend', '/intercom/invite')}
                              disabled={isLoading}
                              className="text-emerald-600 hover:text-emerald-700 text-sm font-medium disabled:opacity-50"
                            >
                              {isLoading ? 'Sending...' : 'Resend'}
                            </button>
                          )}
                          {d.status === 'Activated' && (
                            <button
                              onClick={() => {
                                if (!window.confirm('Are you sure you want to revoke access for this tenant?')) return;
                                handleAction('Revoke', '/intercom/deactivate');
                              }}
                              disabled={isLoading}
                              className="text-red-600 hover:text-red-700 text-sm font-medium disabled:opacity-50"
                            >
                              {isLoading ? 'Revoking...' : 'Revoke'}
                            </button>
                          )}
                          {d.status === 'Revoked' && (
                            <button
                              onClick={() => handleAction('Reactivate', '/intercom/reactivate')}
                              disabled={isLoading}
                              className="text-green-600 hover:text-green-700 text-sm font-medium disabled:opacity-50"
                            >
                              {isLoading ? 'Reactivating...' : 'Reactivate'}
                            </button>
                          )}
                          {d.status === 'Activated' && (
                            <button
                              onClick={async () => {
                                setActionLoading({ ...actionLoading, [d.tenant_id]: true });
                                try {
                                  const res = await fetch(`${apiOrigin()}/video/call`, {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({ tenantId: d.tenant_id, propertyId: id, visitorName: 'Test Push' }),
                                  });
                                  const result = await res.json();
                                  if (result.push_sent) {
                                    alert('Test push sent! Check if the phone rings.');
                                  } else {
                                    alert(`Push failed: ${result.error || 'Unknown error'}\n\nThe tenant may need to reopen the app to refresh their VoIP token.`);
                                  }
                                } catch (e: any) {
                                  alert(`Test push failed: ${e.message}`);
                                } finally {
                                  setActionLoading({ ...actionLoading, [d.tenant_id]: false });
                                }
                              }}
                              disabled={isLoading}
                              className="text-purple-600 hover:text-purple-700 text-sm font-medium disabled:opacity-50"
                            >
                              {isLoading ? 'Sending...' : 'Test Ring'}
                            </button>
                          )}
                          {d.activation_id && (
                            <button
                              onClick={async (e) => {
                                e.stopPropagation();
                                const newVal = !d.suspended_by_device;
                                if (!window.confirm(newVal ? 'Suspend this device? The tenant will not be able to use the app.' : 'Unsuspend this device?')) return;
                                setActionLoading({ ...actionLoading, [d.tenant_id]: true });
                                try {
                                  await apiUpdate('app_activations', d.activation_uuid || d.activation_id, { suspended_by_device: newVal });
                                  refreshAllCounters();
                                  d.suspended_by_device = newVal;
                                  await fetchMobileAccessData();
                                } catch (err) {
                                  console.error('Failed to update device suspension:', err);
                                  alert('Failed to update device suspension.');
                                } finally {
                                  setActionLoading({ ...actionLoading, [d.tenant_id]: false });
                                }
                              }}
                              disabled={isLoading}
                              className={`text-sm font-medium disabled:opacity-50 ${d.suspended_by_device ? 'text-green-600 hover:text-green-700' : 'text-orange-600 hover:text-orange-700'}`}
                            >
                              {isLoading ? 'Updating...' : d.suspended_by_device ? 'Unsuspend' : 'Suspend Device'}
                            </button>
                          )}
                        </div>
                      );
                    },
                  },
                ]}
                data={mobileAccessData}
              />
            )}
          </Card>
        </div>
      )}

      {tab === 'vendors' && (
        <PropertyVendorsTab propertyId={String((property as any)?.uuid || id || '')} />
      )}

      {tab === 'guests' && (
        <Card padding={false}>
          <div className="p-4 border-b border-gray-100 flex items-center justify-between gap-3">
            <div>
              <h3 className="font-semibold">Guests</h3>
              <p className="text-xs text-gray-400 mt-0.5">{activeGuests.length + guestPasses.filter((pass: any) => pass.status === 'active').length} active · {guestPins.length - deliveryPins.length + guestPasses.length} total</p>
            </div>
            <Button onClick={() => { setCreateGuestResult(null); setCreateGuestErr(null); setShowCreateGuestModal(true); }}>
              <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4"/></svg>
              Create Visitor Pass
            </Button>
          </div>
          {renderPropertyPasses(guestPasses, 'visitor')}
          {/* Mobile cards */}
          <div className="md:hidden p-3 space-y-2">
            {loadingGuests ? (
              <div className="p-6 text-center text-sm text-gray-400">Loading guests...</div>
            ) : guestPins.length === 0 ? (
              <div className="p-6 text-center text-sm text-gray-400">No guest PINs for this property.</div>
            ) : guestPins.map((g: any) => {
              const tenant = allTenants.find((t: any) => t.id === g.granted_by_tenant_id);
              const status = (g.status || 'active').toLowerCase();
              const statusColor = status === 'active' ? 'bg-green-100 text-green-700' : status === 'expired' ? 'bg-gray-100 text-gray-500' : 'bg-amber-100 text-amber-700';
              return (
                <div key={g.id} className="bg-white rounded-xl border border-gray-200 p-3">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="min-w-0">
                      <p className="font-semibold text-gray-900 truncate">{[g.guest_first_name, g.guest_last_name].filter(Boolean).join(' ') || g.guest_name || '—'}</p>
                      {g.pin && <p className="text-xs font-mono text-gray-500">PIN {g.pin}</p>}
                    </div>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${statusColor}`}>{status}</span>
                  </div>
                  {tenant && <p className="text-xs text-gray-600">Granted by: {tenant.first_name} {tenant.last_name}</p>}
                </div>
              );
            })}
          </div>
          {/* Desktop table */}
          {loadingGuests ? (
            <div className="hidden md:block p-8 text-center text-gray-400">Loading guests...</div>
          ) : guestPins.length === 0 ? (
            <div className="hidden md:block p-8 text-center text-gray-400">No guest PINs for this property.</div>
          ) : (
            <div className="hidden md:block">
            <Table
              columns={[
                {
                  key: 'guest_name',
                  header: 'Guest Name',
                  render: (g: any) => (
                    <span className="font-medium text-gray-900">
                      {[g.guest_first_name, g.guest_last_name].filter(Boolean).join(' ') || g.guest_name || '—'}
                    </span>
                  ),
                },
                {
                  key: 'granted_by',
                  header: 'Granted By',
                  render: (g: any) => {
                    if (g.granted_by_pm || !g.granted_by_tenant_id) {
                      return <span className="text-sm text-gray-700 italic">Property Manager</span>;
                    }
                    const tenant = allTenants.find((t: any) => t.id === g.granted_by_tenant_id);
                    if (!tenant) return <span className="text-gray-400">—</span>;
                    return (
                      <div>
                        <p className="text-sm text-gray-900">{tenant.first_name} {tenant.last_name}</p>
                      </div>
                    );
                  },
                },
                {
                  key: 'tenant_unit',
                  header: 'Tenant Unit',
                  render: (g: any) => {
                    const tenant = allTenants.find((t: any) => t.id === g.granted_by_tenant_id);
                    if (!tenant) return <span className="text-gray-400">—</span>;
                    const unit = units.find((u: any) => u.id === tenant.unit_id);
                    return unit ? <span className="font-medium">#{unit.unit_number}</span> : <span className="text-gray-400">—</span>;
                  },
                },
                {
                  key: 'access_start',
                  header: 'Access Start',
                  render: (g: any) => g.created_at ? <span className="text-sm text-gray-600">{formatDateTime(g.created_at)}</span> : <span className="text-gray-400">—</span>,
                },
                {
                  key: 'access_end',
                  header: 'Access End',
                  render: (g: any) => g.expires_at
                    ? <span className="text-sm text-gray-600">{formatDateTime(g.expires_at)}</span>
                    : <span className="text-xs text-gray-400 italic">No Expiry</span>,
                },
                {
                  key: 'doors',
                  header: 'Doors',
                  render: (g: any) => {
                    let doorNames: string[] = [];
                    try {
                      const doorJson = typeof g.door_access_json === 'string' ? JSON.parse(g.door_access_json) : (g.door_access_json || []);
                      const doorIds: number[] = Array.isArray(doorJson) ? doorJson : doorJson.device_ids || [];
                      doorNames = doorIds.map((did: number) => {
                        const dev = devices.find((d: any) => d.id === did);
                        return dev?.name || dev?.device_name || `Device ${did}`;
                      });
                    } catch {}
                    return doorNames.length > 0
                      ? <div className="flex flex-wrap gap-1">{doorNames.map((n, i) => <span key={i} className="text-xs bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded">{n}</span>)}</div>
                      : <span className="text-gray-400 text-xs">All doors</span>;
                  },
                },
                {
                  key: 'pin',
                  header: 'PIN',
                  render: (g: any) => {
                    const id = String(g.uuid || g.id);
                    const revealed = revealedPinIds.has(id);
                    if (!g.pin_code) return <span className="text-gray-400">—</span>;
                    return (
                      <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                        <span className="font-mono font-semibold text-gray-900 select-all">
                          {revealed ? g.pin_code : '••••••'}
                        </span>
                        <button
                          type="button"
                          onClick={() => togglePinReveal(id)}
                          className="text-[10px] text-brand-600 hover:text-brand-700 font-medium"
                          title={revealed ? 'Hide' : 'Show'}
                        >{revealed ? 'Hide' : 'Show'}</button>
                      </div>
                    );
                  },
                },
                {
                  key: 'last_used',
                  header: 'Last Used',
                  render: (g: any) => g.last_used_at
                    ? (
                        <div className="flex flex-col">
                          <span className="text-sm text-gray-900">{formatDateTime(g.last_used_at)}</span>
                          <span className="text-[10px] text-gray-500">{g.use_count || 1}× used</span>
                        </div>
                      )
                    : <span className="text-xs text-gray-400 italic">Unused</span>,
                },
                {
                  key: 'generate_activation',
                  header: 'Generate Code',
                  render: (g: any) => {
                    const hasUnconsumedCode = Boolean(g.activation_code) && !g.activation_activated;
                    const label = !g.activation_code
                      ? 'Generate'
                      : g.activation_activated
                        ? 'New Code'
                        : 'Regenerate';
                    const onClick = async (e: React.MouseEvent) => {
                      e.stopPropagation();
                      e.preventDefault();
                      if (hasUnconsumedCode && !window.confirm('Replace the existing code? The old code stops working.')) return;
                      try {
                        const API = process.env.REACT_APP_API_URL || 'https://api.intrlock.io';
                        const token = localStorage.getItem('intrlock_token') || localStorage.getItem('jwt') || localStorage.getItem('token') || '';
                        if (!token) { alert('You are not signed in.'); return; }
                        const res = await fetch(`${API}/intercom/regenerate-guest-activation`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                          body: JSON.stringify({ guest_id: String(g.uuid || g.id) }),
                        });
                        const j = await res.json().catch(() => ({}));
                        if (!res.ok || j.error) {
                          alert(`Failed: ${j.error || res.statusText}`);
                          return;
                        }
                        const newCode = j.activation_code;
                        if (!newCode) { alert('Server did not return a code'); return; }
                        await fetchGuestPins();
                        try { await navigator.clipboard?.writeText(String(newCode)); } catch {}
                      } catch (err: any) {
                        alert(`Failed: ${err.message || err}`);
                      }
                    };
                    return (
                      <button
                        onClick={onClick}
                        className="text-xs text-emerald-600 hover:text-emerald-800 font-medium px-2 py-1 rounded hover:bg-emerald-50"
                        title="Issue a fresh 6-digit mobile-app activation code for this guest"
                      >
                        {label}
                      </button>
                    );
                  },
                },
                {
                  key: 'status',
                  header: 'Status',
                  render: (g: any) => {
                    const statusColors: Record<string, string> = { active: 'green', expired: 'gray', revoked: 'red' };
                    const s = g.status || 'active';
                    return <Badge color={statusColors[s] || 'gray'}>{s}</Badge>;
                  },
                },
                {
                  key: 'actions',
                  header: 'Actions',
                  render: (g: any) => (
                    <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                      {g.status !== 'revoked' && (
                        <button
                          onClick={async () => {
                            if (!window.confirm('Revoke this guest PIN?')) return;
                            try {
                              await apiUpdate('guest_pins', g.uuid || g.id, { status: 'revoked' });
                              refreshAllCounters();
                              await fetchGuestPins();
                            } catch (err) {
                              console.error('Failed to revoke guest:', err);
                              alert('Failed to revoke guest PIN.');
                            }
                          }}
                          className="text-red-600 hover:text-red-700 text-xs font-medium"
                        >
                          Revoke
                        </button>
                      )}
                      <button
                        onClick={() => openGuestEditor(g)}
                        className="text-brand-600 hover:text-brand-700 text-xs font-medium"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => regenerateGuestPin(g)}
                        className="text-amber-600 hover:text-amber-700 text-xs font-medium"
                        title="Issue a new PIN code — the old one stops working"
                      >
                        Regenerate
                      </button>
                    </div>
                  ),
                },
              ]}
              data={guestPins.filter((g: any) => !isDeliveryPin(g))}
            />
            </div>
          )}
        </Card>
      )}

      {tab === 'deliveries' && (
        <Card padding={false}>
          <div className="p-4 border-b border-gray-100 flex items-center justify-between gap-3">
            <div>
              <h3 className="font-semibold">Deliveries</h3>
              <p className="text-xs text-gray-400 mt-0.5">{activeDeliveries.length + deliveryPasses.filter((pass: any) => pass.status === 'active').length} active · {deliveryPins.length + deliveryPasses.length} total</p>
            </div>
            <Button onClick={() => { setCreateDeliveryResult(null); setCreateDeliveryErr(null); setShowCreateDeliveryModal(true); }}>
              <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4"/></svg>
              Create Delivery Pass
            </Button>
          </div>
          {renderPropertyPasses(deliveryPasses, 'delivery')}
          {loadingGuests ? (
            <div className="p-8 text-center text-gray-400">Loading deliveries...</div>
          ) : deliveryPins.length === 0 ? (
            <div className="p-8 text-center text-gray-400">No delivery PINs for this property yet.</div>
          ) : (
            <>
              {/* Mobile cards */}
              <div className="md:hidden p-3 space-y-2">
                {deliveryPins.map((g: any) => {
                  const tenant = allTenants.find((t: any) => t.id === g.granted_by_tenant_id);
                  const status = (g.status || 'active').toLowerCase();
                  const statusColor = status === 'active' ? 'bg-green-100 text-green-700' : status === 'expired' ? 'bg-gray-100 text-gray-500' : 'bg-amber-100 text-amber-700';
                  return (
                    <div key={g.id} className="bg-white rounded-xl border border-gray-200 p-3">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <div className="min-w-0">
                          <p className="font-semibold text-gray-900 truncate">{g.delivery_carrier || g.guest_last_name || 'Delivery'}</p>
                          {g.pin_code && <p className="text-xs font-mono text-gray-500">PIN {g.pin_code}</p>}
                        </div>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${statusColor}`}>{status}</span>
                      </div>
                      {g.delivery_arrival_at && <p className="text-xs text-gray-600">Arrival: {formatDateTime(g.delivery_arrival_at)}</p>}
                      {tenant && <p className="text-xs text-gray-600">For: {tenant.first_name} {tenant.last_name}</p>}
                      {g.delivery_notes && <p className="text-xs text-gray-500 mt-1 italic">"{g.delivery_notes}"</p>}
                      <p className="text-xs text-gray-600 mt-1">
                        {g.last_used_at
                          ? <>Used: {formatDateTime(g.last_used_at)} ({g.use_count || 1}×)</>
                          : <span className="italic text-gray-400">Unused</span>}
                      </p>
                    </div>
                  );
                })}
              </div>
              {/* Desktop table */}
              <div className="hidden md:block">
                <Table
                  columns={[
                    { key: 'carrier', header: 'Carrier', render: (g: any) => (
                      <span className="font-medium text-gray-900">{g.delivery_carrier || g.guest_last_name || '—'}</span>
                    )},
                    { key: 'tenant', header: 'Granted By', render: (g: any) => {
                      if (g.granted_by_pm || !g.granted_by_tenant_id) {
                        return <span className="text-sm text-gray-700 italic">Property Manager</span>;
                      }
                      const tenant = allTenants.find((t: any) => t.id === g.granted_by_tenant_id);
                      if (!tenant) return <span className="text-gray-400">—</span>;
                      const unit = units.find((u: any) => u.id === tenant.unit_id);
                      return (
                        <div>
                          <p className="text-sm text-gray-900">{tenant.first_name} {tenant.last_name}</p>
                          {unit && <p className="text-xs text-gray-500">Unit #{unit.unit_number}</p>}
                        </div>
                      );
                    }},
                    { key: 'arrival', header: 'Arrival', render: (g: any) => g.delivery_arrival_at
                      ? <span className="text-sm text-gray-600">{formatDateTime(g.delivery_arrival_at)}</span>
                      : <span className="text-gray-400">—</span>
                    },
                    { key: 'expires', header: 'Expires', render: (g: any) => g.expires_at
                      ? <span className="text-sm text-gray-600">{formatDateTime(g.expires_at)}</span>
                      : <span className="text-xs text-gray-400 italic">No Expiry</span>
                    },
                    { key: 'notes', header: 'Notes', render: (g: any) => g.delivery_notes
                      ? <span className="text-xs text-gray-600 italic line-clamp-2">{g.delivery_notes}</span>
                      : <span className="text-gray-400 text-xs">—</span>
                    },
                    { key: 'pin', header: 'PIN', render: (g: any) => {
                      const id = String(g.uuid || g.id);
                      const revealed = revealedPinIds.has(id);
                      if (!g.pin_code) return <span className="text-gray-400">—</span>;
                      return (
                        <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                          <span className="font-mono font-semibold text-gray-900 select-all">
                            {revealed ? g.pin_code : '••••'}
                          </span>
                          <button
                            type="button"
                            onClick={() => togglePinReveal(id)}
                            className="text-[10px] text-brand-600 hover:text-brand-700 font-medium"
                            title={revealed ? 'Hide' : 'Show'}
                          >{revealed ? 'Hide' : 'Show'}</button>
                        </div>
                      );
                    }},
                    { key: 'last_used', header: 'Last Used', render: (g: any) => g.last_used_at
                      ? (
                          <div className="flex flex-col">
                            <span className="text-sm text-gray-900">{formatDateTime(g.last_used_at)}</span>
                            <span className="text-[10px] text-gray-500">{g.use_count || 1}× used</span>
                          </div>
                        )
                      : <span className="text-xs text-gray-400 italic">Unused</span>
                    },
                    { key: 'status', header: 'Status', render: (g: any) => {
                      const statusColors: Record<string, string> = { active: 'green', expired: 'gray', revoked: 'red' };
                      const s = g.status || 'active';
                      return <Badge color={statusColors[s] || 'gray'}>{s}</Badge>;
                    }},
                    { key: 'actions', header: 'Actions', render: (g: any) => (
                      <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => openGuestEditor(g)}
                          className="text-brand-600 hover:text-brand-700 text-xs font-medium"
                        >Edit</button>
                        <button
                          onClick={() => regenerateGuestPin(g)}
                          className="text-amber-600 hover:text-amber-700 text-xs font-medium"
                          title="Issue a new PIN code — the old one stops working"
                        >Regenerate</button>
                        {g.status !== 'revoked' && (
                          <button
                            onClick={async () => {
                              if (!window.confirm('Revoke this delivery PIN?')) return;
                              try {
                                await apiUpdate('guest_pins', g.uuid || g.id, { status: 'revoked' });
                                refreshAllCounters();
                                await fetchGuestPins();
                              } catch (err) {
                                console.error('Failed to revoke delivery:', err);
                                alert('Failed to revoke delivery PIN.');
                              }
                            }}
                            className="text-red-600 hover:text-red-700 text-xs font-medium"
                          >Revoke</button>
                        )}
                      </div>
                    )},
                  ]}
                  data={deliveryPins}
                />
              </div>
            </>
          )}
        </Card>
      )}

      {/* ── Create Guest PIN modal (PM-side) ──────────────────────────── */}
      <Modal open={showCreateGuestModal} onClose={() => setShowCreateGuestModal(false)} title="Create Visitor Pass" size="md">
        <div className="space-y-3 max-h-[70vh] overflow-y-auto">
          <p className="text-sm text-gray-500">Create a directory code and secure link for the visitor to verify by phone.</p>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Granted by</label>
            <select
              value={createGuestForm.tenant_id}
              onChange={(e) => setCreateGuestForm({ ...createGuestForm, tenant_id: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="">Property Manager (you)</option>
              <optgroup label="On behalf of a tenant">
                {allTenants.filter((t: any) => !isSubtenantRecord(t)).map((t: any) => {
                  const unit = units.find((u: any) => u.id === t.unit_id);
                  return (
                    <option key={t.id} value={t.uuid || t.id}>
                      {t.first_name} {t.last_name}{unit ? ` — Unit #${unit.unit_number}` : ''}
                    </option>
                  );
                })}
              </optgroup>
            </select>
            <p className="text-[11px] text-gray-400 mt-1">Choose a resident when this visitor is for a specific unit.</p>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">First name</label>
              <input value={createGuestForm.first_name} onChange={(e) => setCreateGuestForm({ ...createGuestForm, first_name: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Last name</label>
              <input value={createGuestForm.last_name} onChange={(e) => setCreateGuestForm({ ...createGuestForm, last_name: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Phone</label>
              <input type="tel" value={createGuestForm.phone} onChange={(e) => setCreateGuestForm({ ...createGuestForm, phone: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Email (optional)</label>
              <input type="email" value={createGuestForm.email} onChange={(e) => setCreateGuestForm({ ...createGuestForm, email: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Start</label>
              <input type="datetime-local" value={createGuestForm.starts_at} onChange={(e) => setCreateGuestForm({ ...createGuestForm, starts_at: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">End</label>
              <input type="datetime-local" value={createGuestForm.expires_at} onChange={(e) => setCreateGuestForm({ ...createGuestForm, expires_at: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Doors</label>
            <div className="mb-2 flex flex-wrap gap-3 text-xs"><label><input type="radio" name="property-guest-access" checked={createGuestForm.access_mode === 'all'} onChange={() => setCreateGuestForm(cur => ({ ...cur, access_mode: 'all', device_ids: [] }))}/> All eligible doors</label><label><input type="radio" name="property-guest-access" checked={createGuestForm.access_mode === 'selected'} onChange={() => setCreateGuestForm(cur => ({ ...cur, access_mode: 'selected' }))}/> Selected doors</label><label><input type="radio" name="property-guest-access" checked={createGuestForm.access_mode === 'none'} onChange={() => setCreateGuestForm(cur => ({ ...cur, access_mode: 'none', device_ids: [] }))}/> No door access</label></div>
            {createGuestForm.access_mode === 'selected' && (
            <div className="border border-gray-200 rounded-lg max-h-40 overflow-y-auto">
              {unlockDevices.length === 0 ? (
                <p className="px-3 py-2 text-xs text-gray-500 italic">No devices on this property.</p>
              ) : unlockDevices.map((d: any) => {
                const k = deviceAccessId(d);
                const checked = createGuestForm.device_ids.includes(k);
                return (
                  <label key={k} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-gray-50 border-b border-gray-50 last:border-b-0">
                    <input type="checkbox" checked={checked}
                      onChange={() => setCreateGuestForm((cur) => ({
                        ...cur,
                        device_ids: checked ? cur.device_ids.filter((x) => x !== k) : [...cur.device_ids, k],
                      }))}
                      className="w-4 h-4 rounded border-gray-300 text-accent-600 focus:ring-accent-500" />
                    <span className="flex-1 text-gray-700 truncate">{d.name || d.device_name || `Device ${d.id}`}</span>
                  </label>
                );
              })}
            </div>
            )}
          </div>
          <p className="text-xs text-gray-600">Effective access: {createGuestForm.access_mode === 'all' ? 'All current and future eligible doors at this property' : createGuestForm.access_mode === 'none' ? 'No door unlock access' : `${createGuestForm.device_ids.length} selected door${createGuestForm.device_ids.length === 1 ? '' : 's'}`}.</p>
          <fieldset className="border border-gray-300 p-3 space-y-2">
            <legend className="px-1 text-xs font-semibold text-gray-800">Visitor consent</legend>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={createGuestForm.consent_enabled} onChange={(e) => setCreateGuestForm((cur) => ({ ...cur, consent_enabled: e.target.checked }))} />Require acceptance during onboarding</label>
            {createGuestForm.consent_enabled && <>
              <label className="block text-xs font-medium text-gray-600">Form title<input className="mt-1 w-full border border-gray-300 px-3 py-2 text-sm" value={createGuestForm.consent_title} onChange={(e) => setCreateGuestForm((cur) => ({ ...cur, consent_title: e.target.value }))} /></label>
              <label className="block text-xs font-medium text-gray-600">Consent disclosure<textarea className="mt-1 w-full border border-gray-300 px-3 py-2 text-sm" rows={3} value={createGuestForm.consent_text} onChange={(e) => setCreateGuestForm((cur) => ({ ...cur, consent_text: e.target.value }))} /></label>
              <label className="block text-xs font-medium text-gray-600">Acknowledgement label<input className="mt-1 w-full border border-gray-300 px-3 py-2 text-sm" value={createGuestForm.consent_acknowledgement} onChange={(e) => setCreateGuestForm((cur) => ({ ...cur, consent_acknowledgement: e.target.value }))} /></label>
            </>}
          </fieldset>
          {createGuestErr && <p className="text-sm text-red-600">{createGuestErr}</p>}
          {createGuestResult && (
            <div className="rounded-lg bg-green-50 border border-green-200 p-3 text-center">
              <p className="text-xs text-green-700 mb-1">Directory code for {createGuestResult.name}</p>
              <p className="text-3xl font-mono font-bold text-green-900 tracking-widest">{createGuestResult.pin}</p>
              {createGuestResult.passUrl && <a href={createGuestResult.passUrl} target="_blank" rel="noreferrer" className="mt-2 block break-all text-xs font-medium text-green-800 underline">Open secure visitor pass</a>}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
            <Button variant="secondary" onClick={() => setShowCreateGuestModal(false)}>{createGuestResult ? 'Close' : 'Cancel'}</Button>
            <Button onClick={submitCreateGuest} disabled={createGuestBusy}>{createGuestBusy ? 'Creating…' : 'Create secure visitor pass'}</Button>
          </div>
        </div>
      </Modal>

      {/* ── Create Delivery PIN modal (PM-side) ──────────────────────── */}
      <Modal open={showCreateDeliveryModal} onClose={() => setShowCreateDeliveryModal(false)} title="Create Delivery Pass" size="md">
        <div className="space-y-3 max-h-[70vh] overflow-y-auto">
          <p className="text-sm text-gray-500">Create a secure delivery link and directory code. Access ends 24 hours after expected arrival by default, or at the time you choose.</p>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Granted by</label>
            <select
              value={createDeliveryForm.tenant_id}
              onChange={(e) => setCreateDeliveryForm({ ...createDeliveryForm, tenant_id: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="">Select a resident</option>
              <optgroup label="Resident">
                {allTenants.filter((t: any) => !isSubtenantRecord(t)).map((t: any) => {
                  const unit = units.find((u: any) => u.id === t.unit_id);
                  return (
                    <option key={t.id} value={t.uuid || t.id}>
                      {t.first_name} {t.last_name}{unit ? ` — Unit #${unit.unit_number}` : ''}
                    </option>
                  );
                })}
              </optgroup>
            </select>
            <p className="text-[11px] text-gray-400 mt-1">This resident and unit are the delivery destination; the contact below verifies the secure pass.</p>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Expected arrival</label>
            <input type="datetime-local" value={createDeliveryForm.arrival_at} onChange={(e) => setCreateDeliveryForm({ ...createDeliveryForm, arrival_at: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
          </div>
          <div><label className="block text-xs font-medium text-gray-600 mb-1">Access ends (optional)</label><input type="datetime-local" value={createDeliveryForm.expires_at} onChange={(e) => setCreateDeliveryForm({ ...createDeliveryForm, expires_at: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" /></div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Carrier</label>
            <input type="text" value={createDeliveryForm.carrier} onChange={(e) => setCreateDeliveryForm({ ...createDeliveryForm, carrier: e.target.value })} placeholder="UPS, FedEx, Amazon…" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Delivery contact phone</label>
            <input type="tel" autoComplete="tel" value={createDeliveryForm.courier_phone} onChange={(e) => setCreateDeliveryForm({ ...createDeliveryForm, courier_phone: e.target.value })} placeholder="Phone used to verify the secure pass" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Message for driver</label>
            <textarea rows={3} value={createDeliveryForm.notes} onChange={(e) => setCreateDeliveryForm({ ...createDeliveryForm, notes: e.target.value })} placeholder="Where to leave it, gate code, etc." className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
          </div>
          <fieldset>
            <legend className="block text-xs font-medium text-gray-600 mb-1">Door access</legend>
            <div className="mb-2 flex flex-wrap gap-2" role="radiogroup" aria-label="Delivery door access">
              {(['all', 'selected', 'none'] as const).map((mode) => <label key={mode} className="inline-flex items-center gap-2 rounded-full border border-gray-300 px-3 py-2 text-sm"><input type="radio" name="delivery-access-mode" checked={createDeliveryForm.access_mode === mode} onChange={() => setCreateDeliveryForm((cur) => ({ ...cur, access_mode: mode }))} /><span>{mode === 'all' ? 'All eligible doors' : mode === 'selected' ? 'Selected doors' : 'No door access'}</span></label>)}
            </div>
            {createDeliveryForm.access_mode === 'selected' && <div>
            <div className="border border-gray-200 rounded-lg max-h-40 overflow-y-auto">
              {unlockDevices.length === 0 ? (
                <p className="px-3 py-2 text-xs text-gray-500 italic">No devices on this property.</p>
              ) : unlockDevices.map((d: any) => {
                const k = deviceAccessId(d);
                const checked = createDeliveryForm.device_ids.includes(k);
                return (
                  <label key={k} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-gray-50 border-b border-gray-50 last:border-b-0">
                    <input type="checkbox" checked={checked}
                      onChange={() => setCreateDeliveryForm((cur) => ({
                        ...cur,
                        device_ids: checked ? cur.device_ids.filter((x) => x !== k) : [...cur.device_ids, k],
                      }))}
                      className="w-4 h-4 rounded border-gray-300 text-accent-600 focus:ring-accent-500" />
                    <span className="flex-1 text-gray-700 truncate">{d.name || d.device_name || `Device ${d.id}`}</span>
                  </label>
                );
              })}
            </div>
            </div>}
          </fieldset>
          <p className="text-xs text-gray-600">Effective access: {createDeliveryForm.access_mode === 'all' ? 'All current and future eligible doors at this property' : createDeliveryForm.access_mode === 'none' ? 'No door unlock access' : `${createDeliveryForm.device_ids.length} selected door${createDeliveryForm.device_ids.length === 1 ? '' : 's'}`}. Ends {createDeliveryForm.expires_at ? new Date(createDeliveryForm.expires_at).toLocaleString() : '24 hours after expected arrival'}.</p>
          {createDeliveryErr && <p className="text-sm text-red-600">{createDeliveryErr}</p>}
          {createDeliveryResult && (
            <div className="rounded-lg bg-green-50 border border-green-200 p-3 text-center">
              <p className="text-xs text-green-700 mb-1">Directory code · arrival {createDeliveryResult.arrival}</p>
              <p className="text-3xl font-mono font-bold text-green-900 tracking-widest">{createDeliveryResult.pin}</p>
              {createDeliveryResult.passUrl && <a className="mt-2 block break-all text-sm font-semibold text-emerald-700 underline" href={createDeliveryResult.passUrl} target="_blank" rel="noreferrer">Open secure delivery pass</a>}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
            <Button variant="secondary" onClick={() => setShowCreateDeliveryModal(false)}>{createDeliveryResult ? 'Close' : 'Cancel'}</Button>
            <Button onClick={submitCreateDelivery} disabled={createDeliveryBusy}>{createDeliveryBusy ? 'Creating…' : 'Generate secure pass'}</Button>
          </div>
        </div>
      </Modal>

      {/* ── Edit Guest / Edit Delivery modal ─────────────────────────── */}
      <Modal open={!!editingGuest} onClose={() => setEditingGuest(null)} title={isEditingDelivery ? 'Edit Delivery PIN' : 'Edit Guest PIN'} size="md">
        {editingGuest && (
          <div className="space-y-3 max-h-[70vh] overflow-y-auto">
            <p className="text-xs text-gray-500">
              PIN <span className="font-mono font-semibold text-gray-900">{editingGuest.pin_code || '—'}</span> · status {editingGuest.status || 'active'}
            </p>
            {!isEditingDelivery && (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">First name</label>
                  <input value={editGuestForm.first_name} onChange={(e) => setEditGuestForm({ ...editGuestForm, first_name: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Last name</label>
                  <input value={editGuestForm.last_name} onChange={(e) => setEditGuestForm({ ...editGuestForm, last_name: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
              </div>
            )}
            {!isEditingDelivery && (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Phone</label>
                  <input type="tel" value={editGuestForm.phone} onChange={(e) => setEditGuestForm({ ...editGuestForm, phone: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Email</label>
                  <input type="email" value={editGuestForm.email} onChange={(e) => setEditGuestForm({ ...editGuestForm, email: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
              </div>
            )}
            {isEditingDelivery && (
              <>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Carrier</label>
                  <input type="text" value={editGuestForm.carrier} onChange={(e) => setEditGuestForm({ ...editGuestForm, carrier: e.target.value })} placeholder="UPS, FedEx, Amazon…" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Message for driver</label>
                  <textarea rows={3} value={editGuestForm.notes} onChange={(e) => setEditGuestForm({ ...editGuestForm, notes: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
              </>
            )}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{isEditingDelivery ? 'Arrival' : 'Start'}</label>
                <input type="datetime-local" value={editGuestForm.starts_at} onChange={(e) => setEditGuestForm({ ...editGuestForm, starts_at: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{isEditingDelivery ? 'Expires' : 'End'}</label>
                <input type="datetime-local" value={editGuestForm.expires_at} onChange={(e) => setEditGuestForm({ ...editGuestForm, expires_at: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Doors ({editGuestForm.device_ids.length} selected)</label>
              <div className="border border-gray-200 rounded-lg max-h-40 overflow-y-auto">
                {unlockDevices.length === 0 ? (
                  <p className="px-3 py-2 text-xs text-gray-500 italic">No devices on this property.</p>
                ) : unlockDevices.map((d: any) => {
                  const k = deviceAccessId(d);
                  const checked = editGuestForm.device_ids.includes(k);
                  return (
                    <label key={k} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-gray-50 border-b border-gray-50 last:border-b-0">
                      <input type="checkbox" checked={checked}
                        onChange={() => setEditGuestForm((cur) => ({
                          ...cur,
                          device_ids: checked ? cur.device_ids.filter((x) => x !== k) : [...cur.device_ids, k],
                        }))}
                        className="w-4 h-4 rounded border-gray-300 text-accent-600 focus:ring-accent-500" />
                      <span className="flex-1 text-gray-700 truncate">{d.name || d.device_name || `Device ${d.id}`}</span>
                    </label>
                  );
                })}
              </div>
              <p className="text-[11px] text-gray-400 mt-1">Doors this PIN can unlock at this property.</p>
            </div>
            {editGuestErr && <p className="text-sm text-red-600">{editGuestErr}</p>}
            <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
              <Button variant="secondary" onClick={() => setEditingGuest(null)}>Cancel</Button>
              <Button onClick={submitEditGuest} disabled={editGuestBusy}>{editGuestBusy ? 'Saving…' : 'Save Changes'}</Button>
            </div>
          </div>
        )}
      </Modal>

      {(tab === "settings" || tab === "intercom" as any) && (() => {
        // Helper: toggle a section open/closed
        const toggleSection = (key: string) =>
          setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));

        // Helper: save a section and show feedback
        // Fields that can override per-intercom. When the Settings Scope picker
        // has a specific intercom selected, these go to /intercom-configs/{id}
        // instead of (or in addition to) the property row. Visitor pages
        // merge intercom over property; missing intercom fields fall back to
        // the property setting.
        const PER_INTERCOM_FIELDS = new Set([
          // Identity / call routing
          'mode', 'phone_number', 'label', 'kiosk_code', 'hidden_unit_ids',
          // Access controls
          'pin_access_enabled', 'face_access_enabled', 'guest_pin_access_enabled',
          'require_directory_code', 'directory_access_code',
          'auto_lock_enabled', 'auto_lock_start', 'auto_lock_end',
          // Call behavior
          'allow_video_call', 'allow_phone_call', 'simultaneous_ring',
          'allow_door_unlock_in_call', 'record_video_calls',
          'require_visitor_name', 'require_visitor_phone',
          'call_timeout_seconds', 'ring_duration_seconds',
          'no_answer_message', 'call_blocked_message',
          // Directory display
          'directory_display', 'directory_name_format', 'unit_sort_order',
          'show_phone_on_directory', 'show_floor_grouping',
          'hide_vacant_units', 'show_tenant_count',
          // Branding & theme (visitor greeting stays per-intercom because
          // each intercom renders its own visitor screen)
          'display_style', 'accent_color', 'background_color',
          'logo_url', 'logo_size_px', 'greeting_message',
          'custom_no_answer_text', 'custom_footer_text', 'show_powered_by',
          // Privacy / rate-limit
          'private_directory', 'log_all_visitor_access',
          'visitor_rate_limit', 'block_repeated_no_answers', 'max_no_answers_before_block',
          // Voice IVR — per-intercom. telnyx-texml-webhook (us-east-1)
          // looks up intercom_config by inbound DID and overlays these
          // onto the property defaults. Null/empty intercom values fall
          // back to the property row.
          'voice_greeting_message', 'voice_name', 'voice_speed', 'voice_pitch',
          'voice_volume', 'voice_language', 'ring_tone',
          'dtmf_unlock_digit', 'dwell_time',
        ]);

        const saveSection = async (key: string, fields: Record<string, any>) => {
          const targetIntercomIds: Array<string | number> = applySettingsToAll
            ? intercoms.map((i) => i.id)
            : (selectedIntercomId ? [selectedIntercomId] : []);
          // eslint-disable-next-line no-console
          console.log(`[settings save] section="${key}" scope_intercom_uuids=${JSON.stringify(targetIntercomIds)} applyAll=${applySettingsToAll} fields=`, fields);
          setSectionSaving(prev => ({ ...prev, [key]: true }));
          setSectionSaveMsg(prev => ({ ...prev, [key]: '' }));
          try {
            const assertPersisted = (saved: Record<string, any>, expected: Record<string, any>) => {
              for (const [field, value] of Object.entries(expected)) {
                if (JSON.stringify(saved?.[field] ?? null) !== JSON.stringify(value ?? null)) {
                  throw new Error(`${field} was not persisted; visitor settings were not updated`);
                }
              }
            };
            // Split into per-intercom subset + property-only subset
            const perIntercom: Record<string, any> = {};
            const propertyOnly: Record<string, any> = {};
            for (const [k, v] of Object.entries(fields)) {
              if (PER_INTERCOM_FIELDS.has(k)) perIntercom[k] = v;
              else propertyOnly[k] = v;
            }

            // 1. Property-only fields go to the property row. Stamp the
            //    targeted intercom uuids in the body for traceability — the
            //    backend ignores unknown columns but it shows up in the
            //    request body in DevTools/server logs so you can confirm
            //    the right intercom was being configured at save time.
            if (Object.keys(propertyOnly).length > 0) {
              const propBody = targetIntercomIds.length > 0
                ? { ...propertyOnly, _scoped_intercom_uuids: targetIntercomIds }
                : propertyOnly;
              const resp = await apiUpdate('properties', id!, propBody);
              assertPersisted(resp, propertyOnly);
              // eslint-disable-next-line no-console
              console.log(`[settings save] PUT /properties/${id} body=`, propBody, 'resp=', resp);
            }

            // 2. Per-intercom fields go to the selected intercom(s). When no
            //    intercom is selected (scope = property/all), still write to
            //    the property row so the property-wide setting tracks it.
            if (Object.keys(perIntercom).length > 0) {
              if (targetIntercomIds.length > 0) {
                for (const icId of targetIntercomIds) {
                  const body = { ...perIntercom, intercom_config_uuid: icId, intercom_config_id: icId };
                  const r = await apiUpdate('intercom_configs', icId, body);
                  assertPersisted(r, perIntercom);
                  // eslint-disable-next-line no-console
                  console.log(`[settings save] PUT /intercom-configs/${icId} body=`, body, 'resp=', r);
                }
                // Optimistically merge the saved per-intercom fields into the
                // local intercoms array so the toggles reflect the new state
                // BEFORE refreshProperty re-fetches. Without this, refreshProperty's
                // overlay uses the stale intercom value and the UI snaps back.
                setIntercoms((prev) => prev.map((ic) =>
                  targetIntercomIds.includes(ic.id) ? { ...ic, ...perIntercom } : ic
                ));
              } else {
                const resp = await apiUpdate('properties', id!, perIntercom);
                assertPersisted(resp, perIntercom);
                // eslint-disable-next-line no-console
                console.log(`[settings save] property fallback PUT body=`, perIntercom, 'resp=', resp);
              }
            }

            // Optimistic UI: we already wrote, the next render via the
            // setIntercoms above + the local toggle state will already match
            // the saved values. refreshAllCounters runs in the background;
            // refreshProperty refetches the property row but the intercom
            // overlay it does is now using FRESH local data.
            refreshAllCounters();
            await refreshProperty();
            setSectionSaveMsg(prev => ({ ...prev, [key]: 'Saved!' }));
            setTimeout(() => setSectionSaveMsg(prev => ({ ...prev, [key]: '' })), 3000);
          } catch (err) {
            console.error(`Failed to save section ${key}:`, err);
            setSectionSaveMsg(prev => ({ ...prev, [key]: 'Save failed.' }));
          } finally {
            setSectionSaving(prev => ({ ...prev, [key]: false }));
          }
        };

        const selectCls = "w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-accent-500 focus:border-accent-500 bg-white";
        const checkRow = (checked: boolean, onChange: (v: boolean) => void, label: string) => (
          <label className="flex items-center gap-3 cursor-pointer py-1">
            <input
              type="checkbox"
              checked={checked}
              onChange={e => onChange(e.target.checked)}
              className="rounded border-gray-300 text-accent-600 focus:ring-accent-500 h-4 w-4 flex-shrink-0"
            />
            <span className="text-sm text-gray-700">{label}</span>
          </label>
        );

        // Static (non-collapsible) header used for the consolidated subsections
        // inside each view's single Card. Importance hierarchy is conveyed by
        // numbered prefixes + ordering, not by collapse-state.
        const staticHeader = (n: number, title: string, subtitle?: string, badge?: string) => (
          <div className="flex items-start justify-between gap-3 p-4 border-b border-gray-100 bg-gray-50/50">
            <div>
              <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-accent-100 text-accent-700 text-[10px] font-bold">{n}</span>
                {title}
              </h3>
              {subtitle && <p className="text-xs text-gray-500 mt-0.5 ml-7">{subtitle}</p>}
            </div>
            {badge && <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">{badge}</span>}
          </div>
        );
        const sectionHeader = (key: string, title: string, subtitle?: string) => (
          <button
            type="button"
            onClick={() => toggleSection(key)}
            className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors rounded-t-lg"
          >
            <div>
              <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
              {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
            </div>
            <svg
              className={`w-5 h-5 text-gray-400 transition-transform ${expandedSections[key] ? 'rotate-180' : ''}`}
              fill="none" viewBox="0 0 24 24" stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        );

        const saveBtnRow = (key: string) => (
          <div className="flex items-center gap-3 pt-2">
            <Button
              className="bg-accent-500 hover:bg-accent-600"
              disabled={sectionSaving[key]}
              onClick={() => {
                if (key === 'directory') saveSection(key, {
                  directory_display: iDirectoryDisplay,
                  directory_name_format: iDirectoryNameFormat,
                  show_phone_on_directory: iShowPhoneOnDirectory,
                  show_floor_grouping: iShowFloorGrouping,
                  unit_sort_order: iUnitSortOrder,
                  hide_vacant_units: iHideVacantUnits,
                  show_tenant_count: iShowTenantCount,
                  greeting_message: iGreetingMessage,
                });
                else if (key === 'deliveries') saveSection(key, {
                  delivery_access_enabled: iDeliveryAccessEnabled,
                  delivery_message: iDeliveryMessage,
                  delivery_door_ids: iDeliveryDoorIds,
                });
                else if (key === 'calls') saveSection(key, {
                  // Postgres has these as singular `allow_video_call` /
                  // `allow_phone_call` (matches what VisitorDirectory reads
                  // from `settings`). Writing the plural would silently drop.
                  allow_video_call: iAllowVideoCalls,
                  allow_phone_call: iAllowPhoneCalls,
                  call_timeout_seconds: Number(iCallTimeoutSeconds),
                  ring_duration_seconds: Number(iRingDurationSeconds),
                  simultaneous_ring: iSimultaneousRing,
                  allow_door_unlock_in_call: iAllowDoorUnlockInCall,
                  record_video_calls: iRecordVideoCalls,
                  require_visitor_name: iRequireVisitorName,
                  require_visitor_phone: iRequireVisitorPhone,
                  no_answer_message: iNoAnswerMessage,
                  call_blocked_message: iCallBlockedMessage,
                });
                else if (key === 'access') saveSection(key, {
                  visitor_pass_entry_photo_mode: visitorPassEntryPhotoMode,
                  pin_access_enabled: iPinAccessEnabled,
                  face_access_enabled: iFaceAccessEnabled,
                  guest_pin_access_enabled: iGuestPinAccessEnabled,
                  require_directory_code: iRequireDirectoryCode,
                  directory_access_code: iDirectoryAccessCode,
                  auto_lock_enabled: iAutoLockEnabled,
                  auto_lock_start: iAutoLockStart,
                  auto_lock_end: iAutoLockEnd,
                  geofence_enabled: iGeofenceEnabled,
                  geofence_lat: iGeofenceLat ? Number(iGeofenceLat) : null,
                  geofence_lng: iGeofenceLng ? Number(iGeofenceLng) : null,
                  geofence_radius_m: iGeofenceRadiusM ? Number(iGeofenceRadiusM) : 100,
                  geofence_apply_to: [
                    iGeofenceApplyWeb ? 'web' : null,
                    iGeofenceApplyMobile ? 'mobile' : null,
                  ].filter(Boolean),
                });
                else if (key === 'guestAccess') saveSection(key, {
                  allow_tenant_create_guests: iAllowTenantCreateGuests,
                  default_max_guests_per_tenant: Number(iDefaultMaxGuestsPerTenant),
                  default_max_guest_duration_days: Number(iDefaultMaxGuestDurationDays),
                  // guest_doors_available_json removed — door scope now lives on each Intercom card.
                  guest_pin_length: Number(iGuestPinLength),
                  guest_app_access_enabled: iGuestAppAccessEnabled,
                  notify_manager_on_guest_create: iNotifyManagerOnGuestCreate,
                  notify_tenant_on_guest_pin_use: iNotifyTenantOnGuestPinUse,
                });
                else if (key === 'subTenant') saveSection(key, {
                  allow_tenant_create_subtenants: iAllowTenantCreateSubtenants,
                  subtenant_receive_calls: iSubtenantReceiveCalls,
                  subtenant_door_access_default: iSubtenantDoorAccessDefault,
                  minor_age_threshold: Number(iMinorAgeThreshold),
                  minor_call_block: iMinorCallBlock,
                });
                else if (key === 'voice') saveSection(key, {
                  voice_name: iVoiceName,
                  voice_speed: iVoiceSpeed,
                  voice_pitch: iVoicePitch,
                  voice_volume: iVoiceVolume,
                  voice_language: iVoiceLanguage,
                  dtmf_unlock_digit: iDtmfUnlockDigit,
                  // Voice IVR section edits the phone-call greeting only.
                  // The visitor-flow greeting lives on its own field
                  // (saved by the visitor-settings section).
                  voice_greeting_message: iVoiceGreeting,
                  ring_tone: iRingTone,
                });
                else if (key === 'branding') saveSection(key, {
                  display_style: iDisplayStyle,
                  accent_color: iAccentColor,
                  background_color: iBackgroundColor,
                  logo_url: iLogoUrl,
                  logo_size_px: Number(iLogoSizePx) || 80,
                  custom_no_answer_text: iCustomNoAnswerText,
                  custom_footer_text: iCustomFooterText,
                });
                else if (key === 'privacy') saveSection(key, {
                  private_directory: iPrivateDirectory,
                  log_all_visitor_access: iLogAllVisitorAccess,
                  visitor_rate_limit: Number(iVisitorRateLimit),
                  block_repeated_no_answers: iBlockRepeatedNoAnswers,
                  max_no_answers_before_block: Number(iMaxNoAnswersBeforeBlock),
                });
              }}
            >
              {sectionSaving[key] ? 'Saving...' : 'Save'}
            </Button>
            {sectionSaveMsg[key] && (
              <span className={`text-sm font-medium ${sectionSaveMsg[key] === 'Saved!' ? 'text-green-600' : 'text-red-600'}`}>
                {sectionSaveMsg[key]}
              </span>
            )}
          </div>
        );

        const settingsSections = [
          { id: 'settings-branding-display', label: 'Branding and Display',     icon: '🎨' },
          { id: 'settings-vv',               label: 'Voice and Video Settings', icon: '🎥' },
          { id: 'settings-access-sec',       label: 'Access and Security',      icon: '🔐' },
          { id: 'settings-mobile-app',       label: 'Mobile App Permissions',   icon: '📱' },
        ];
        // Every sidebar item is now a switchable view — Intercom Devices is the
        // first / default view; Settings Scope picker is removed entirely.
        const viewKeyForId: Record<string, typeof activeSettingsView> = {
          'settings-branding-display': 'branding-display',
          'settings-vv': 'vv',
          'settings-access-sec': 'access-sec',
          'settings-mobile-app': 'mobile-app',
        };
        return (
          <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-6">
            {/* ── Sidebar (sticky on desktop) ── */}
            <aside className="lg:sticky lg:top-24 lg:self-start">
              <div className="bg-white rounded-xl border border-gray-100 shadow-lg p-2">
                <p className="px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-gray-500">Settings</p>
                <nav className="space-y-0.5">
                  {settingsSections.map(s => {
                    const key = viewKeyForId[s.id];
                    const active = activeSettingsView === key;
                    return (
                      <button
                        key={s.id}
                        onClick={() => setActiveSettingsView(key)}
                        className={`w-full text-left flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                          active
                            ? 'bg-accent-50 text-accent-700 ring-1 ring-accent-200'
                            : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                        }`}
                      >
                        <span className="text-base leading-none">{s.icon}</span>
                        <span>{s.label}</span>
                      </button>
                    );
                  })}
                </nav>
              </div>
            </aside>

            {/* ── Settings content ── */}
            <div className="space-y-6 min-w-0">
            {/* Intercom Devices is now its own top-level Property tab; the
                Settings sidebar focuses solely on configuration views. */}

            {/* ── Settings Scope picker — chooses which intercom the settings
                below apply to. */}
            {true && (
              <Card padding={false}>
                <div className="px-4 py-3 flex items-center gap-3 flex-wrap">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Editing Settings For</span>
                  <select
                    value={String(selectedIntercomId)}
                    onChange={(e) => setSelectedIntercomId(e.target.value)}
                    disabled={intercoms.length === 0 || applySettingsToAll}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-medium bg-white focus:ring-2 focus:ring-accent-500 focus:border-accent-500 disabled:bg-gray-100 disabled:text-gray-400 min-w-[220px]"
                  >
                    {intercoms.length === 0 && <option value="">— No intercoms yet —</option>}
                    {intercoms.map((ic, i) => (
                      <option key={String(ic.id)} value={String(ic.id)}>
                        {ic.label || `Intercom #${i + 1}`}
                        {ic.phone_number ? ` · ${ic.phone_number}` : ' · video only'}
                      </option>
                    ))}
                  </select>
                  <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={applySettingsToAll}
                      onChange={(e) => setApplySettingsToAll(e.target.checked)}
                      className="w-4 h-4 rounded border-gray-300 text-accent-600 focus:ring-accent-500"
                      disabled={intercoms.length < 2}
                    />
                    <span>Apply to <strong>all intercoms</strong> <span className="text-xs text-gray-500">(excluding devices)</span></span>
                  </label>
                  {applySettingsToAll && intercoms.length >= 2 && (
                    <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                      Saving writes to all {intercoms.length} intercoms
                    </span>
                  )}
                </div>
              </Card>
            )}

            {/* ── 1. Directory Display Settings ── */}
            {/* ══════════ View: Branding and Display ══════════ */}
            {activeSettingsView === 'branding-display' && (
            <Card padding={false}>
            <div id="settings-directory">
              {staticHeader(1, 'Directory Display Settings', 'How names, units and info appear to visitors — controls the visitor page layout')}
              <div className="px-4 pb-4 space-y-4">
                  <div className="pt-4">
                    <label className="block text-xs font-medium text-gray-600 mb-1">Directory Display Mode</label>
                    <select className={selectCls} value={iDirectoryDisplay} onChange={e => setIDirectoryDisplay(e.target.value)}>
                      <option value="tenant_name">Show Tenant Names</option>
                      <option value="unit_only">Unit Number Only</option>
                      <option value="display_name">Custom Display Names</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Name Format</label>
                    <select className={selectCls} value={iDirectoryNameFormat} onChange={e => setIDirectoryNameFormat(e.target.value)}>
                      <option value="First Last">First Last</option>
                      <option value="Last, First">Last, First</option>
                      <option value="First Initial + Last">First Initial + Last</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Unit Sort Order</label>
                    <select className={selectCls} value={iUnitSortOrder} onChange={e => setIUnitSortOrder(e.target.value)}>
                      <option value="Numerical">Numerical</option>
                      <option value="Alphabetical">Alphabetical</option>
                      <option value="Custom">Custom</option>
                    </select>
                  </div>
                  <div className="space-y-1">
                    {checkRow(iShowPhoneOnDirectory, setIShowPhoneOnDirectory, 'Show phone numbers on directory')}
                    {checkRow(iShowFloorGrouping, setIShowFloorGrouping, 'Group units by floor')}
                    {checkRow(iHideVacantUnits, setIHideVacantUnits, 'Hide vacant units (units with no tenants)')}
                    {checkRow(iShowTenantCount, setIShowTenantCount, 'Show total tenant count on directory')}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Visitor Directory Greeting</label>
                    <textarea
                      value={iGreetingMessage}
                      onChange={e => setIGreetingMessage(e.target.value)}
                      placeholder="Welcome! Please select the unit you're visiting."
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-accent-500 focus:border-accent-500"
                    />
                    <p className="text-xs text-gray-400 mt-1">Shown on the visitor directory page at visitor.intrlock.io. Separate from the phone IVR greeting.</p>
                  </div>
                  {saveBtnRow('directory')}
                </div>
            </div>
            <div className="border-t-2 border-gray-100" />
            <div id="settings-deliveries-inline">
              {staticHeader(2, 'Deliveries', 'Door access + driver message for delivery drivers')}
              <div className="px-4 pb-4 space-y-4">
                <div className="space-y-1">
                  {checkRow(iDeliveryAccessEnabled, setIDeliveryAccessEnabled, 'Enable delivery driver access')}
                </div>
                <div className={iDeliveryAccessEnabled ? '' : 'opacity-50 pointer-events-none'}>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Doors delivery drivers can unlock</label>
                  {unlockDevices.length === 0 ? (
                    <p className="text-xs text-gray-400 italic">No doors configured on this property yet — add devices first.</p>
                  ) : (
                    <div className="space-y-1 max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-2 bg-gray-50">
                      {unlockDevices.map((d: any) => {
                        const id = deviceAccessId(d);
                        const checked = iDeliveryDoorIds.includes(id);
                        const toggle = () => setIDeliveryDoorIds(prev =>
                          checked ? prev.filter(x => x !== id) : [...prev, id]
                        );
                        return (
                          <label key={id} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer hover:bg-white px-2 py-1 rounded">
                            <input type="checkbox" checked={checked} onChange={toggle} className="rounded border-gray-300" />
                            <span className="flex-1">{d.name || d.device_name || `Door ${id.slice(0, 6)}`}</span>
                            <span className="text-xs text-gray-400">{intrlockTypeLabel(d)}</span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                  <p className="text-xs text-gray-400 mt-1">Drivers with a valid delivery code can unlock only the doors you select here.</p>
                </div>
                <div className={iDeliveryAccessEnabled ? '' : 'opacity-50 pointer-events-none'}>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Message to delivery drivers</label>
                  <textarea
                    value={iDeliveryMessage}
                    onChange={e => setIDeliveryMessage(e.target.value)}
                    placeholder="e.g. Leave packages by the lobby door. Do not block the elevator."
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-accent-500 focus:border-accent-500"
                  />
                  <p className="text-xs text-gray-400 mt-1">Shown to every delivery driver on the visitor flow — appears in addition to the tenant&apos;s own arrival note.</p>
                </div>
                {saveBtnRow('deliveries')}
              </div>
            </div>
            <div className="border-t-2 border-gray-100" />
            <div id="settings-branding-inline">
              {staticHeader(2, 'Branding & Theme', 'Visual styling — logo, colors, footer copy')}
              <div className="px-4 pb-4 space-y-4">
                  <div className="pt-4">
                    <label className="block text-xs font-medium text-gray-600 mb-1">Display Style</label>
                    <select className={selectCls} value={iDisplayStyle} onChange={e => setIDisplayStyle(e.target.value)}>
                      <option value="modern_dark">Modern Dark</option>
                      <option value="modern_light">Modern Light</option>
                      <option value="classic">Classic</option>
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Accent Color</label>
                      <div className="flex items-center gap-2">
                        <input type="color" value={iAccentColor} onChange={e => setIAccentColor(e.target.value)} className="h-9 w-14 px-1 py-1 border border-gray-300 rounded-lg cursor-pointer" />
                        <input type="text" value={iAccentColor} onChange={e => setIAccentColor(e.target.value)} className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-accent-500 focus:border-accent-500" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Background Color</label>
                      <div className="flex items-center gap-2">
                        <input type="color" value={iBackgroundColor} onChange={e => setIBackgroundColor(e.target.value)} className="h-9 w-14 px-1 py-1 border border-gray-300 rounded-lg cursor-pointer" />
                        <input type="text" value={iBackgroundColor} onChange={e => setIBackgroundColor(e.target.value)} className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-accent-500 focus:border-accent-500" />
                      </div>
                    </div>
                  </div>
                  {/* Property Logo — file upload (replaces the visitor directory's
                      default building icon). Stored as a base64 data URL on the
                      property record so the visitor SPA can render it without
                      hitting any auth-gated endpoint. */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Property Logo</label>
                    <div className="flex items-start gap-4">
                      <div className="w-20 h-20 rounded-2xl border-2 border-dashed border-gray-300 flex items-center justify-center bg-gray-50 overflow-hidden flex-shrink-0">
                        {iLogoUrl ? (
                          <img src={iLogoUrl} alt="Property logo" className="w-full h-full object-cover" />
                        ) : (
                          <svg className="w-8 h-8 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <input
                          id="property-logo-upload"
                          type="file"
                          accept="image/png,image/jpeg,image/webp,image/svg+xml"
                          className="hidden"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            // Cap at ~750KB raw to avoid bloating the property row.
                            if (file.size > 750 * 1024) {
                              alert('Logo file is too large. Please upload an image under 750KB.');
                              e.currentTarget.value = '';
                              return;
                            }
                            const reader = new FileReader();
                            reader.onload = () => {
                              const dataUrl = String(reader.result || '');
                              setILogoUrl(dataUrl);
                            };
                            reader.onerror = () => alert('Failed to read the image file.');
                            reader.readAsDataURL(file);
                            e.currentTarget.value = '';
                          }}
                        />
                        <div className="flex items-center gap-2">
                          <label
                            htmlFor="property-logo-upload"
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer text-gray-700"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"/></svg>
                            {iLogoUrl ? 'Replace Logo' : 'Upload Logo'}
                          </label>
                          {iLogoUrl && (
                            <button
                              type="button"
                              onClick={() => setILogoUrl('')}
                              className="text-xs font-medium text-red-600 hover:text-red-700"
                            >Remove</button>
                          )}
                        </div>
                        <p className="text-[11px] text-gray-500 mt-1.5">
                          PNG / JPG / SVG · Max 750 KB · Replaces the building icon on the visitor directory page.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Logo Size on Directory: <span className="font-mono">{iLogoSizePx}px</span>
                    </label>
                    <input
                      type="range"
                      min={40}
                      max={240}
                      step={4}
                      value={iLogoSizePx}
                      onChange={(e) => setILogoSizePx(e.target.value)}
                      className="w-full"
                    />
                    <div className="flex justify-between text-[10px] text-gray-400 mt-0.5">
                      <span>40px</span>
                      <span>240px</span>
                    </div>
                    {iLogoUrl && (
                      <div className="mt-2 flex justify-center bg-gray-50 rounded-lg p-3">
                        <img
                          src={iLogoUrl}
                          alt="Logo preview"
                          style={{ height: `${iLogoSizePx}px`, width: 'auto', maxWidth: '100%' }}
                        />
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Custom No-Answer Text</label>
                    <input type="text" value={iCustomNoAnswerText} onChange={e => setICustomNoAnswerText(e.target.value)} placeholder="No one is available right now." className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-accent-500 focus:border-accent-500" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Custom Footer Text (above "Powered by Intrlock")</label>
                    <input type="text" value={iCustomFooterText} onChange={e => setICustomFooterText(e.target.value)} placeholder="Managed by Acme Property Group" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-accent-500 focus:border-accent-500" />
                  </div>
                  {saveBtnRow('branding')}
                </div>
            </div>
            </Card>
            )}

            {/* ══════════ View: Voice and Video Settings ══════════ */}
            {activeSettingsView === 'vv' && (
            <Card padding={false}>
            <div id="settings-calls">
              {staticHeader(1, 'Call Routing', 'Enable / disable channels, ring behavior, timeouts — drives every visitor call')}
              <div className="px-4 pb-4 space-y-4">
                  <div className="pt-4 space-y-1">
                    {checkRow(iAllowVideoCalls, setIAllowVideoCalls, 'Enable video calls')}
                    {(() => {
                      // Phone-calls only meaningful when at least one intercom
                      // has a phone number assigned. Without that, no inbound
                      // route exists, so disable the toggle and force false.
                      const anyPhone = (intercoms || []).some((c: any) => (c.phone_number || '').toString().trim().length > 0);
                      if (!anyPhone) {
                        return (
                          <label className="flex items-center gap-3 py-1 opacity-60 cursor-not-allowed" title="Assign a phone number to an intercom to enable phone calls">
                            <input type="checkbox" disabled checked={false} className="rounded border-gray-300 h-4 w-4 flex-shrink-0" />
                            <span className="text-sm text-gray-700">
                              Enable phone calls
                              <span className="ml-2 text-[11px] text-amber-600">requires at least one intercom with a phone number</span>
                            </span>
                          </label>
                        );
                      }
                      return checkRow(iAllowPhoneCalls, setIAllowPhoneCalls, 'Enable phone calls');
                    })()}
                    {checkRow(iSimultaneousRing, setISimultaneousRing, 'Simultaneous ring — ring all tenants in unit at once (vs. first tenant only)')}
                    {checkRow(iAllowDoorUnlockInCall, setIAllowDoorUnlockInCall, 'Allow tenant to unlock door during video call')}
                    {checkRow(iRecordVideoCalls, setIRecordVideoCalls, 'Record all video calls')}
                    {checkRow(iRequireVisitorName, setIRequireVisitorName, 'Require visitor to enter name before calling')}
                    {checkRow(iRequireVisitorPhone, setIRequireVisitorPhone, 'Require visitor phone number before calling')}
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Call Timeout (seconds, 15–120)</label>
                      <input
                        type="number" min={15} max={120}
                        value={iCallTimeoutSeconds}
                        onChange={e => setICallTimeoutSeconds(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-accent-500 focus:border-accent-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Ring Duration (seconds, 10–60)</label>
                      <input
                        type="number" min={10} max={60}
                        value={iRingDurationSeconds}
                        onChange={e => setIRingDurationSeconds(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-accent-500 focus:border-accent-500"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">No-Answer Message</label>
                    <input
                      type="text"
                      value={iNoAnswerMessage}
                      onChange={e => setINoAnswerMessage(e.target.value)}
                      placeholder="Sorry, nobody is available right now."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-accent-500 focus:border-accent-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Call Disabled Message</label>
                    <input
                      type="text"
                      value={iCallBlockedMessage}
                      onChange={e => setICallBlockedMessage(e.target.value)}
                      placeholder="Calls are currently unavailable."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-accent-500 focus:border-accent-500"
                    />
                  </div>
                  {saveBtnRow('calls')}
                </div>
            </div>
            <div className="border-t-2 border-gray-100" />
            <div id="settings-voice">
              {staticHeader(2, 'Voice IVR', 'TTS greeting + voice / language / DTMF unlock — only used on the phone fallback path')}
              <div className="px-4 pb-4 space-y-4 pt-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Phone Greeting Message</label>
                    <textarea
                      value={iVoiceGreeting}
                      onChange={e => setIVoiceGreeting(e.target.value)}
                      placeholder="Hello, welcome to [Property Name]. Please enter the apartment number you are visiting, followed by the pound key."
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-accent-500"
                    />
                    <p className="text-xs text-gray-400 mt-1">Played by the phone-intercom TTS. Different from the visitor-app greeting. Must include instructions for entering unit number.</p>
                    <button
                      type="button"
                      disabled={iPreviewLoading}
                      onClick={async () => {
                        const text = iGreetingMessage || `Hello, welcome to ${property?.name || 'this building'}. Please enter the apartment number you are visiting, followed by the pound key.`;
                        setIPreviewLoading(true);
                        try {
                          const res = await fetch(`${apiOrigin()}/voice/preview`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ text, voice_name: iVoiceName, voice_speed: iVoiceSpeed, voice_pitch: iVoicePitch }),
                          });
                          const data = await res.json();
                          if (data.error) throw new Error(data.error);
                          const blob = new Blob([Uint8Array.from(atob(data.audio), c => c.charCodeAt(0))], { type: 'audio/mpeg' });
                          const url = URL.createObjectURL(blob);
                          const audio = new Audio(url);
                          audio.onended = () => URL.revokeObjectURL(url);
                          await audio.play();
                        } catch (e: any) {
                          alert('Preview failed: ' + (e.message || e));
                        } finally {
                          setIPreviewLoading(false);
                        }
                      }}
                      className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-brand-700 bg-brand-50 hover:bg-brand-100 rounded-lg transition-colors disabled:opacity-50"
                    >
                      {iPreviewLoading ? (
                        <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                      ) : (
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072M18.364 5.636a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" /></svg>
                      )}
                      {iPreviewLoading ? 'Playing...' : 'Preview Greeting'}
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Voice</label>
                      <select value={iVoiceName} onChange={e => setIVoiceName(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                        <optgroup label="English (US)">
                          <option value="Azure.en-US-JennyNeural">Jenny (Female)</option>
                          <option value="Azure.en-US-SaraNeural">Sara (Female)</option>
                          <option value="Azure.en-US-AriaNeural">Aria (Female)</option>
                          <option value="Azure.en-US-GuyNeural">Guy (Male)</option>
                          <option value="Azure.en-US-DavisNeural">Davis (Male)</option>
                        </optgroup>
                        <optgroup label="English (UK)">
                          <option value="Azure.en-GB-SoniaNeural">Sonia (Female)</option>
                          <option value="Azure.en-GB-RyanNeural">Ryan (Male)</option>
                        </optgroup>
                        <optgroup label="Spanish">
                          <option value="Azure.es-US-PalomaNeural">Paloma (Female, US)</option>
                          <option value="Azure.es-US-AlonsoNeural">Alonso (Male, US)</option>
                          <option value="Azure.es-MX-DaliaNeural">Dalia (Female, MX)</option>
                        </optgroup>
                        <optgroup label="French">
                          <option value="Azure.fr-FR-DeniseNeural">Denise (Female)</option>
                          <option value="Azure.fr-FR-HenriNeural">Henri (Male)</option>
                        </optgroup>
                        <optgroup label="Portuguese">
                          <option value="Azure.pt-BR-FranciscaNeural">Francisca (Female, BR)</option>
                        </optgroup>
                        <optgroup label="Chinese">
                          <option value="Azure.zh-CN-XiaoxiaoNeural">Xiaoxiao (Female)</option>
                          <option value="Azure.zh-CN-YunxiNeural">Yunxi (Male)</option>
                        </optgroup>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Language</label>
                      <select value={iVoiceLanguage} onChange={e => setIVoiceLanguage(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                        <option value="en-US">English (US)</option>
                        <option value="en-GB">English (UK)</option>
                        <option value="es-US">Spanish (US)</option>
                        <option value="es-MX">Spanish (Mexico)</option>
                        <option value="fr-FR">French</option>
                        <option value="pt-BR">Portuguese (Brazil)</option>
                        <option value="zh-CN">Chinese (Mandarin)</option>
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Speed</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          value={parseInt((iVoiceSpeed || '-10%').replace('%', '').replace('+', ''))}
                          onChange={e => {
                            const v = parseInt(e.target.value) || 0;
                            const clamped = Math.max(-50, Math.min(50, v));
                            setIVoiceSpeed(clamped >= 0 ? `+${clamped}%` : `${clamped}%`);
                          }}
                          min={-50}
                          max={50}
                          step={1}
                          className="w-20 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-accent-500"
                        />
                        <span className="text-sm text-gray-500">%</span>
                      </div>
                      <p className="text-xs text-gray-400 mt-1">-50 (slowest) to +50 (fastest). Default: -10</p>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Pitch</label>
                      <select value={iVoicePitch} onChange={e => setIVoicePitch(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                        <option value="x-low">Very Low</option>
                        <option value="low">Low</option>
                        <option value="medium">Medium (default)</option>
                        <option value="high">High</option>
                        <option value="x-high">Very High</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Volume</label>
                      <select value={iVoiceVolume} onChange={e => setIVoiceVolume(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                        <option value="x-soft">Very Soft</option>
                        <option value="soft">Soft</option>
                        <option value="medium">Medium (default)</option>
                        <option value="loud">Loud</option>
                        <option value="x-loud">Very Loud</option>
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">DTMF Unlock Digit</label>
                      <select value={iDtmfUnlockDigit} onChange={e => setIDtmfUnlockDigit(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                        {['0','1','2','3','4','5','6','7','8','9','*','#'].map(d => (
                          <option key={d} value={d}>{d}{d === '9' ? ' (default)' : ''}</option>
                        ))}
                      </select>
                      <p className="text-xs text-gray-400 mt-1">Digit tenant presses during call to unlock the door</p>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Ringback Tone</label>
                      <select value={iRingTone} onChange={e => setIRingTone(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                        <option value="us">United States</option>
                        <option value="uk">United Kingdom</option>
                        <option value="au">Australia</option>
                        <option value="de">Germany</option>
                        <option value="fr">France</option>
                        <option value="es">Spain</option>
                        <option value="it">Italy</option>
                        <option value="jp">Japan</option>
                        <option value="mx">Mexico</option>
                        <option value="br">Brazil</option>
                        <option value="nl">Netherlands</option>
                        <option value="se">Sweden</option>
                      </select>
                      <p className="text-xs text-gray-400 mt-1">Tone visitor hears while the tenant's phone rings</p>
                    </div>
                  </div>
                  {saveBtnRow('voice')}
                </div>
            </div>
            </Card>
            )}

            {/* ══════════ View: Access and Security ══════════ */}
            {activeSettingsView === 'access-sec' && (
            <Card padding={false}>
            <div id="settings-access">
              {staticHeader(1, 'Access Controls', 'How visitors and tenants unlock — PIN, Face ID, auto-lock schedule')}
              <div className="px-4 pb-4 space-y-4">
                  <div className="pt-4 space-y-1">
                    {/* PIN + Face are PREMIUM features. Toggling ON triggers
                        a confirmation modal so the PM acknowledges the billing
                        impact. Toggling OFF skips the modal. */}
                    <label className="flex items-center gap-3 cursor-pointer py-1">
                      <input
                        type="checkbox"
                        checked={iPinAccessEnabled}
                        onChange={async (e) => {
                          if (e.target.checked) {
                            // Dollar pricing is admin-only — on app.intrlock.io the
                            // confirm dialog just says "premium feature" without
                            // exposing the price.
                            setPremiumConfirm({ feature: 'pin', price: isAdminHost() ? '$5/mo per property' : 'Premium feature' });
                          } else {
                            setIPinAccessEnabled(false);
                            try { await apiUpdate('properties', id!, { pin_access_enabled: false }); }
                            catch (err) { console.error('[pin off] auto-save failed:', err); }
                          }
                        }}
                        className="rounded border-gray-300 text-accent-600 focus:ring-accent-500 h-4 w-4 flex-shrink-0"
                      />
                      <span className="text-sm text-gray-700">
                        Enable PIN unlock on visitor page
                        <span className="ml-2 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">Premium</span>
                      </span>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer py-1">
                      <input
                        type="checkbox"
                        checked={iFaceAccessEnabled}
                        onChange={async (e) => {
                          if (e.target.checked) {
                            setPremiumConfirm({ feature: 'face', price: isAdminHost() ? '$15/mo per property' : 'Premium feature' });
                          } else {
                            setIFaceAccessEnabled(false);
                            try { await apiUpdate('properties', id!, { face_access_enabled: false }); }
                            catch (err) { console.error('[face off] auto-save failed:', err); }
                          }
                        }}
                        className="rounded border-gray-300 text-accent-600 focus:ring-accent-500 h-4 w-4 flex-shrink-0"
                      />
                      <span className="text-sm text-gray-700">
                        Enable face recognition access on visitor page
                        <span className="ml-2 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">Premium</span>
                      </span>
                    </label>
                  {checkRow(iGuestPinAccessEnabled, setIGuestPinAccessEnabled, 'Allow guest PINs to work at visitor kiosk')}
                  <label className="block border-t border-gray-200 pt-3 text-sm font-semibold text-gray-800">
                    Visitor-pass photo at each unlock
                    <select
                      value={visitorPassEntryPhotoMode}
                      onChange={(event) => setVisitorPassEntryPhotoMode(event.target.value as 'off' | 'optional' | 'required')}
                      className="mt-2 block w-full max-w-sm border border-gray-300 bg-white px-3 py-2 text-sm"
                    >
                      <option value="off">Off — no camera request</option>
                      <option value="optional">Optional — visitor may attach a photo</option>
                      <option value="required">Required — photo and consent before every unlock</option>
                    </select>
                    <span className="mt-1 block text-xs font-normal text-gray-600">Applies to all visitor passes at this property. Photos are encrypted and linked to the unlock attempt.</span>
                  </label>
                  <VisitorPassEntryPhotos propertyId={String(id)} />
                  {checkRow(iRequireDirectoryCode, setIRequireDirectoryCode, 'Require a code to view directory (private mode)')}
                  </div>

                  {/* ── Face Access Subsection ── */}
                  {iFaceAccessEnabled && (
                    <div className="border border-indigo-100 rounded-xl bg-indigo-50/40 p-4 space-y-3">
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4 text-indigo-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.121 17.804A9 9 0 1118.88 6.196M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <h4 className="text-sm font-semibold text-indigo-700">Face Access</h4>
                      </div>

                      <div className="flex items-center gap-3">
                        <span className="text-sm text-gray-600">Enrolled Faces:</span>
                        {faceCountLoading ? (
                          <span className="text-sm text-gray-400 italic">Loading...</span>
                        ) : (
                          <span className="text-sm font-bold text-indigo-700">
                            {faceEnrolledCount ?? '—'}
                          </span>
                        )}
                        <button
                          onClick={fetchFaceCount}
                          disabled={faceCountLoading}
                          className="text-xs text-indigo-500 hover:text-indigo-700 underline disabled:opacity-50"
                        >
                          Refresh
                        </button>
                      </div>

                      <p className="text-xs text-gray-500 italic">
                        Face data is stored securely in AWS and is not accessible from this dashboard.
                      </p>

                      <button
                        onClick={() => setFaceResetConfirm(true)}
                        className="px-3 py-1.5 text-xs font-medium rounded-lg border border-red-200 text-red-600 hover:bg-red-50 transition-colors"
                      >
                        Reset All Enrollments
                      </button>
                    </div>
                  )}
                  {iRequireDirectoryCode && (
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Directory Access Code</label>
                      <input
                        type="text"
                        value={iDirectoryAccessCode}
                        onChange={e => setIDirectoryAccessCode(e.target.value)}
                        placeholder="e.g. 1234"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-accent-500 focus:border-accent-500"
                      />
                    </div>
                  )}
                  <div className="border-t border-gray-100 pt-3">
                    {checkRow(iAutoLockEnabled, setIAutoLockEnabled, 'Auto-lock directory at certain hours')}
                  </div>
                  {iAutoLockEnabled && (
                    <div className="grid grid-cols-2 gap-4 pl-7">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Lock at (directory goes offline)</label>
                        <input
                          type="time"
                          value={iAutoLockStart}
                          onChange={e => setIAutoLockStart(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-accent-500 focus:border-accent-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Unlock at (directory comes online)</label>
                        <input
                          type="time"
                          value={iAutoLockEnd}
                          onChange={e => setIAutoLockEnd(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-accent-500 focus:border-accent-500"
                        />
                      </div>
                    </div>
                  )}

                  {/* ── Geofence — restrict door unlocks to property area ── */}
                  <div className="border-t border-gray-100 pt-4 space-y-2">
                    <label className="flex items-center gap-3 cursor-pointer py-1">
                      <input
                        type="checkbox"
                        checked={iGeofenceEnabled}
                        onChange={(e) => setIGeofenceEnabled(e.target.checked)}
                        className="rounded border-gray-300 text-accent-600 focus:ring-accent-500 h-4 w-4 flex-shrink-0"
                      />
                      <span className="text-sm font-semibold text-gray-800">Restrict door unlocks to a property geofence</span>
                      <span className="ml-auto text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">📺 web · 📱 app</span>
                    </label>
                    <p className="text-xs text-gray-500 -mt-1 ml-7">
                      When enabled, both web visitors and mobile-app users can only unlock doors when their device's geolocation reports inside this circle.
                    </p>
                    {iGeofenceEnabled && (
                      <div className="ml-7 mt-2 space-y-3 p-3 bg-amber-50/40 border border-amber-200 rounded-lg">
                        <div className="flex items-center gap-3 flex-wrap">
                          <label className="text-xs font-medium text-gray-600 w-28">Apply to</label>
                          <label className="inline-flex items-center gap-1.5 text-sm text-gray-700">
                            <input
                              type="checkbox"
                              checked={iGeofenceApplyWeb}
                              onChange={(e) => setIGeofenceApplyWeb(e.target.checked)}
                              className="rounded border-gray-300 text-accent-600 focus:ring-accent-500 h-4 w-4"
                            />
                            Web visitors
                          </label>
                          <label className="inline-flex items-center gap-1.5 text-sm text-gray-700">
                            <input
                              type="checkbox"
                              checked={iGeofenceApplyMobile}
                              onChange={(e) => setIGeofenceApplyMobile(e.target.checked)}
                              className="rounded border-gray-300 text-accent-600 focus:ring-accent-500 h-4 w-4"
                            />
                            Mobile app users
                          </label>
                        </div>
                        {/* Visual geofence picker — satellite imagery, click-or-drag
                            to set the center, slider to size the radius. PM sees
                            the actual perimeter they're enforcing. */}
                        <GeofencePicker
                          lat={iGeofenceLat ? Number(iGeofenceLat) : null}
                          lng={iGeofenceLng ? Number(iGeofenceLng) : null}
                          radius={iGeofenceRadiusM ? Number(iGeofenceRadiusM) : 100}
                          onChange={({ lat, lng, radius }) => {
                            setIGeofenceLat(lat.toFixed(6));
                            setIGeofenceLng(lng.toFixed(6));
                            setIGeofenceRadiusM(String(radius));
                          }}
                        />
                        <div className="flex flex-wrap items-center gap-2 text-xs">
                          <button
                            type="button"
                            onClick={async () => {
                              const addr = [property.address, property.city, property.state, property.zip].filter(Boolean).join(', ');
                              if (!addr) { alert('Property has no address — set one first.'); return; }
                              try {
                                const r = await fetch(`https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(addr)}`);
                                const j = await r.json();
                                if (!j[0]) { alert('Could not geocode the property address.'); return; }
                                setIGeofenceLat(Number(j[0].lat).toFixed(6));
                                setIGeofenceLng(Number(j[0].lon).toFixed(6));
                              } catch { alert('Geocoding lookup failed.'); }
                            }}
                            className="px-2.5 py-1.5 font-medium border border-gray-300 rounded hover:bg-white text-gray-700"
                          >🏢 Use property address</button>
                        </div>
                        <p className="text-[11px] text-gray-500">
                          Users outside the circle who try to unlock will see "Your property manager requires you to be on the property to unlock doors. Please enable GPS permissions." This is opt-in — leave the toggle off if you don't want to enforce location.
                        </p>
                      </div>
                    )}
                  </div>

                  {saveBtnRow('access')}
                </div>
            </div>
            <div className="border-t-2 border-gray-100" />
            <div id="settings-privacy-inline">
              {staticHeader(2, 'Privacy & Abuse Prevention', 'Visitor logging, rate limits, no-answer block — protects tenants from harassment')}
              <div className="px-4 pb-4 space-y-4">
                  <div className="pt-4 space-y-1">
                    {checkRow(iPrivateDirectory, setIPrivateDirectory, 'Private directory — visitors must know unit number, no browsing list')}
                    {checkRow(iLogAllVisitorAccess, setILogAllVisitorAccess, 'Log every visitor page view')}
                    {checkRow(iBlockRepeatedNoAnswers, setIBlockRepeatedNoAnswers, 'Temporarily block visitors after too many no-answers')}
                  </div>
                  {iBlockRepeatedNoAnswers && (
                    <div className="pl-7">
                      <label className="block text-xs font-medium text-gray-600 mb-1">No-Answers Before Block</label>
                      <input
                        type="number" min={1}
                        value={iMaxNoAnswersBeforeBlock}
                        onChange={e => setIMaxNoAnswersBeforeBlock(e.target.value)}
                        className="w-40 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-accent-500 focus:border-accent-500"
                      />
                    </div>
                  )}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Visitor Rate Limit (max calls per visitor per hour)</label>
                    <input
                      type="number" min={1}
                      value={iVisitorRateLimit}
                      onChange={e => setIVisitorRateLimit(e.target.value)}
                      className="w-40 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-accent-500 focus:border-accent-500"
                    />
                  </div>
                  {saveBtnRow('privacy')}
                </div>
            </div>
            </Card>
            )}

            {/* ══════════ View: Mobile App Permissions ══════════ */}
            {activeSettingsView === 'mobile-app' && (
            <Card padding={false}>
            {/* ── 2. Guest Access — short-term visitors (lower priority — they don't live here) ── */}
            <div id="settings-guests">
              {staticHeader(2, 'Guest Access Controls', 'Short-term visitors — friends, services, deliveries. Tenants generate temporary PINs / app access.')}
              <div className="px-4 pb-4 space-y-4">
                  <div className="pt-4 space-y-1">
                    {checkRow(iAllowTenantCreateGuests, setIAllowTenantCreateGuests, 'Allow tenants to create guest access (master switch)')}
                    {checkRow(iGuestAppAccessEnabled, setIGuestAppAccessEnabled, 'Allow tenants to give guests app access')}
                    {checkRow(iNotifyManagerOnGuestCreate, setINotifyManagerOnGuestCreate, 'Notify manager when tenant creates a guest')}
                    {checkRow(iNotifyTenantOnGuestPinUse, setINotifyTenantOnGuestPinUse, 'Push notify tenant when their guest uses a PIN')}
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Max Guests per Tenant (default)</label>
                      <input
                        type="number" min={1}
                        value={iDefaultMaxGuestsPerTenant}
                        onChange={e => setIDefaultMaxGuestsPerTenant(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-accent-500 focus:border-accent-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Max Guest Duration (days)</label>
                      <input
                        type="number" min={1}
                        value={iDefaultMaxGuestDurationDays}
                        onChange={e => setIDefaultMaxGuestDurationDays(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-accent-500 focus:border-accent-500"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Guest PIN Length</label>
                    <select className={selectCls} value={iGuestPinLength} onChange={e => setIGuestPinLength(e.target.value)}>
                      <option value="4">4 digits</option>
                      <option value="5">5 digits</option>
                      <option value="6">6 digits</option>
                    </select>
                  </div>
                  {/* "Doors available for Guest Access" removed — door scope is now
                      controlled per-Intercom in the Intercom Devices card above.
                      Guests inherit whatever doors each intercom is wired to. */}
                  {saveBtnRow('guestAccess')}
              </div>
            </div>
            </Card>
            )}

            {/* Branding subsection now lives inline inside the Branding-and-Display
                Card up above; same for Privacy inside Access-and-Security. */}
            </div>
          </div>
        );
      })()}

      {/* ══════════ Contacts Tab ══════════ */}
      {tab === 'contacts' && (
        <Card padding={false}>
          <div className="p-4 border-b border-gray-100 flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-gray-900">Property Contacts</h3>
              <p className="text-sm text-gray-500 mt-0.5">Building service contacts visible to tenants via the mobile app</p>
            </div>
            <Button size="sm" onClick={() => { setContactForm({ name: '', role: '', phone: '', email: '', show_in_directory: false, dtmf_shortcut: '' }); setContactModal({ open: true }); }}>
              Add Contact
            </Button>
          </div>
          {/* Mobile cards */}
          <div className="md:hidden p-3 space-y-2">
            {propertyContacts.length === 0 ? (
              <div className="p-6 text-center text-sm text-gray-400">No contacts added yet.</div>
            ) : propertyContacts.map((c: any) => (
              <div key={c.id} className="bg-white rounded-xl border border-gray-200 p-3">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <p className="font-semibold text-gray-900 truncate">{c.name}</p>
                  {c.role && <Badge color="blue">{c.role}</Badge>}
                </div>
                <div className="text-xs text-gray-600 space-y-0.5">
                  {c.phone && <p>☎ {c.phone}</p>}
                  {c.email && <p className="truncate">✉ {c.email}</p>}
                  {(c.show_in_directory || c.dtmf_shortcut) && (
                    <div className="flex gap-1 flex-wrap pt-1">
                      {c.show_in_directory && <Badge color="green">Directory</Badge>}
                      {c.dtmf_shortcut && <Badge color="purple">Press {c.dtmf_shortcut}</Badge>}
                    </div>
                  )}
                </div>
                <div className="flex gap-3 mt-2 pt-2 border-t border-gray-100">
                  <button className="text-xs text-brand-600 font-medium" onClick={() => { setContactForm({ name: c.name, role: c.role, phone: c.phone || '', email: c.email || '', show_in_directory: !!c.show_in_directory, dtmf_shortcut: c.dtmf_shortcut || '' }); setContactModal({ open: true, contact: c }); }}>Edit</button>
                  <button className="text-xs text-red-600 font-medium" onClick={async () => { if (window.confirm('Remove this contact?')) { const updated = propertyContacts.filter(pc => pc.id !== c.id); setPropertyContacts(updated); try { await apiUpdate('properties', id!, { contacts_json: JSON.stringify(updated) }); refreshAllCounters(); } catch (err) { console.error('Failed to remove contact:', err); alert('Failed to remove contact. Please try again.'); } } }}>Remove</button>
                </div>
              </div>
            ))}
          </div>
          {/* Desktop table */}
          {propertyContacts.length === 0 ? (
            <div className="hidden md:block p-8 text-center text-gray-400 text-sm">
              No contacts added yet. Add contacts like Leasing Office, Building Maintenance, or Emergency Services.
            </div>
          ) : (
            <div className="hidden md:block">
            <Table
              columns={[
                { key: 'name', header: 'Name', render: (c: any) => <span className="font-medium">{c.name}</span> },
                { key: 'role', header: 'Role', render: (c: any) => <Badge color="blue">{c.role}</Badge> },
                { key: 'phone', header: 'Phone', render: (c: any) => c.phone || '—' },
                { key: 'email', header: 'Email', render: (c: any) => c.email || '—' },
                { key: 'directory', header: 'Directory', render: (c: any) => c.show_in_directory ? <Badge color="green">Yes</Badge> : '—' },
                { key: 'dtmf', header: 'DTMF', render: (c: any) => c.dtmf_shortcut ? <Badge color="purple">Press {c.dtmf_shortcut}</Badge> : '—' },
                { key: 'actions', header: 'Actions', render: (c: any) => (
                  <div className="flex gap-2">
                    <button className="text-xs text-brand-600 hover:underline" onClick={(e) => { e.stopPropagation(); setContactForm({ name: c.name, role: c.role, phone: c.phone || '', email: c.email || '', show_in_directory: !!c.show_in_directory, dtmf_shortcut: c.dtmf_shortcut || '' }); setContactModal({ open: true, contact: c }); }}>Edit</button>
                    <button className="text-xs text-red-600 hover:underline" onClick={async (e) => { e.stopPropagation(); if (window.confirm('Remove this contact?')) { const updated = propertyContacts.filter(pc => pc.id !== c.id); setPropertyContacts(updated); try { await apiUpdate('properties', id!, { contacts_json: JSON.stringify(updated) }); refreshAllCounters(); } catch (err) { console.error('Failed to remove contact:', err); alert('Failed to remove contact. Please try again.'); } } }}>Remove</button>
                  </div>
                )},
              ]}
              data={propertyContacts}
              emptyMessage="No contacts"
            />
            </div>
          )}
        </Card>
      )}

      {/* ══════════ Contact Add/Edit Modal ══════════ */}
      <Modal open={contactModal.open} onClose={() => setContactModal({ open: false })} title={contactModal.contact ? 'Edit Contact' : 'Add Contact'}>
        <div className="space-y-3">
          <Input label="Contact Name" placeholder="e.g., Leasing Office" value={contactForm.name} onChange={(e: any) => setContactForm(f => ({ ...f, name: e.target.value }))} />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Role / Type</label>
            <select className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" value={contactForm.role} onChange={(e) => setContactForm(f => ({ ...f, role: e.target.value }))}>
              <option value="">Select a role</option>
              <option value="Leasing Office">Leasing Office</option>
              <option value="Building Maintenance">Building Maintenance</option>
              <option value="Property Manager">Property Manager</option>
              <option value="Emergency">Emergency</option>
              <option value="Concierge">Concierge</option>
              <option value="Security">Security</option>
              <option value="Other">Other</option>
            </select>
          </div>
          <PhoneInput label="Phone" value={contactForm.phone} onChange={(v) => setContactForm(f => ({ ...f, phone: v }))} />
          <Input label="Email" type="email" value={contactForm.email} onChange={(e: any) => setContactForm(f => ({ ...f, email: e.target.value }))} />
          <label className="flex items-center gap-2 pt-2">
            <input
              type="checkbox"
              className="w-4 h-4"
              checked={!!contactForm.show_in_directory}
              onChange={(e) => setContactForm(f => ({ ...f, show_in_directory: e.target.checked }))}
            />
            <span className="text-sm text-gray-700">Show in Visitor Directory</span>
          </label>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">DTMF Shortcut</label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              value={contactForm.dtmf_shortcut}
              onChange={(e) => setContactForm(f => ({ ...f, dtmf_shortcut: e.target.value }))}
            >
              <option value="">— None —</option>
              {['*','#','0','1','2','3','4','5','6','7','8','9'].map(d => {
                const unlockDigit = String((property as any)?.dtmf_unlock_digit ?? '');
                const usedBy = propertyContacts.find(c =>
                  c.dtmf_shortcut === d && c.id !== contactModal.contact?.id
                );
                const collidesWithUnlock = unlockDigit && unlockDigit === d;
                const disabled = !!usedBy || !!collidesWithUnlock;
                const label = collidesWithUnlock
                  ? `${d} (door unlock)`
                  : usedBy ? `${d} (in use: ${usedBy.name})` : d;
                return <option key={d} value={d} disabled={disabled}>{label}</option>;
              })}
            </select>
            <p className="text-xs text-gray-500 mt-1">Visitors press this key during the IVR greeting to be transferred to this contact's phone.</p>
          </div>
        </div>
        <div className="flex justify-end gap-3 mt-4">
          <Button variant="secondary" onClick={() => setContactModal({ open: false })}>Cancel</Button>
          <Button onClick={async () => {
            if (!contactForm.name || !contactForm.role) return;
            let updatedContacts: any[];
            if (contactModal.contact) {
              updatedContacts = propertyContacts.map(c => c.id === contactModal.contact.id ? { ...c, ...contactForm } : c);
            } else {
              updatedContacts = [...propertyContacts, { id: Date.now(), ...contactForm }];
            }
            setPropertyContacts(updatedContacts);
            try {
              await apiUpdate('properties', id!, { contacts_json: JSON.stringify(updatedContacts) });
              refreshAllCounters();
            } catch (err) {
              console.error('Failed to save contact:', err);
              alert('Failed to save contact. Please try again.');
            }
            setContactModal({ open: false });
          }}>Save</Button>
        </div>
      </Modal>

      {/* ══════════ Billing Rates Tab (Super Admin) ══════════ */}
      {tab === 'billing_rates' && isSuperAdmin(role) && (
        <div className="space-y-6">
          <RatesTab
            scopeType="property"
            scopeId={id!}
            entityName={property.name || 'Property'}
            propertyId={id!}
            customerId={(property as any).customer_id}
            dealerId={(property as any).integrator_id}
          />
          <FeatureToggleList entityType="property" entityId={id!} />
        </div>
      )}

      {tab === 'calls' && (
        // Embed the global Call History view, locked to this property.
        // Same UI as /call-history (snapshots, leg merging, expand panel)
        // — keeps the experience consistent everywhere.
        <CallHistory presetPropertyId={id} hidePageHeader />
      )}

      {tab === 'device_history' && (
        // Same component as the global /device-history page, scoped to
        // this property via the presetPropertyId prop. The backend GET
        // /acs-systems/unlock-events already supports property_id filtering.
        <DeviceHistory presetPropertyId={id} hidePageHeader />
      )}

      {false && (() => {
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const weekAgo = new Date(today); weekAgo.setDate(weekAgo.getDate() - 7);
        const monthAgo = new Date(today); monthAgo.setMonth(monthAgo.getMonth() - 1);

        const filteredCalls = calls.filter(c => {
          const t = new Date(c.timestamp);
          if (callDateFilter === 'today' && t < today) return false;
          if (callDateFilter === 'week' && t < weekAgo) return false;
          if (callDateFilter === 'month' && t < monthAgo) return false;
          if (callStatusFilter !== 'all') {
            const s = String(c.status || '').toLowerCase();
            const dur = Number(c.duration || 0);
            const resolved = (s === 'completed' && dur > 0) || s === 'answered' ? 'answered'
              : (s === 'missed' || s === 'no_answer' || (s === 'completed' && dur === 0)) ? 'missed'
              : s;
            if (callStatusFilter === 'answered' && resolved !== 'answered') return false;
            if (callStatusFilter === 'missed' && resolved !== 'missed') return false;
            if (callStatusFilter === 'no_answer' && resolved !== 'no_answer') return false;
          }
          return true;
        });

        const answeredCalls = filteredCalls.filter(c => {
          const s = String(c.status || '').toLowerCase();
          const dur = Number(c.duration || 0);
          return s === 'answered' || (s === 'completed' && dur > 0);
        });
        const totalSeconds = filteredCalls.reduce((sum, c) => sum + Number(c.duration || 0), 0);
        const totalMinutes = Math.ceil(totalSeconds / 60);
        const doorUnlocks = filteredCalls.filter(c => c.door_unlocked).length;
        const answeredRate = filteredCalls.length > 0 ? ((answeredCalls.length / filteredCalls.length) * 100).toFixed(0) : '0';

        const filterBtn = (label: string, val: typeof callDateFilter) => (
          <button
            key={val}
            onClick={() => setCallDateFilter(val)}
            className={`px-3 py-1.5 text-sm rounded-lg ${callDateFilter === val ? 'bg-brand-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
          >
            {label}
          </button>
        );

        return (
          <div className="space-y-4">
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { label: 'Total Calls', value: filteredCalls.length, color: 'text-gray-900' },
                { label: 'Answered Rate', value: `${answeredRate}%`, color: 'text-green-600' },
                { label: 'Total Minutes', value: totalMinutes, color: 'text-emerald-600' },
                { label: 'Door Unlocks', value: doorUnlocks, color: 'text-purple-600' },
              ].map(s => (
                <Card key={s.label}>
                  <p className="text-xs text-gray-500">{s.label}</p>
                  <p className={`text-2xl font-bold mt-1 ${s.color}`}>{s.value}</p>
                </Card>
              ))}
            </div>

            {/* Filters + Table */}
            <Card padding={false}>
              <div className="p-3 border-b border-gray-100 flex flex-wrap items-center gap-3">
                <div className="flex gap-1.5">
                  {filterBtn('Today', 'today')}
                  {filterBtn('This Week', 'week')}
                  {filterBtn('This Month', 'month')}
                  {filterBtn('All', 'all')}
                </div>
                <select
                  value={callStatusFilter}
                  onChange={e => setCallStatusFilter(e.target.value as any)}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500"
                >
                  <option value="all">All Results</option>
                  <option value="answered">Answered</option>
                  <option value="missed">Unanswered</option>
                </select>
                <span className="ml-auto text-xs text-gray-500">{filteredCalls.length} call{filteredCalls.length !== 1 ? 's' : ''}</span>
              </div>
              {filteredCalls.length === 0 ? (
                <div className="p-8 text-center text-gray-400">No calls found.</div>
              ) : (
                <Table
                  columns={[
                    {
                      key: 'timestamp',
                      header: 'Date / Time',
                      render: (c: any) => <span className="text-xs text-gray-600">{formatDateTime(c.timestamp)}</span>,
                    },
                    {
                      key: 'call_type',
                      header: 'Type',
                      render: (c: any) => {
                        const t = String(c.call_type || '').toLowerCase();
                        if (t === 'video') return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">Video</span>;
                        if (t === 'voice' || t === 'intercom' || t === 'phone') return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">Voice</span>;
                        if (t === 'kiosk') return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">Kiosk</span>;
                        return <span className="text-gray-400 text-xs">—</span>;
                      },
                    },
                    {
                      key: 'visitor_name',
                      header: 'Caller / Visitor',
                      render: (c: any) => <span className="text-gray-700">{c.visitor_name || c.from_number || <span className="text-gray-400">—</span>}</span>,
                    },
                    {
                      key: 'unit_number',
                      header: 'Unit',
                      render: (c: any) => c.unit_number ? <span className="font-medium">#{c.unit_number}</span> : <span className="text-gray-400">—</span>,
                    },
                    {
                      key: 'tenant_name',
                      header: 'Tenant',
                      render: (c: any) => c.tenant_name || <span className="text-gray-400">—</span>,
                    },
                    {
                      key: 'duration',
                      header: 'Duration',
                      render: (c: any) => {
                        const d = Number(c.duration || 0);
                        return <span className="font-medium text-gray-700">{formatDuration(d)}</span>;
                      },
                    },
                    {
                      key: 'door_unlocked',
                      header: 'Doors',
                      render: (c: any) => {
                        const assigned: string[] = c.assigned_door_names || [];
                        const unlocked: string[] = c.unlocked_door_names || [];
                        // Pre-arrays calls: fall back to legacy boolean + door_name.
                        if (assigned.length === 0) {
                          return c.door_unlocked
                            ? <span className="text-green-600 font-medium text-sm">🔓 {c.door_name || 'Unlocked'}</span>
                            : <span className="text-gray-400">—</span>;
                        }
                        const unlockedSet = new Set(unlocked);
                        return (
                          <div className="flex flex-wrap gap-1">
                            {assigned.map((name) => {
                              const opened = unlockedSet.has(name);
                              return (
                                <span
                                  key={name}
                                  className={
                                    opened
                                      ? 'text-[11px] px-1.5 py-0.5 rounded bg-green-50 text-green-700 border border-green-200'
                                      : 'text-[11px] px-1.5 py-0.5 rounded bg-gray-50 text-gray-500 border border-gray-200'
                                  }
                                >
                                  {opened ? '🔓 ' : ''}{name}
                                </span>
                              );
                            })}
                          </div>
                        );
                      },
                    },
                    {
                      key: 'status',
                      header: 'Result',
                      render: (c: any) => {
                        const s = String(c.status || '').toLowerCase();
                        const answered = !!c.answered_at || ['answered', 'completed', 'unlocked'].includes(s);
                        return answered
                          ? <Badge color="green">Answered</Badge>
                          : <Badge color="gray">Unanswered</Badge>;
                      },
                    },
                  ]}
                  data={filteredCalls}
                />
              )}
            </Card>
          </div>
        );
      })()}

      {/* Unit Detail Slide-out */}
      {selectedUnit && (
        <UnitDetailPanel
          unit={selectedUnit}
          propertyId={id!}
          onClose={() => setSelectedUnit(null)}
          onEdit={(u) => setEditingUnit(u)}
          onUpdate={() => {
            fetchUnits();
            fetchTenants();
          }}
        />
      )}

      {/* Add Unit Modal */}
      {/* Add Tenant Modal */}
      <Modal open={addPersonModal} onClose={() => setAddPersonModal(false)} title="Add Tenant">
        <TenantForm
          propertyId={id!}
          onSubmit={async (data) => {
            await apiCreate('tenants', { ...data, property_id: id });
            setAddPersonModal(false);
            await fetchTenants();
            refreshAllCounters();
          }}
          onCancel={() => setAddPersonModal(false)}
        />
      </Modal>

      <Modal open={!!bulkImportMode} onClose={() => setBulkImportMode(null)} title="Bulk Add" size="xl">
        {bulkImportMode && <BulkImportWizard
          propertyId={id!}
          initialMode={bulkImportMode}
          existingUnits={units}
          onComplete={async () => {
            await Promise.all([fetchUnits(), fetchTenants()]);
            refreshAllCounters();
            setBulkImportMode(null);
          }}
          onClose={() => setBulkImportMode(null)}
        />}
      </Modal>

      <Modal open={editPropertyOpen} onClose={() => setEditPropertyOpen(false)} title="Edit Property" size="lg">
        <PropertyForm
          initialData={property}
          customerId={property?.customer_id}
          onSubmit={handleEditProperty}
          onCancel={() => setEditPropertyOpen(false)}
        />
        {savingProperty && (
          <p className="mt-2 text-sm text-gray-500">Saving…</p>
        )}
      </Modal>

      <Modal open={unitModal} onClose={() => setUnitModal(false)} title="Add Unit">
        <UnitForm
          propertyId={id!}
          onSubmit={handleCreateUnit}
          onCancel={() => setUnitModal(false)}
        />
      </Modal>

      {/* Edit Unit Modal */}
      <Modal open={!!editingUnit} onClose={() => setEditingUnit(null)} title="Edit Unit">
        <UnitForm
          propertyId={id!}
          initialData={editingUnit}
          onSubmit={handleUpdateUnit}
          onCancel={() => setEditingUnit(null)}
        />
      </Modal>

      {/* Delete Unit Confirmation */}
      <Modal open={!!deletingUnit} onClose={() => setDeletingUnit(null)} title="Delete Unit">
        <div className="space-y-4">
          <p className="text-gray-600">
            Are you sure you want to delete Unit <span className="font-semibold">#{deletingUnit?.unit_number}</span>?
            {' '}This action cannot be undone.
          </p>
          {(() => {
            const unit = unitsWithTenantCount.find((u) => u.id === deletingUnit?.id);
            return unit && unit.tenant_count > 0 ? (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-sm text-yellow-800">
                  ⚠️ This unit has {unit.tenant_count} tenant(s).
                  Deleting the unit will not delete the tenants, but they will no longer be associated with a unit.
                </p>
              </div>
            ) : null;
          })()}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" onClick={() => setDeletingUnit(null)}>
              Cancel
            </Button>
            <Button className="bg-red-600 hover:bg-red-700" onClick={handleDeleteUnit}>
              Delete Unit
            </Button>
          </div>
        </div>
      </Modal>

      {/* Premium-feature confirmation — appears when PM toggles PIN or Face ON */}
      <Modal open={!!premiumConfirm} onClose={() => setPremiumConfirm(null)} title="Premium Feature">
        {premiumConfirm && (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">Premium</span>
              <span className="text-sm font-semibold text-gray-900">
                {premiumConfirm.feature === 'pin' ? 'PIN Unlock Access' : 'Face Recognition Access'}
              </span>
            </div>
            <p className="text-sm text-gray-700">
              Enabling this feature adds <strong>{premiumConfirm.price}</strong> to this property's
              monthly bill. {premiumConfirm.feature === 'face'
                ? 'Face recognition uses AWS Rekognition (per-scan compute + storage of enrolled faces).'
                : 'PIN unlock includes guest PIN issuance, rate-limited PIN-pad access, and audit logging.'}
            </p>
            <p className="text-xs text-gray-500">
              You can disable this anytime — the change takes effect immediately and prorates to your next invoice.
            </p>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="secondary" onClick={() => setPremiumConfirm(null)}>Cancel</Button>
              <Button
                onClick={async () => {
                  const pinNext = premiumConfirm.feature === 'pin' ? true : iPinAccessEnabled;
                  const faceNext = premiumConfirm.feature === 'face' ? true : iFaceAccessEnabled;
                  if (premiumConfirm.feature === 'pin') setIPinAccessEnabled(true);
                  else setIFaceAccessEnabled(true);
                  setPremiumConfirm(null);
                  // Auto-persist immediately so the PM doesn't lose the
                  // toggle if they navigate away before hitting the
                  // section's Save button. Sends ONLY the two premium
                  // fields — every other access-section field is
                  // unaffected.
                  try {
                    await apiUpdate('properties', id!, {
                      pin_access_enabled: pinNext,
                      face_access_enabled: faceNext,
                    });
                  } catch (err) {
                    console.error('[premium accept] auto-save failed:', err);
                  }
                }}
              >
                Enable & Accept Charges
              </Button>
            </div>
          </div>
        )}
      </Modal>

      <AddDeviceModal
        open={deviceModal}
        onClose={() => setDeviceModal(false)}
        propertyId={id!}
        existingDevices={devices}
        onAdded={async () => {
          const refreshed = await getDevices();
          setDevices((Array.isArray(refreshed) ? refreshed : []).filter((dev: any) => String(dev.property_id) === String(id)));
        }}
      />

      <Modal open={!!editingDevice} onClose={() => setEditingDevice(null)} title="Edit Device">
        {editingDevice && <DeviceForm
          initialData={editingDevice}
          propertyId={id!}
          onCancel={() => setEditingDevice(null)}
          onSubmit={async (data) => {
            await updateDevice(editingDevice.id, { name: data.name });
            setDevices((current) => current.map((device) => String(device.id) === String(editingDevice.id) ? { ...device, name: data.name } : device));
            setEditingDevice(null);
          }}
        />}
      </Modal>

      <EmailTenantModal
        open={!!emailTenant}
        onClose={() => setEmailTenant(null)}
        tenant={emailTenant}
        propertyName={property?.name}
        activationCode={(() => {
          if (!emailTenant) return null;
          const t = emailTenant as any;
          const a = activationsByTenant[String(t.uuid || t.id)] || activationsByTenant[String(t.id)];
          return (
            a?.activation_code ||
            a?.video_activation_code ||
            t.activation_code ||
            t.video_activation_code ||
            null
          );
        })()}
      />

      <Modal open={!!tenantConfirmDelete} onClose={() => setTenantConfirmDelete(null)} title="Remove Person">
        <p className="text-sm text-gray-600 mb-4">
          Remove <span className="font-semibold">{tenantConfirmDelete?.first_name} {tenantConfirmDelete?.last_name}</span> from this property? This deletes their record, PIN, and app access. This cannot be undone.
        </p>
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setTenantConfirmDelete(null)} disabled={tenantDeleting}>Cancel</Button>
          <Button
            className="!bg-red-600 hover:!bg-red-700"
            disabled={tenantDeleting}
            onClick={async () => {
              if (!tenantConfirmDelete) return;
              setTenantDeleting(true);
              try {
                await deleteTenant(tenantConfirmDelete.id);
                setAllTenants((prev) => prev.filter((x: any) => x.id !== tenantConfirmDelete.id));
                refreshAllCounters();
                setTenantConfirmDelete(null);
              } catch (e: any) {
                alert(`Failed to remove: ${e?.message || e}`);
              } finally {
                setTenantDeleting(false);
              }
            }}
          >{tenantDeleting ? 'Removing…' : 'Remove'}</Button>
        </div>
      </Modal>

      <DeviceTenantAccessModal
        device={deviceAccessFor}
        propertyId={String(id ?? '')}
        tenants={allTenants}
        onClose={() => setDeviceAccessFor(null)}
      />

      {/* Turing live stream overlay — fired from the Devices tab
          "View Stream" button. Token never leaves the Lambda; we
          receive only a 10-min HLS URL. */}
      {turingPlay && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setTuringPlay(null)}>
          <div className="bg-black rounded-lg w-full max-w-3xl aspect-video relative" onClick={(e) => e.stopPropagation()}>
            {turingPlay.error ? (
              <div className="absolute inset-0 flex items-center justify-center text-white text-sm p-6 text-center">{turingPlay.error}</div>
            ) : turingPlay.streamUrl ? (
              <HlsVideo
                src={turingPlay.streamUrl}
                className="w-full h-full"
                onRefreshStreamUrl={async () => {
                  if (!turingPlay.systemId || !turingPlay.cameraId) return null;
                  try {
                    const r = await fetch(`${process.env.REACT_APP_API_URL || 'https://api.intrlock.io'}/integrations/turing/systems/${turingPlay.systemId}/stream-url`, {
                      method: 'POST',
                      headers: {
                        Authorization: `Bearer ${localStorage.getItem('intrlock_token') ?? ''}`,
                        'Content-Type': 'application/json',
                      },
                      body: JSON.stringify({ camera_id: turingPlay.cameraId, resolution: 'sub' }),
                    });
                    const data = await r.json();
                    return data.play_url ?? null;
                  } catch { return null; }
                }}
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center text-white text-sm">Fetching stream URL…</div>
            )}
            <button className="absolute top-2 right-2 bg-white/10 text-white rounded px-2 py-1 text-xs" onClick={() => setTuringPlay(null)}>Close</button>
          </div>
        </div>
      )}

      <Modal open={!!routingDevice} onClose={() => setRoutingDevice(null)} title={`Door routing — ${routingDevice?.name || ''}`} size="md">
        <div className="space-y-4">
          <p className="text-xs text-gray-500">
            Choose which integration unlocks this door when a PIN, face, or app unlock fires.
            "Parallel" fires all enabled door integrations at once.
          </p>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Preferred integration</label>
            <select
              value={routingForm.preferred_integration}
              onChange={(e) => setRoutingForm({ ...routingForm, preferred_integration: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            >
              <option value="">Default (auto)</option>
              <option value="pdk">PDK only</option>
              <option value="intrlock_bridge_relay">Intrlock Bridge only</option>
              <option value="parallel">Parallel (fire all)</option>
            </select>
          </div>

          {(routingForm.preferred_integration === 'intrlock_bridge_relay' || routingForm.preferred_integration === 'parallel' || routingForm.bridge_serial) && (
            <>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Bridge serial</label>
                <input
                  type="text"
                  value={routingForm.bridge_serial}
                  onChange={(e) => setRoutingForm({ ...routingForm, bridge_serial: e.target.value })}
                  placeholder="BR-2604-RELAY-XXXXXXXX"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Bridge channel</label>
                  <input
                    type="number"
                    min={1}
                    max={16}
                    value={routingForm.bridge_channel}
                    onChange={(e) => setRoutingForm({ ...routingForm, bridge_channel: e.target.value })}
                    placeholder="1"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Pulse duration (ms)</label>
                  <input
                    type="number"
                    min={100}
                    max={30000}
                    step={100}
                    value={routingForm.pulse_ms}
                    onChange={(e) => setRoutingForm({ ...routingForm, pulse_ms: e.target.value })}
                    placeholder="3000"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  />
                </div>
              </div>
            </>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => setRoutingDevice(null)} disabled={routingSaving}>Cancel</Button>
            <Button
              onClick={async () => {
                if (!routingDevice) return;
                setRoutingSaving(true);
                try {
                  await apiUpdate('devices', routingDevice.id, {
                    preferred_integration: routingForm.preferred_integration || null,
                    bridge_serial: routingForm.bridge_serial || null,
                    bridge_channel: routingForm.bridge_channel ? Number(routingForm.bridge_channel) : null,
                    pulse_ms: routingForm.pulse_ms ? Number(routingForm.pulse_ms) : null,
                  });
                  const refreshed = await getDevices();
                  setDevices((Array.isArray(refreshed) ? refreshed : []).filter((dev: any) => String(dev.property_id) === String(id)));
                  setRoutingDevice(null);
                } catch (err) {
                  console.error('Failed to save routing:', err);
                  alert('Failed to save routing');
                } finally {
                  setRoutingSaving(false);
                }
              }}
              disabled={routingSaving}
            >
              {routingSaving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
      </Modal>

      <CreateSubscriptionModal
        open={createSubOpen}
        onClose={() => setCreateSubOpen(false)}
        defaultCustomerId={customerData?.stripe_customer_id}
        defaultPropertyId={id!}
        customers={
          customerData
            ? [
                {
                  id: customerData.id,
                  company_name: customerData.company || customerData.company_name || customerData.name,
                  stripe_customer_id: customerData.stripe_customer_id,
                },
              ]
            : []
        }
        onCreated={() => window.location.reload()}
      />
      <CreateInvoiceModal
        open={createInvOpen}
        onClose={() => setCreateInvOpen(false)}
        defaultCustomerId={customerData?.stripe_customer_id}
        customers={
          customerData
            ? [
                {
                  id: customerData.id,
                  company_name: customerData.company || customerData.company_name || customerData.name,
                  stripe_customer_id: customerData.stripe_customer_id,
                },
              ]
            : []
        }
        onCreated={() => window.location.reload()}
      />
      <CustomPricingModal
        open={customPricingOpen}
        onClose={() => setCustomPricingOpen(false)}
        entityType="property"
        entityId={id!}
        entityName={property?.name || ''}
        stripeCustomerId={customerData?.stripe_customer_id}
      />

      {/* ── Face Reset Confirmation Modal ── */}
      <Modal open={faceResetConfirm} onClose={() => setFaceResetConfirm(false)} title="Reset All Face Enrollments">
        <div className="space-y-4">
          <div className="flex gap-3 p-3 bg-red-50 border border-red-200 rounded-lg">
            <svg className="w-5 h-5 text-red-500 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            </svg>
            <div>
              <p className="text-sm font-medium text-red-800">Warning: This action cannot be undone</p>
              <p className="text-sm text-red-700 mt-1">
                All {faceEnrolledCount ?? 'enrolled'} face enrollment{faceEnrolledCount !== 1 ? 's' : ''} for this property will be permanently deleted.
                Every tenant will need to re-enroll their face to use face access.
              </p>
            </div>
          </div>
          <p className="text-sm text-gray-600">
            Are you sure you want to reset all face enrollments for this property?
          </p>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" onClick={() => setFaceResetConfirm(false)} disabled={faceResetLoading}>
              Cancel
            </Button>
            <Button
              onClick={handleFaceReset}
              disabled={faceResetLoading}
              className="!bg-red-600 hover:!bg-red-700"
            >
              {faceResetLoading ? 'Resetting...' : 'Reset All Enrollments'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* ReassignPropertyModal removed — admin.intrlock.io now owns this flow. */}
    </div>
  );
};

// ─── PropertyVendorsTab — §28.518.99.47.d ──────────────────────────────────
// Rendered inside PropertyDetail's Vendors tab. Lists vendors that have at
// least one active grant on this property, plus a "+ Add Vendor" affordance
// that pre-scopes the new vendor to this property.

const PropertyVendorsTab: React.FC<{ propertyId: string }> = ({ propertyId }) => {
  const [vendors, setVendors] = React.useState<Vendor[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [showCreate, setShowCreate] = React.useState(false);
  const [detailId, setDetailId] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const rows = await listVendors({ property_id: propertyId }).catch(() => [] as Vendor[]);
      setVendors(rows);
    } finally {
      setLoading(false);
    }
  }, [propertyId]);

  React.useEffect(() => { load(); }, [load]);

  return (
    <Card padding={false}>
      <div className="p-4 border-b border-gray-100 flex items-center justify-between">
        <div>
          <h3 className="font-semibold">Vendors</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Back-of-house service providers with access to this property. Not listed in visitor directory or intercom simring.
          </p>
        </div>
        <Button onClick={() => setShowCreate(true)}>+ Add Vendor</Button>
      </div>

      {loading ? (
        <div className="p-8 text-center text-sm text-gray-400">Loading vendors…</div>
      ) : vendors.length === 0 ? (
        <div className="p-8 text-center text-sm text-gray-400">
          No vendors have access to this property yet.
        </div>
      ) : (
        <div className="divide-y divide-gray-100">
          {vendors.map((v) => (
            <button
              key={v.id}
              onClick={() => setDetailId(v.id)}
              className="w-full text-left p-4 hover:bg-gray-50 flex items-center justify-between"
            >
              <div>
                <div className="font-medium text-gray-900">{v.name}</div>
                <div className="text-xs text-gray-500">
                  {v.category ? v.category.charAt(0).toUpperCase() + v.category.slice(1) : 'Uncategorized'}
                  {v.contact_email ? <> · {v.contact_email}</> : null}
                </div>
              </div>
              {v.revoked_at
                ? <Badge status="suspended">Revoked</Badge>
                : <Badge status="active">Active</Badge>}
            </button>
          ))}
        </div>
      )}

      {showCreate && (
        <VendorCreateModal
          propertyId={propertyId}
          onClose={() => setShowCreate(false)}
          onCreated={(v) => { setVendors((prev) => [v, ...prev]); setShowCreate(false); setDetailId(v.id); }}
        />
      )}

      {detailId && (
        <Modal open onClose={() => setDetailId(null)} title="Vendor" size="lg">
          <VendorDetail
            vendorId={detailId}
            scopedPropertyId={propertyId}
            onChanged={(v) => setVendors((prev) => prev.map((r) => r.id === v.id ? v : r))}
            onClose={() => setDetailId(null)}
          />
        </Modal>
      )}
    </Card>
  );
};
